import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, Settings as SettingsIcon, Printer, Users, FileText, Database,
  ScrollText, Pencil, Lock, Unlock, Trash2, User, CheckCircle, Clock, Coins,
  Package, Download, RefreshCw, BookOpen, Crown, Banknote, Smartphone, Wrench,
  Volume2, HelpCircle, Shield, Key, ClipboardCheck, Eye, Server, TrendingUp,
  HardHat, Award, Leaf, Scale, Bot, FileSearch, Plus, ChevronRight, AlertCircle,
  History, Calendar, Filter, Search, ChevronDown, ChevronUp, Activity, XCircle, Info, AlertTriangle, Tag, FileUp, RotateCcw, Percent, ShoppingBag
} from 'lucide-react';
import * as XLSX from 'xlsx';
import LogModal from '../components/LogModal';
import logService from '../services/logService';
import { API_URL } from '../config/api';
import { withApiToken } from '../utils/apiAuth';
import useEscapeClose from '../hooks/useEscapeClose';
import { handlePrint, fetchPrintersFromAgent } from '../utils/printHelper';
import {
  isBluetoothSupported,
  pairBluetoothPrinter,
  clearBluetoothPrinter,
  getBluetoothPrinterInfo,
  setBluetoothPrinterEnabled,
  printRawToBluetooth,
  buildTestReceiptBytes,
} from '../utils/bluetoothPrinter';
import './Settings.css';

const Settings = ({ user, selectedStore }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const isAdmin = user?.role === 'admin';

  // Kullanıcı yönetimi state'leri
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'user', store_id: '', permissions: {}, default_sales_staff_id: '' });
  const [stores, setStores] = useState([]);
  const [salesStaffList, setSalesStaffList] = useState([]);
  const [formStoreStaffList, setFormStoreStaffList] = useState([]); // Terminal kullanıcı formu: sadece seçili mağazanın personeli
  const [userRoleFilter, setUserRoleFilter] = useState('all'); // all, admin, cashier, terminal, user

  // Yedekleme state'leri
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(false);
  const [autoBackupInterval, setAutoBackupInterval] = useState(24);
  const [savingBackupSettings, setSavingBackupSettings] = useState(false);
  const [backupFilter, setBackupFilter] = useState('');
  const [backupDateFilter, setBackupDateFilter] = useState('all'); // all, 30min, 1hour, today, week, month
  const [backupTypeFilter, setBackupTypeFilter] = useState('all'); // all, json, sql

  // Ürün + Resim Yedekleme state'leri
  const [productImageBackups, setProductImageBackups] = useState([]);
  const [loadingProductImageBackups, setLoadingProductImageBackups] = useState(false);
  const [creatingProductImageBackup, setCreatingProductImageBackup] = useState(false);

  // Log saklama süresi state'leri
  const [logRetentionDays, setLogRetentionDays] = useState(30);
  const [savingLogRetention, setSavingLogRetention] = useState(false);

  // E-Fatura state'leri
  const [efaturaSettings, setEfaturaSettings] = useState({
    company_vkn: '',
    company_name: '',
    company_tax_office: '',
    company_address: '',
    company_city: '',
    company_district: '',
    company_postal_code: '',
    company_country: 'Türkiye',
    company_phone: '',
    company_fax: '',
    company_email: '',
    company_website: '',
    gib_username: '',
    gib_password: '',
    gib_postbox_alias: '',
    signature_type: 'pkcs12',
    signature_path: '',
    signature_password: '',
    efatura_enabled: false,
    earsiv_enabled: false,
    efatura_api_url: 'http://localhost:8080/api',
    invoice_prefix: 'AKK',
    default_tax_rate: 20,
    prices_include_tax: true,
    auto_send: false,
    test_mode: true
  });
  const [loadingEfatura, setLoadingEfatura] = useState(false);
  const [savingEfatura, setSavingEfatura] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [efaturaStats, setEfaturaStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoiceFilter, setInvoiceFilter] = useState({ status: '', scenario: '', search: '', startDate: '', endDate: '' });

  // Log modal için
  const [showLogModal, setShowLogModal] = useState(false);

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Yardım modal için
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Ses/Bildirim ayarı (cihaz bazlı - localStorage)
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('soundEnabled');
    return saved !== null ? saved === 'true' : true;
  });


  const [eskiEtiketScanEnabled, setEskiEtiketScanEnabled] = useState(() => {
    const saved = localStorage.getItem('eskiEtiketScanEnabled');
    return saved !== null ? saved === 'true' : false;
  });

  // Yazıcı ayarları (cihaz bazlı - localStorage)
  const [directPrintEnabled, setDirectPrintEnabled] = useState(() => {
    const saved = localStorage.getItem('directPrintEnabled');
    return saved !== null ? saved === 'true' : false;
  });
  const [receiptPrinter, setReceiptPrinter] = useState(() => {
    return localStorage.getItem('receiptPrinter') || '';
  });
  const [labelPrinter, setLabelPrinter] = useState(() => {
    return localStorage.getItem('labelPrinter') || '';
  });
  // Tamirat fişi için ikinci sistem yazıcısı (cihaza özel localStorage)
  const [repairPrinter, setRepairPrinter] = useState(() => {
    return localStorage.getItem('repairPrinter') || '';
  });
  // Bu cihaz tamirat fişi yazdırma sunucusu mu? (yazıcıya bağlı tek cihaz olarak işaretlenir)
  const [isRepairPrintHost, setIsRepairPrintHost] = useState(() => {
    return localStorage.getItem('isRepairPrintHost') === 'true';
  });
  const [printerList, setPrinterList] = useState([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);

  // Bluetooth fiş yazıcı (HPRT HM-A300E vb. taşınabilir ESC/POS)
  const [btPrinterInfo, setBtPrinterInfo] = useState(() => getBluetoothPrinterInfo());
  const [btPairing, setBtPairing] = useState(false);
  const [btTesting, setBtTesting] = useState(false);

  // Tamirat hatırlatma ayarları (cihaz bazlı - localStorage)
  const [repairReminderHours, setRepairReminderHours] = useState(() => {
    const saved = localStorage.getItem('repairReminderHours');
    return saved ? parseInt(saved) : 24;
  });
  const [repairReminderInterval, setRepairReminderInterval] = useState(() => {
    const saved = localStorage.getItem('repairReminderIntervalMin');
    return saved ? parseInt(saved) : 15;
  });
  const [repairReminderEnabled, setRepairReminderEnabled] = useState(() => {
    const saved = localStorage.getItem('repairReminderEnabled');
    return saved !== null ? saved === 'true' : true;
  });

  // Kod olmadan tamirat teslim şifresi (DB'den yüklenir, tüm cihazlarda ortak)
  const [repairOverrideEnabled, setRepairOverrideEnabled] = useState(false);
  const [repairOverridePassword, setRepairOverridePassword] = useState('');
  const [repairOverrideHasPassword, setRepairOverrideHasPassword] = useState(false);

  // 2FA (İki Faktörlü Doğrulama) state'leri
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [loading2FA, setLoading2FA] = useState(false);
  const [setting2FA, setSetting2FA] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisable2FA, setShowDisable2FA] = useState(false);

  // QR Kodu ile Giriş state'leri
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrLoginCode, setQrLoginCode] = useState('');
  const [loadingQrToken, setLoadingQrToken] = useState(false);
  const [qrTokenActive, setQrTokenActive] = useState(false);

  // Satış silme izni state'i
  const [saleDeleteAllowed, setSaleDeleteAllowed] = useState(false);
  const [loadingSaleDeleteSetting, setLoadingSaleDeleteSetting] = useState(false);

  // Uyumluluk & Güvenlik (Compliance) state'leri
  const [complianceItems, setComplianceItems] = useState([]);
  const [loadingCompliance, setLoadingCompliance] = useState(false);
  const [showAddComplianceModal, setShowAddComplianceModal] = useState(false);
  const [selectedComplianceCategory, setSelectedComplianceCategory] = useState(null);
  const [newComplianceItem, setNewComplianceItem] = useState({ title: '', description: '', category: '', status: 'pending' });

  // Log Kayıtları state'leri
  const [logData, setLogData] = useState({ logs: {}, dayStats: [], totalDays: 0, totalLogs: 0 });
  const [logSummary, setLogSummary] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const [logFilters, setLogFilters] = useState({ module: '', action: '', status: '', search: '' });
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);
  const [cleaningLogs, setCleaningLogs] = useState(false);

  // Yasal Bilgiler accordion state'leri
  const [openLegalSections, setOpenLegalSections] = useState({});
  const toggleLegalSection = (section) => {
    setOpenLegalSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Satış Aktarımı state'leri
  const [salesImportData, setSalesImportData] = useState(null);
  const [salesImportPreview, setSalesImportPreview] = useState(null);
  const [salesImportStore, setSalesImportStore] = useState('');
  const [importingSales, setImportingSales] = useState(false);
  const [salesImportResult, setSalesImportResult] = useState(null);
  const [showSalesImportPreview, setShowSalesImportPreview] = useState(false);
  const salesImportFileRef = useRef(null);

  // İade Aktarımı state'leri
  const [returnsImportData, setReturnsImportData] = useState(null);
  const [returnsImportPreview, setReturnsImportPreview] = useState(null);
  const [returnsImportStore, setReturnsImportStore] = useState('');
  const [importingReturns, setImportingReturns] = useState(false);
  const [returnsImportResult, setReturnsImportResult] = useState(null);
  const [showReturnsImportPreview, setShowReturnsImportPreview] = useState(false);
  const returnsImportFileRef = useRef(null);

  // Detaylı Aktivite Logları state'leri
  const [activityData, setActivityData] = useState({ activities: [], pagination: {} });
  const [activityStats, setActivityStats] = useState(null);
  const [userActivitySummary, setUserActivitySummary] = useState([]);
  const [pageActivitySummary, setPageActivitySummary] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityFilters, setActivityFilters] = useState({ user_id: '', activity_type: '', page_name: '', search: '' });
  const [selectedUserTimeline, setSelectedUserTimeline] = useState(null);
  const [userTimeline, setUserTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [showDetailedLogs, setShowDetailedLogs] = useState(false);

  // Kasiyer Maksimum İndirim Limitleri
  const [cashierDiscountLimits, setCashierDiscountLimits] = useState([]);
  const [savingMaxDiscount, setSavingMaxDiscount] = useState(false);
  const [isCashierLimitsOpen, setIsCashierLimitsOpen] = useState(false);

  // Etiket Yazıcı Ayarları (cihaz bazlı - localStorage)
  const [labelSettings, setLabelSettings] = useState(() => {
    const saved = localStorage.getItem('labelPrinterSettings');
    return saved ? JSON.parse(saved) : {
      // Etiket boyutları (mm) - Plastik saplı kuyumcu etiketi
      // Sıralama: Sol Baskı -> Katlama -> Bilgi Alanı -> Kuyruk
      labelWidth: 70,              // Toplam uzunluk
      labelHeight: 9,              // Toplam kalınlık (yükseklik)
      leftPrintAreaWidth: 20,      // Sol baskı alanı (marka + barkod + barkod no)
      foldAreaWidth: 3,            // Katlama alanı (baskı YOK)
      infoPrintAreaWidth: 16,      // Bilgi alanı (ağırlık/ayar, HAS, kategori)
      tailAreaWidth: 31,           // Kuyruk/sap alanı (baskı YOK)
      // Eski uyumluluk için
      printAreaWidth: 20,
      middleAreaWidth: 3,
      rightPrintAreaWidth: 16,
      handleAreaWidth: 31,
      // Yazıcı ayarları
      printerDpi: 203,
      printerModel: 'Zebra ZD220',
      // Font boyutları (pt) - sol baskı alanı
      fontSizeInfo: 4,          // Ürün bilgi satırı
      fontSizeBarcode: 5,       // Barkod numarası
      fontSizeBrand: 5,         // Marka (sol üst)
      // Font boyutları (pt) - sağ baskı alanı
      fontSizeCategory: 7,      // Kategori/ürün adı
      fontSizeStock: 4,         // Stok kodu
      fontSizeValues: 7,        // HAS/USD/GRAM değerleri
      // Barkod ayarları
      barcodeHeight: 3.5,       // Barkod yüksekliği (mm)
      barcodeWidth: 1.0,        // Barkod çizgi kalınlığı
      // Varsayılan değerler
      defaultBrand: 'AKİK GOLD JEWELLERY',
      defaultPurity: '14k',
      defaultProfitMargin: 3,
      // Görünüm ayarları - sol baskı alanı
      showBarcode: true,        // Barkod (çizgili)
      showBarcodeNumber: true,  // Barkod numarası
      showBrand: true,          // Firma adı (sol alt)
      showProductInfo: true,    // Ürün bilgisi (ayar/gram)
      showPurity: true,         // Ayar bilgisi
      showWeight: true,         // Gram bilgisi
      // Görünüm ayarları - sağ baskı alanı
      showCategory: true,       // Kategori/ürün tipi
      showHAS: true,            // HAS: Gram * Kar Oranı
      showUSD: true,            // USD: İşçilik * Kar Oranı
      showGRAM: true,           // GRAM: Ürün Gramı
      showStockCode: true,      // Stok/Barkod kodu
      // Kenar boşlukları (mm)
      leftPadding: 0.5,
      rightPadding: 0.5,
      topPadding: 0.3,
      bottomPadding: 0.3,
      // Yazdırma yönü
      orientation: 'landscape'  // landscape veya portrait
    };
  });

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showHelpModal, () => setShowHelpModal(false)],
    [selectedLogDetail, () => setSelectedLogDetail(null)],
    [showAddComplianceModal, () => setShowAddComplianceModal(false)],
    [showDisable2FA, () => setShowDisable2FA(false)],
    [showSalesImportPreview, () => setShowSalesImportPreview(false)],
    [showReturnsImportPreview, () => setShowReturnsImportPreview(false)],
    [selectedUserTimeline, () => setSelectedUserTimeline(null)],
    [showDetailedLogs, () => setShowDetailedLogs(false)],
    [showUserModal, () => setShowUserModal(false)],
    [showQrModal, () => setShowQrModal(false)],
    [showLogModal, () => {}],
  ]);

  // Etiket ayarlarını kaydet
  const saveLabelSettings = (newSettings) => {
    setLabelSettings(newSettings);
    localStorage.setItem('labelPrinterSettings', JSON.stringify(newSettings));
    toast.success('Etiket ayarları kaydedildi!');
  };

  // Etiket ayarlarını sıfırla
  const resetLabelSettings = () => {
    const defaultSettings = {
      // Etiket boyutları (mm) - Plastik saplı kuyumcu etiketi
      labelWidth: 70,
      labelHeight: 9,
      leftPrintAreaWidth: 20,
      foldAreaWidth: 3,
      infoPrintAreaWidth: 16,
      tailAreaWidth: 31,
      printAreaWidth: 20,
      middleAreaWidth: 3,
      rightPrintAreaWidth: 16,
      handleAreaWidth: 31,
      // Yazıcı ayarları
      printerDpi: 203,
      printerModel: 'Zebra ZD220',
      // Font boyutları
      fontSizeInfo: 4,
      fontSizeBarcode: 5,
      fontSizeBrand: 5,
      fontSizeCategory: 7,
      fontSizeStock: 4,
      fontSizeValues: 7,
      // Barkod ayarları
      barcodeHeight: 3.5,
      barcodeWidth: 1.0,
      // Varsayılan değerler
      defaultBrand: 'AKİK GOLD JEWELLERY',
      defaultPurity: '14k',
      defaultProfitMargin: 3,
      // Görünüm ayarları - sol baskı alanı
      showBarcode: true,
      showBarcodeNumber: true,
      showBrand: true,
      showProductInfo: true,
      showPurity: true,
      showWeight: true,
      // Görünüm ayarları - sağ baskı alanı
      showCategory: true,
      showHAS: true,
      showUSD: true,
      showGRAM: true,
      showStockCode: true,
      // Kenar boşlukları
      leftPadding: 0.5,
      rightPadding: 0.5,
      topPadding: 0.3,
      bottomPadding: 0.3,
      orientation: 'landscape'
    };
    setLabelSettings(defaultSettings);
    localStorage.setItem('labelPrinterSettings', JSON.stringify(defaultSettings));
    toast.success('Etiket ayarları varsayılana döndürüldü!');
  };

  // Uyumluluk kategorileri
  const complianceCategories = [
    { id: 'data_protection', name: 'Veri Koruma ve Gizlilik', icon: <Eye size={20} />, color: '#3b82f6', description: 'KVKK, GDPR ve veri güvenliği politikaları' },
    { id: 'info_security', name: 'Bilgi Güvenliği', icon: <Server size={20} />, color: '#8b5cf6', description: 'ISO 27001, siber güvenlik ve erişim kontrolü' },
    { id: 'business_continuity', name: 'İş Sürekliliği', icon: <TrendingUp size={20} />, color: '#10b981', description: 'Felaket kurtarma ve iş sürekliliği planları' },
    { id: 'financial_compliance', name: 'Finansal Uyumluluk', icon: <Coins size={20} />, color: '#f59e0b', description: 'Mali düzenlemeler ve denetim gereksinimleri' },
    { id: 'hr_safety', name: 'İK ve İş Güvenliği', icon: <HardHat size={20} />, color: '#ef4444', description: 'Çalışan hakları ve iş sağlığı güvenliği' },
    { id: 'industry_standards', name: 'Sektör Standartları', icon: <Award size={20} />, color: '#06b6d4', description: 'Sektöre özel kalite ve güvenlik standartları' },
    { id: 'environmental_esg', name: 'Çevre ve ESG', icon: <Leaf size={20} />, color: '#22c55e', description: 'Çevresel sürdürülebilirlik ve ESG raporlaması' },
    { id: 'legal_policies', name: 'Yasal Politikalar', icon: <Scale size={20} />, color: '#6366f1', description: 'Şirket politikaları ve yasal uyumluluk' },
    { id: 'ai_automation', name: 'Yapay Zeka ve Otomasyon', icon: <Bot size={20} />, color: '#ec4899', description: 'AI kullanım politikaları ve otomasyon güvenliği' },
    { id: 'audit_reports', name: 'Denetim ve Raporlar', icon: <FileSearch size={20} />, color: '#14b8a6', description: 'İç/dış denetim raporları ve uyumluluk belgeleri' },
  ];

  // Ses ayarı değiştiğinde localStorage'a kaydet
  const handleSoundToggle = (enabled) => {
    setSoundEnabled(enabled);
    localStorage.setItem('soundEnabled', enabled.toString());
  };


  const handleEskiEtiketScanToggle = (enabled) => {
    setEskiEtiketScanEnabled(enabled);
    localStorage.setItem('eskiEtiketScanEnabled', enabled.toString());
  };

  // 2FA durumunu kontrol et
  const check2FAStatus = async () => {
    setLoading2FA(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/2fa/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setTwoFactorEnabled(data.enabled);
      }
    } catch (error) {
      console.error('2FA status check error:', error);
    } finally {
      setLoading2FA(false);
    }
  };

  // 2FA kurulumunu başlat
  const setup2FA = async () => {
    setSetting2FA(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/2fa/setup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setQrCode(data.qrCode);
        setSecret(data.secret);
      } else {
        toast.error(data.message || '2FA kurulumu başlatılamadı');
      }
    } catch (error) {
      console.error('2FA setup error:', error);
      toast.error('2FA kurulumu başlatılamadı');
    } finally {
      setSetting2FA(false);
    }
  };

  // 2FA doğrulama ve etkinleştirme
  const verify2FA = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Lütfen 6 haneli doğrulama kodunu girin');
      return;
    }

    setSetting2FA(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/2fa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: verifyCode })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('İki faktörlü doğrulama etkinleştirildi!');
        setTwoFactorEnabled(true);
        setQrCode('');
        setSecret('');
        setVerifyCode('');
      } else {
        toast.error(data.message || 'Doğrulama başarısız');
      }
    } catch (error) {
      console.error('2FA verify error:', error);
      toast.error('Doğrulama başarısız');
    } finally {
      setSetting2FA(false);
    }
  };

  // 2FA devre dışı bırak
  const disable2FA = async () => {
    if (!disablePassword) {
      toast.error('Lütfen şifrenizi girin');
      return;
    }
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Lütfen 6 haneli doğrulama kodunu girin');
      return;
    }

    setSetting2FA(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/2fa/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: disablePassword, code: disableCode })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('İki faktörlü doğrulama devre dışı bırakıldı');
        setTwoFactorEnabled(false);
        setShowDisable2FA(false);
        setDisablePassword('');
        setDisableCode('');
      } else {
        toast.error(data.message || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('2FA disable error:', error);
      toast.error('İşlem başarısız');
    } finally {
      setSetting2FA(false);
    }
  };

  // 2FA kurulumunu iptal et
  const cancel2FASetup = () => {
    setQrCode('');
    setSecret('');
    setVerifyCode('');
  };

  // Satış silme izni ayarını getir
  const fetchSaleDeletePermission = async () => {
    setLoadingSaleDeleteSetting(true);
    try {
      const response = await fetch(`${API_URL}/settings/sale-delete-permission`);
      const data = await response.json();
      setSaleDeleteAllowed(data.allowed);
    } catch (error) {
      console.error('Satış silme ayarı yüklenemedi:', error);
    } finally {
      setLoadingSaleDeleteSetting(false);
    }
  };

  // Satış silme izni ayarını güncelle
  const toggleSaleDeletePermission = async (allowed) => {
    setLoadingSaleDeleteSetting(true);
    try {
      const response = await fetch(`${API_URL}/settings/sale-delete-permission`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowed })
      });
      const data = await response.json();
      if (data.success) {
        setSaleDeleteAllowed(data.allowed);
        toast.success(allowed ? 'Satış silme özelliği açıldı' : 'Satış silme özelliği kapatıldı');

        // Log kaydet
        logService.log('settings_change', 'sale_delete_permission', {
          action: allowed ? 'enabled' : 'disabled',
          userId: user?.id,
          userName: user?.name
        });
      }
    } catch (error) {
      console.error('Satış silme ayarı güncellenemedi:', error);
      toast.error('Ayar güncellenemedi');
    } finally {
      setLoadingSaleDeleteSetting(false);
    }
  };

  // Uyumluluk verilerini getir
  const fetchComplianceItems = async () => {
    setLoadingCompliance(true);
    try {
      const response = await fetch(`${API_URL}/compliance/items`);
      const data = await response.json();
      setComplianceItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Uyumluluk verileri yüklenemedi:', error);
      setComplianceItems([]);
    } finally {
      setLoadingCompliance(false);
    }
  };

  // Yeni uyumluluk öğesi ekle
  const addComplianceItem = async () => {
    if (!newComplianceItem.title || !newComplianceItem.category) {
      toast.error('Lütfen başlık ve kategori seçin');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/compliance/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newComplianceItem)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Uyumluluk öğesi eklendi');
        setShowAddComplianceModal(false);
        setNewComplianceItem({ title: '', description: '', category: '', status: 'pending' });
        fetchComplianceItems();
        logService.log('compliance', 'create', `Uyumluluk öğesi eklendi: ${newComplianceItem.title}`, {
          entity_type: 'compliance',
          details: { kategori: newComplianceItem.category, baslik: newComplianceItem.title },
          status: 'success'
        });
      } else {
        toast.error(data.message || 'Eklenemedi');
      }
    } catch (error) {
      console.error('Uyumluluk öğesi eklenemedi:', error);
      toast.error('Uyumluluk öğesi eklenemedi');
    }
  };

  // Uyumluluk öğesi durumunu güncelle
  const updateComplianceStatus = async (id, status) => {
    const item = complianceItems.find(i => i.id === id);
    const oldStatus = item?.status;
    try {
      const response = await fetch(`${API_URL}/compliance/items/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Durum güncellendi');
        // Log kaydet
        logService.log('settings', 'update', `Uyumluluk öğesi güncellendi: ${item?.title || id}`, {
          entity_type: 'compliance_item',
          entity_id: id,
          status: 'info',
          details: {
            title: item?.title,
            old_status: oldStatus,
            new_status: status
          }
        });
        fetchComplianceItems();
      }
    } catch (error) {
      console.error('Durum güncellenemedi:', error);
      toast.error('Durum güncellenemedi');
    }
  };

  // Uyumluluk öğesi sil
  const deleteComplianceItem = async (id) => {
    // Silinecek öğeyi bul
    const itemToDelete = complianceItems.find(item => item.id === id);

    showConfirmation({
      title: 'Öğe Silme',
      message: 'Bu öğeyi silmek istediğinize emin misiniz?',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/compliance/items/${id}`, {
            method: 'DELETE'
          });
          const data = await response.json();
          if (data.success) {
            toast.success('Öğe silindi');
            // Log kaydet
            logService.log('settings', 'delete', `Uyumluluk öğesi silindi: ${itemToDelete?.title || id}`, {
              entity_type: 'compliance_item',
              entity_id: id,
              status: 'warning',
              details: {
                title: itemToDelete?.title,
                category: itemToDelete?.category,
                description: itemToDelete?.description
              }
            });
            fetchComplianceItems();
          }
        } catch (error) {
          console.error('Öğe silinemedi:', error);
          toast.error('Öğe silinemedi');
          logService.log('settings', 'delete', `Uyumluluk öğesi silinemedi: ${id}`, {
            status: 'error',
            details: { error: error.message }
          });
        }
      }
    });
  };

  // Kategoriye göre öğeleri getir
  const getItemsByCategory = (categoryId) => {
    return complianceItems.filter(item => item.category === categoryId);
  };

  const tabs = [
    { id: 'general', name: 'Genel', icon: <SettingsIcon size={18} /> },
    ...(isAdmin ? [{ id: 'users', name: 'Kullanıcılar', icon: <Users size={18} /> }] : []),
    { id: 'printer', name: 'Yazıcı Ayarları', icon: <Printer size={18} /> },
    ...(isAdmin ? [{ id: 'backup', name: 'Yedekleme', icon: <Database size={18} /> }] : []),
    ...(isAdmin ? [{ id: 'logs', name: 'Log Kayıtları', icon: <History size={18} /> }] : []),
    ...(isAdmin ? [{ id: 'sales-import', name: 'Satış Aktarımı', icon: <FileUp size={18} /> }] : []),
    ...(isAdmin ? [{ id: 'returns-import', name: 'İade Aktarımı', icon: <RotateCcw size={18} /> }] : []),
    { id: 'security', name: 'Güvenlik', icon: <Shield size={18} /> },
    // ...(isAdmin ? [{ id: 'efatura', name: 'E-Fatura / E-Arşiv', icon: <FileText size={18} /> }] : []),
    ...(isAdmin ? [{ id: 'compliance', name: 'Uyumluluk', icon: <ClipboardCheck size={18} /> }] : []),
    { id: 'legal', name: 'Yasal Bilgiler', icon: <ScrollText size={18} /> },
    // Geçici: ERP'nin eski Shopify/Trendyol pazaryeri ayarları (e-ticaret Akik Pazar'a taşınana kadar).
    // Sekme değil, kendi menüsü ve canlı sayaçları olan tam sayfa — tıklanınca /marketplace/ayarlar açılır.
    ...((isAdmin || user?.permissions?.canAccessShopify === true)
      ? [{ id: 'marketplace-settings', name: 'Pazaryeri Ayarları', icon: <ShoppingBag size={18} />, path: '/marketplace/ayarlar' }]
      : []),
  ];

  // Kullanıcıları getir
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API_URL}/users`);
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Mağazaları getir
  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_URL}/stores`);
      const data = await response.json();
      setStores(data);
    } catch (error) {
      console.error('Mağazalar yüklenemedi:', error);
    }
  };

  // Satış sorumlularını getir
  const fetchSalesStaff = async () => {
    try {
      const response = await fetch(`${API_URL}/sales-staff`);
      const data = await response.json();
      setSalesStaffList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Satış sorumluları yüklenemedi:', error);
    }
  };

  // Terminal kullanıcı formunda varsayılan sorumlu listesi: sadece seçili mağazanın personeli
  useEffect(() => {
    if (userForm.role !== 'terminal' || !userForm.store_id) { setFormStoreStaffList([]); return; }
    let cancelled = false;
    fetch(`${API_URL}/sales-staff?store_id=${userForm.store_id}`)
      .then(r => r.json())
      .then(data => { if (!cancelled) setFormStoreStaffList(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setFormStoreStaffList([]); });
    return () => { cancelled = true; };
  }, [userForm.role, userForm.store_id]);

  // Log saklama süresi ayarını getir
  const fetchLogRetentionSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/settings/log-retention`);
      const data = await response.json();
      if (data.days) setLogRetentionDays(data.days);
    } catch (error) {
      console.error('Log saklama süresi ayarı yüklenemedi:', error);
    }
  };

  // Log saklama süresi ayarını kaydet
  const saveLogRetention = async (days) => {
    setSavingLogRetention(true);
    try {
      const response = await fetch(`${API_URL}/settings/log-retention`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: parseInt(days, 10), user_role: user?.role })
      });
      const data = await response.json();
      if (data.success) {
        setLogRetentionDays(parseInt(days, 10));
        toast.success('Log saklama süresi güncellendi!');
      } else {
        toast.error(data.error || 'Ayar kaydedilemedi');
      }
    } catch (error) {
      toast.error('Log saklama süresi kaydedilemedi');
    } finally {
      setSavingLogRetention(false);
    }
  };

  // Tamirat teslim override ayarlarını getir
  const fetchRepairOverrideSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/settings/repair-deliver-override`);
      const data = await response.json();
      setRepairOverrideEnabled(!!data.enabled);
      setRepairOverrideHasPassword(!!data.hasPassword);
    } catch (error) {
      console.error('Tamirat teslim override ayarı yüklenemedi:', error);
    }
  };

  // Tamirat teslim override toggle (enabled aç/kapat)
  const saveRepairOverrideEnabled = async (enabled) => {
    try {
      const response = await fetch(`${API_URL}/settings/repair-deliver-override`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      const data = await response.json();
      if (data.success) {
        setRepairOverrideEnabled(!!data.enabled);
        setRepairOverrideHasPassword(!!data.hasPassword);
        if (!enabled) setRepairOverridePassword('');
      } else {
        toast.error(data.error || 'Ayar kaydedilemedi');
      }
    } catch (error) {
      toast.error('Tamirat teslim ayarı kaydedilemedi');
    }
  };

  // Tamirat teslim override şifresini kaydet
  const saveRepairOverridePassword = async () => {
    const pw = repairOverridePassword.trim();
    if (!pw) {
      toast.warning('Lütfen bir şifre girin!');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/settings/repair-deliver-override`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: true, password: pw })
      });
      const data = await response.json();
      if (data.success) {
        setRepairOverrideEnabled(true);
        setRepairOverrideHasPassword(true);
        setRepairOverridePassword('');
        toast.success('Teslim şifresi kaydedildi!');
      } else {
        toast.error(data.error || 'Şifre kaydedilemedi');
      }
    } catch (error) {
      toast.error('Şifre kaydedilemedi');
    }
  };

  // Kasiyer maksimum indirim limitlerini getir
  const fetchMaxDiscountSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/settings/max-discount`);
      const data = await response.json();
      setCashierDiscountLimits(data.map(c => ({
        id: c.id,
        name: c.name,
        max_discount_percent: c.max_discount_percent !== null ? String(c.max_discount_percent) : '',
        max_scrap_bonus_percent: c.max_scrap_bonus_percent !== null ? String(c.max_scrap_bonus_percent) : ''
      })));
    } catch (error) {
      console.error('Kasiyer indirim limitleri yüklenemedi:', error);
    }
  };

  // Kasiyer maksimum indirim limitlerini kaydet
  const saveMaxDiscountSettings = async () => {
    setSavingMaxDiscount(true);
    try {
      const limits = cashierDiscountLimits.map(c => ({
        id: c.id,
        max_discount_percent: c.max_discount_percent !== '' ? parseFloat(c.max_discount_percent) : null,
        max_scrap_bonus_percent: c.max_scrap_bonus_percent !== '' ? parseFloat(c.max_scrap_bonus_percent) : null
      }));
      const response = await fetch(`${API_URL}/settings/max-discount`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limits, user_role: user?.role })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Kasiyer indirim limitleri güncellendi!');
      } else {
        toast.error(data.error || 'Ayar kaydedilemedi');
      }
    } catch (error) {
      toast.error('İndirim limitleri kaydedilemedi');
    } finally {
      setSavingMaxDiscount(false);
    }
  };

  // Kullanıcılar tabı açıldığında yükle
  useEffect(() => {
    if (activeTab === 'general') {
      fetchLogRetentionSettings();
      fetchRepairOverrideSettings();
      if (isAdmin) fetchMaxDiscountSettings();
    }
    if (activeTab === 'users') {
      fetchUsers();
      fetchStores();
      fetchSalesStaff();
    }
    if (activeTab === 'backup') {
      fetchBackups();
      fetchBackupSettings();
      fetchProductImageBackups();
    }
    if (activeTab === 'efatura') {
      fetchEfaturaSettings();
    }
    if (activeTab === 'efatura') {
      fetchEfaturaStats();
      fetchInvoices();
    }
    if (activeTab === 'security') {
      check2FAStatus();
      fetchSaleDeletePermission();
    }
    if (activeTab === 'compliance') {
      fetchComplianceItems();
    }
    if (activeTab === 'logs') {
      fetchLogData();
      fetchLogSummary();
    }
    if (activeTab === 'sales-import') {
      fetchStores();
    }
    if (activeTab === 'returns-import') {
      fetchStores();
    }
    if (activeTab === 'printer') {
      fetchPrinters();
    }
  }, [activeTab]);

  // Sistemdeki yazıcıları getir (lokal agent > electron > sunucu API sırasıyla dener)
  const fetchPrinters = async () => {
    setLoadingPrinters(true);
    try {
      if (window.electronAPI) {
        // Electron: doğrudan sistem yazıcılarını al
        const printers = await window.electronAPI.getPrinters();
        setPrinterList(printers.map(p => ({
          name: p.name,
          rawName: p.name,
          enabled: p.status === 0,
        })));
      } else {
        // Web: önce lokal agent, sonra sunucu API
        const { printers, source } = await fetchPrintersFromAgent();
        setPrinterList(printers);
        if (source === 'local') {
          toast.info('Yazıcılar lokal servis üzerinden alındı', { autoClose: 3000 });
        } else if (printers.length === 0) {
          toast.warning('Yazıcı bulunamadı. Bilgisayarınızda yazıcı servisini başlatın: cd print-agent && node agent.js', { autoClose: 7500 });
        }
      }
    } catch (error) {
      console.error('Yazıcı listesi alınamadı:', error);
      setPrinterList([]);
    } finally {
      setLoadingPrinters(false);
    }
  };

  // Log verilerini getir
  const fetchLogData = async () => {
    setLoadingLogs(true);
    try {
      const params = new URLSearchParams({ days: 30 });
      if (logFilters.module) params.append('module', logFilters.module);
      if (logFilters.action) params.append('action', logFilters.action);
      if (logFilters.status) params.append('status', logFilters.status);
      if (logFilters.search) params.append('search', logFilters.search);

      const response = await fetch(`${API_URL}/logs/by-day?${params}`);
      const data = await response.json();
      setLogData(data);
    } catch (error) {
      console.error('Log verisi getirilemedi:', error);
      toast.error('Log verisi yüklenemedi');
    } finally {
      setLoadingLogs(false);
    }
  };

  // Log özeti getir
  const fetchLogSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/logs/summary`);
      const data = await response.json();
      setLogSummary(data);
    } catch (error) {
      console.error('Log özeti getirilemedi:', error);
    }
  };

  // Eski logları temizle
  const handleCleanupLogs = async () => {
    showConfirmation({
      title: 'Log Temizleme',
      message: '30 günden eski tüm loglar silinecek.\n\nDevam etmek istiyor musunuz?',
      confirmText: 'Evet, Temizle',
      cancelText: 'İptal',
      type: 'warning',
      onConfirm: async () => {
        setCleaningLogs(true);
        try {
          const response = await fetch(`${API_URL}/logs/cleanup?days=30`, { method: 'DELETE' });
          const data = await response.json();
          if (data.success) {
            toast.success(data.message);
            // Log kaydet
            logService.log('settings', 'delete', `Eski loglar temizlendi: ${data.deletedCount || 0} kayıt silindi`, {
              status: 'warning',
              details: {
                deleted_count: data.deletedCount,
                days: 30
              }
            });
            fetchLogData();
            fetchLogSummary();
          }
        } catch (error) {
          console.error('Log temizleme hatası:', error);
          toast.error('Loglar temizlenemedi');
          logService.log('settings', 'delete', 'Log temizleme hatası', {
            status: 'error',
            details: { error: error.message }
          });
        } finally {
          setCleaningLogs(false);
        }
      }
    });
  };

  // Gün genişlet/daralt
  const toggleDayExpand = (date) => {
    setExpandedDays(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  // Log filtrelerini uygula
  const applyLogFilters = () => {
    fetchLogData();
  };

  // Log filtrelerini temizle
  const clearLogFilters = () => {
    setLogFilters({ module: '', action: '', status: '', search: '' });
    setTimeout(() => fetchLogData(), 100);
  };

  // Modül isimleri
  const moduleNames = {
    auth: 'Giriş/Çıkış',
    sales: 'Satış',
    products: 'Ürünler',
    customers: 'Müşteriler',
    repairs: 'Tamir',
    campaigns: 'Kampanyalar',
    inventory: 'Sayım',
    personnel: 'Personel',
    reports: 'Raporlar',
    settings: 'Ayarlar',
    scrap: 'Hurda',
    expenses: 'Masraflar',
    debts: 'Borçlar',
    terminal: 'Terminal',
    rates: 'Kurlar'
  };

  // İşlem isimleri
  const actionNames = {
    login: 'Giriş',
    logout: 'Çıkış',
    login_failed: 'Başarısız Giriş',
    create: 'Oluştur',
    read: 'Görüntüle',
    update: 'Güncelle',
    delete: 'Sil',
    sale_complete: 'Satış Tamamla',
    sale_cancel: 'Satış İptal',
    refund: 'İade',
    exchange: 'Değişim',
    hold_sale: 'Sepet Beklet',
    resume_sale: 'Sepet Devam',
    apply_discount: 'İndirim Uygula',
    apply_campaign: 'Kampanya Uygula',
    price_change: 'Fiyat Değiştir',
    stock_in: 'Stok Alımı',
    stock_out: 'Stok Çıkış',
    stock_adjust: 'Stok Düzeltme',
    inventory_count: 'Sayım',
    debt_add: 'Borç Ekle',
    debt_payment: 'Borç Tahsilatı',
    repair_status_change: 'Tamir Durumu',
    export: 'Dışa Aktar',
    import: 'İçe Aktar',
    backup: 'Yedekleme',
    restore: 'Geri Yükleme',
    view: 'Görüntüle',
    search: 'Arama'
  };

  // Durum renkleri ve isimleri
  const statusConfig = {
    success: { color: '#10b981', name: 'Başarılı', icon: <CheckCircle size={14} /> },
    error: { color: '#ef4444', name: 'Hata', icon: <XCircle size={14} /> },
    warning: { color: '#f59e0b', name: 'Uyarı', icon: <AlertCircle size={14} /> },
    info: { color: '#3b82f6', name: 'Bilgi', icon: <Info size={14} /> }
  };

  // Tarih formatla
  const formatLogDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Bugün';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Dün';
    } else {
      return date.toLocaleDateString('tr-TR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  // Saat formatla
  const formatLogTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // ==========================================
  // DETAYLI AKTİVİTE LOGLARI FONKSİYONLARI
  // ==========================================

  // Aktivite verilerini getir
  const fetchActivityData = async (page = 1) => {
    setLoadingActivity(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (activityFilters.user_id) params.append('user_id', activityFilters.user_id);
      if (activityFilters.activity_type) params.append('activity_type', activityFilters.activity_type);
      if (activityFilters.page_name) params.append('page_name', activityFilters.page_name);
      if (activityFilters.search) params.append('search', activityFilters.search);

      const response = await fetch(`${API_URL}/activity?${params}`);
      const data = await response.json();
      setActivityData(data);
      setActivityPage(page);
    } catch (error) {
      console.error('Aktivite verisi getirilemedi:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Aktivite istatistiklerini getir
  const fetchActivityStats = async () => {
    try {
      const response = await fetch(`${API_URL}/activity/stats`);
      const data = await response.json();
      setActivityStats(data);
    } catch (error) {
      console.error('Aktivite istatistikleri getirilemedi:', error);
    }
  };

  // Kullanıcı aktivite özeti
  const fetchUserActivitySummary = async () => {
    try {
      const response = await fetch(`${API_URL}/activity/user-summary?days=30`);
      const data = await response.json();
      setUserActivitySummary(data);
    } catch (error) {
      console.error('Kullanıcı aktivite özeti getirilemedi:', error);
    }
  };

  // Sayfa aktivite özeti
  const fetchPageActivitySummary = async () => {
    try {
      const response = await fetch(`${API_URL}/activity/page-summary?days=30`);
      const data = await response.json();
      setPageActivitySummary(data);
    } catch (error) {
      console.error('Sayfa aktivite özeti getirilemedi:', error);
    }
  };

  // Kullanıcı timeline
  const fetchUserTimeline = async (userId) => {
    setLoadingTimeline(true);
    try {
      const response = await fetch(`${API_URL}/activity/timeline/${userId}?limit=100`);
      const data = await response.json();
      setUserTimeline(data);
    } catch (error) {
      console.error('Kullanıcı timeline getirilemedi:', error);
    } finally {
      setLoadingTimeline(false);
    }
  };

  // Detaylı loglar yükle
  const loadDetailedLogs = () => {
    setShowDetailedLogs(true);
    fetchActivityData();
    fetchActivityStats();
    fetchUserActivitySummary();
    fetchPageActivitySummary();
  };

  // Aktivite filtreleme
  const applyActivityFilters = () => {
    fetchActivityData(1);
  };

  // Aktivite filtrelerini temizle
  const clearActivityFilters = () => {
    setActivityFilters({ user_id: '', activity_type: '', page_name: '', search: '' });
    setTimeout(() => fetchActivityData(1), 100);
  };

  // Aktivite tipi isimleri (Türkçe)
  const activityTypeNames = {
    page_view: 'Sayfa Görüntüleme',
    page_leave: 'Sayfadan Çıkış',
    click: 'Tıklama',
    form_submit: 'Form Gönderimi',
    search: 'Arama',
    filter: 'Filtreleme',
    modal_open: 'Modal Açma',
    modal_close: 'Modal Kapatma',
    button_click: 'Buton Tıklama',
    tab_change: 'Sekme Değişikliği',
    scroll: 'Kaydırma',
    download: 'İndirme',
    print: 'Yazdırma',
    copy: 'Kopyalama',
    other: 'Diğer'
  };

  // Aktivite tipi renkleri
  const activityTypeColors = {
    page_view: '#3b82f6',
    page_leave: '#64748b',
    click: '#8b5cf6',
    form_submit: '#10b981',
    search: '#f59e0b',
    filter: '#06b6d4',
    modal_open: '#ec4899',
    modal_close: '#f43f5e',
    button_click: '#6366f1',
    tab_change: '#14b8a6',
    download: '#22c55e',
    print: '#a855f7',
    other: '#94a3b8'
  };

  // E-Fatura ayarlarını getir
  const fetchEfaturaSettings = async () => {
    setLoadingEfatura(true);
    try {
      const response = await fetch(`${API_URL}/efatura/settings`);
      const data = await response.json();
      setEfaturaSettings({
        ...efaturaSettings,
        ...data,
        efatura_enabled: data.efatura_enabled === 1 || data.efatura_enabled === true,
        earsiv_enabled: data.earsiv_enabled === 1 || data.earsiv_enabled === true,
        prices_include_tax: data.prices_include_tax === 1 || data.prices_include_tax === true,
        auto_send: data.auto_send === 1 || data.auto_send === true,
        test_mode: data.test_mode === 1 || data.test_mode === true
      });
    } catch (error) {
      console.error('E-Fatura ayarları yüklenemedi:', error);
    } finally {
      setLoadingEfatura(false);
    }
  };

  // E-Fatura ayarlarını kaydet
  const saveEfaturaSettings = async () => {
    setSavingEfatura(true);
    try {
      const response = await fetch(`${API_URL}/efatura/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(efaturaSettings)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('E-Fatura ayarları kaydedildi!');
        logService.log('settings', 'update', 'E-Fatura ayarları güncellendi', {
          entity_type: 'efatura_settings',
          details: {
            efatura_aktif: efaturaSettings.efatura_enabled ? 'Evet' : 'Hayır',
            earsiv_aktif: efaturaSettings.earsiv_enabled ? 'Evet' : 'Hayır',
            otomatik_gonder: efaturaSettings.auto_send ? 'Evet' : 'Hayır',
            test_modu: efaturaSettings.test_mode ? 'Evet' : 'Hayır',
            firma: efaturaSettings.company_name || '-',
            vkn: efaturaSettings.company_vkn || '-'
          },
          status: 'success'
        });
      } else {
        toast.error(data.message || 'Ayarlar kaydedilemedi!');
        logService.log('settings', 'update', 'E-Fatura ayarları kaydedilemedi', {
          entity_type: 'efatura_settings',
          details: { error: data.message },
          status: 'error'
        });
      }
    } catch (error) {
      console.error('E-Fatura ayarları kaydetme hatası:', error);
      toast.error('Ayarlar kaydedilemedi!');
    } finally {
      setSavingEfatura(false);
    }
  };

  // E-Fatura bağlantı testi
  const testEfaturaConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await fetch(`${API_URL}/efatura/test-connection`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        logService.log('settings', 'view', 'E-Fatura bağlantı testi başarılı', {
          entity_type: 'efatura',
          details: { sonuc: data.message },
          status: 'success'
        });
      } else {
        toast.error(data.message);
        logService.log('settings', 'view', 'E-Fatura bağlantı testi başarısız', {
          entity_type: 'efatura',
          details: { hata: data.message },
          status: 'error'
        });
      }
    } catch (error) {
      console.error('Bağlantı testi hatası:', error);
      toast.error('Bağlantı testi yapılamadı!');
    } finally {
      setTestingConnection(false);
    }
  };

  // E-Fatura istatistiklerini getir
  const fetchEfaturaStats = async () => {
    try {
      const response = await fetch(`${API_URL}/efatura/stats`);
      const data = await response.json();
      setEfaturaStats(data);
    } catch (error) {
      console.error('E-Fatura istatistikleri yüklenemedi:', error);
    }
  };

  // Faturaları getir
  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      let url = `${API_URL}/efatura/invoices?limit=50`;
      if (invoiceFilter.status) url += `&status=${invoiceFilter.status}`;
      if (invoiceFilter.scenario) url += `&scenario=${invoiceFilter.scenario}`;
      if (invoiceFilter.search) url += `&search=${encodeURIComponent(invoiceFilter.search)}`;
      if (invoiceFilter.startDate) url += `&startDate=${invoiceFilter.startDate}`;
      if (invoiceFilter.endDate) url += `&endDate=${invoiceFilter.endDate}`;

      const response = await fetch(url);
      const data = await response.json();
      setInvoices(data.invoices || []);
    } catch (error) {
      console.error('Faturalar yüklenemedi:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  // PDF ve XML indirme fonksiyonlari
  const handleDownloadPdf = async (invoice) => {
    try {
      const externalId = invoice.external_invoice_id;
      if (!externalId) {
        alert('Bu fatura henuz e-fatura sistemine gonderilmemis');
        return;
      }
      window.open(`${efaturaSettings.efatura_api_url}/v1/invoices/${externalId}/pdf`, '_blank');
    } catch (error) {
      console.error('PDF indirme hatasi:', error);
      alert('PDF indirilemedi');
    }
  };

  const handleDownloadXml = async (invoice) => {
    try {
      const externalId = invoice.external_invoice_id;
      if (!externalId) {
        alert('Bu fatura henuz e-fatura sistemine gonderilmemis');
        return;
      }
      window.open(`${efaturaSettings.efatura_api_url}/v1/invoices/${externalId}/xml`, '_blank');
    } catch (error) {
      console.error('XML indirme hatasi:', error);
      alert('XML indirilemedi');
    }
  };

  const handleViewPdf = async (invoice) => {
    try {
      const externalId = invoice.external_invoice_id;
      if (!externalId) {
        alert('Bu fatura henuz e-fatura sistemine gonderilmemis');
        return;
      }
      // PDF'i yeni sekmede ac
      const pdfUrl = `${efaturaSettings.efatura_api_url}/v1/invoices/${externalId}/pdf`;
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('PDF goruntuleme hatasi:', error);
      alert('PDF goruntuelenemedi');
    }
  };

  // Yedekleri getir
  const fetchBackups = async () => {
    setLoadingBackups(true);
    try {
      const response = await fetch(`${API_URL}/backup/list`);
      const data = await response.json();
      setBackups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Yedekler yüklenemedi:', error);
      setBackups([]);
    } finally {
      setLoadingBackups(false);
    }
  };

  // Ürün + Resim yedeklerini getir
  const fetchProductImageBackups = async () => {
    setLoadingProductImageBackups(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/product-backup/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProductImageBackups(data.backups || []);
    } catch (error) {
      console.error('Ürün yedekleri yüklenemedi:', error);
      setProductImageBackups([]);
    } finally {
      setLoadingProductImageBackups(false);
    }
  };

  // Ürün + Resim yedeği oluştur
  const createProductImageBackup = async () => {
    setCreatingProductImageBackup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/product-backup/create`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Yedekleme başlatıldı! Tamamlanması birkaç dakika sürebilir.');
        setTimeout(() => fetchProductImageBackups(), 30000);
      } else {
        toast.error(data.error || 'Yedekleme başlatılamadı');
      }
    } catch (error) {
      toast.error('Yedekleme başlatılamadı: ' + error.message);
    } finally {
      setCreatingProductImageBackup(false);
    }
  };

  // Yedekleme ayarlarını getir
  const fetchBackupSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/backup/settings`);
      const data = await response.json();
      setAutoBackupEnabled(data.auto_backup_enabled || false);
      setAutoBackupInterval(data.auto_backup_interval || 24);
    } catch (error) {
      console.error('Yedekleme ayarları yüklenemedi:', error);
    }
  };

  // Yedekleme ayarlarını kaydet
  const saveBackupSettings = async () => {
    setSavingBackupSettings(true);
    try {
      const response = await fetch(`${API_URL}/backup/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auto_backup_enabled: autoBackupEnabled,
          auto_backup_interval: autoBackupInterval
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(autoBackupEnabled ? 'Otomatik yedekleme aktif!' : 'Otomatik yedekleme kapatıldı!');
        logService.log('settings', 'update',
          `Otomatik yedekleme ayarları güncellendi: ${autoBackupEnabled ? 'Aktif' : 'Kapalı'}`, {
          entity_type: 'backup_settings',
          details: {
            otomatik_yedekleme: autoBackupEnabled ? 'Aktif' : 'Kapalı',
            yedekleme_sikligi: `${autoBackupInterval} saat`
          },
          status: 'success'
        });
        fetchBackups();
      } else {
        toast.error('Ayarlar kaydedilemedi!');
        logService.log('settings', 'update', 'Yedekleme ayarları kaydedilemedi', {
          entity_type: 'backup_settings',
          status: 'error'
        });
      }
    } catch (error) {
      console.error('Ayar kaydetme hatası:', error);
      toast.error('Ayarlar kaydedilemedi!');
    } finally {
      setSavingBackupSettings(false);
    }
  };

  // Yeni yedek oluştur (JSON veya SQL)
  const createBackup = async (type = 'json') => {
    setCreatingBackup(true);
    try {
      const response = await fetch(`${API_URL}/backup/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`${type.toUpperCase()} yedek başarıyla oluşturuldu!`);
        logService.log('settings', 'backup',
          `${type.toUpperCase()} yedek oluşturuldu`, {
          entity_type: 'backup',
          details: {
            yedek_turu: type.toUpperCase(),
            dosya_adi: data.filename || '-'
          },
          status: 'success'
        });
        fetchBackups();
      } else {
        toast.error(data.error || 'Yedek oluşturulamadı!');
        logService.log('settings', 'backup', `${type.toUpperCase()} yedek oluşturulamadı`, {
          entity_type: 'backup',
          details: { error: data.error, tur: type.toUpperCase() },
          status: 'error'
        });
      }
    } catch (error) {
      console.error('Yedek oluşturma hatası:', error);
      toast.error('Yedek oluşturulamadı!');
      logService.log('settings', 'backup', `Yedek oluşturma hatası: ${type.toUpperCase()}`, {
        entity_type: 'backup',
        details: { error: error.message },
        status: 'error'
      });
    } finally {
      setCreatingBackup(false);
    }
  };

  // Yedek indir
  const downloadBackup = (filename) => {
    logService.log('settings', 'export', `Yedek indirildi: ${filename}`, {
      entity_type: 'backup',
      details: { dosya_adi: filename },
      status: 'success'
    });
    window.open(withApiToken(`${API_URL}/backup/download/${filename}`), '_blank');
  };

  // Yedek sil
  const deleteBackup = async (filename) => {
    showConfirmation({
      title: 'Yedek Silme',
      message: 'Bu yedeği silmek istediğinize emin misiniz?',
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/backup/${filename}`, {
            method: 'DELETE'
          });
          const data = await response.json();
          if (data.success) {
            toast.success('Yedek silindi!');
            logService.log('settings', 'delete', `Yedek dosyası silindi: ${filename}`, {
              entity_type: 'backup',
              details: { dosya_adi: filename },
              status: 'success'
            });
            fetchBackups();
          } else {
            toast.error(data.error || 'Yedek silinemedi!');
            logService.log('settings', 'delete', `Yedek silinemedi: ${filename}`, {
              entity_type: 'backup',
              details: { dosya_adi: filename, error: data.error },
              status: 'error'
            });
          }
        } catch (error) {
          console.error('Yedek silme hatası:', error);
          toast.error('Yedek silinemedi!');
        }
      }
    });
  };

  // Mevcut yedekten geri yükle
  const restoreFromBackup = async (filename) => {
    // Geri yükleme işlemini gerçekleştiren fonksiyon
    const performRestore = async () => {
      setRestoringBackup(true);
      try {
        const response = await fetch(`${API_URL}/backup/restore/${filename}`, {
          method: 'POST'
        });
        const data = await response.json();
        if (data.success) {
          toast.success(data.message || 'Yedek başarıyla geri yüklendi!');
          logService.log('settings', 'restore', `Yedek geri yüklendi: ${filename}`, {
            entity_type: 'backup',
            details: { dosya_adi: filename, stats: data.stats },
            status: 'success'
          });

          // Geri yükleme sonrası sayfayı yenile
          if (data.requiresRefresh) {
            setRestoringBackup(false);
            toast.info('Sayfa 3 saniye içinde yenilenecek...', { autoClose: 4500 });
            setTimeout(() => {
              window.location.reload();
            }, 3000);
            return;
          }
        } else {
          toast.error(data.error || 'Geri yükleme başarısız!');
          logService.log('settings', 'restore', `Yedek geri yüklenemedi: ${filename}`, {
            entity_type: 'backup',
            details: { dosya_adi: filename, error: data.error },
            status: 'error'
          });
        }
      } catch (error) {
        console.error('Geri yükleme hatası:', error);
        toast.error('Geri yükleme başarısız!');
        logService.log('settings', 'restore', `Geri yükleme hatası: ${filename}`, {
          entity_type: 'backup',
          details: { dosya_adi: filename, error: error.message },
          status: 'error'
        });
      } finally {
        setRestoringBackup(false);
      }
    };

    // İlk onay
    showConfirmation({
      title: 'Yedek Geri Yükleme',
      message: 'DİKKAT: Mevcut tüm veriler silinecek ve bu yedekle değiştirilecek.\n\nBu işlem geri alınamaz! Devam etmek istiyor musunuz?',
      confirmText: 'Evet, Devam Et',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: () => {
        // İkinci onay
        showConfirmation({
          title: 'Son Onay',
          message: 'ONAY: Bu işlem TÜM müşteri, ürün, satış ve diğer verileri etkileyecek.\n\nSON KEZ: Devam etmek istediğinizden emin misiniz?',
          confirmText: 'Evet, Geri Yükle',
          cancelText: 'Vazgeç',
          type: 'danger',
          onConfirm: performRestore
        });
      }
    });
  };

  // Dosyadan yedek yükle (JSON veya SQL)
  const handleFileRestore = async (event) => {
    const file = event.target.files[0];
    const inputElement = event.target;
    if (!file) return;

    if (!file.name.endsWith('.json') && !file.name.endsWith('.sql')) {
      toast.error('Sadece JSON veya SQL yedek dosyaları yüklenebilir!');
      inputElement.value = '';
      return;
    }

    // Geri yükleme işlemini gerçekleştiren fonksiyon
    const performFileRestore = async () => {
      setRestoringBackup(true);
      const formData = new FormData();
      formData.append('backup', file);

      try {
        const response = await fetch(`${API_URL}/backup/restore`, {
          method: 'POST',
          body: formData
        });
        const data = await response.json();
        if (data.success) {
          toast.success(data.message || 'Yedek başarıyla geri yüklendi!');
          logService.log('settings', 'restore', `Dosyadan yedek geri yüklendi: ${file.name}`, {
            entity_type: 'backup',
            details: {
              dosya_adi: file.name,
              dosya_boyutu: `${(file.size / 1024).toFixed(2)} KB`,
              stats: data.stats
            },
            status: 'success'
          });

          // Geri yükleme sonrası sayfayı yenile
          if (data.requiresRefresh) {
            setRestoringBackup(false);
            inputElement.value = '';
            toast.info('Sayfa 3 saniye içinde yenilenecek...', { autoClose: 4500 });
            setTimeout(() => {
              window.location.reload();
            }, 3000);
            return;
          }
          fetchBackups();
        } else {
          toast.error(data.error || 'Geri yükleme başarısız!');
          logService.log('settings', 'restore', `Dosyadan geri yükleme başarısız: ${file.name}`, {
            entity_type: 'backup',
            details: { dosya_adi: file.name, error: data.error },
            status: 'error'
          });
        }
      } catch (error) {
        console.error('Geri yükleme hatası:', error);
        toast.error('Geri yükleme başarısız!');
        logService.log('settings', 'restore', `Dosyadan geri yükleme hatası: ${file.name}`, {
          entity_type: 'backup',
          details: { dosya_adi: file.name, error: error.message },
          status: 'error'
        });
      } finally {
        setRestoringBackup(false);
        inputElement.value = '';
      }
    };

    // İlk onay
    showConfirmation({
      title: 'Dosyadan Yedek Yükleme',
      message: `DİKKAT: "${file.name}" dosyası yüklenecek.\n\nMevcut tüm veriler silinecek ve bu yedekle değiştirilecek.\n\nBu işlem geri alınamaz! Devam etmek istiyor musunuz?`,
      confirmText: 'Evet, Devam Et',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: () => {
        // İkinci onay
        showConfirmation({
          title: 'Son Onay',
          message: 'ONAY: Bu işlem TÜM müşteri, ürün, satış ve diğer verileri etkileyecek.\n\nSON KEZ: Devam etmek istediğinizden emin misiniz?',
          confirmText: 'Evet, Geri Yükle',
          cancelText: 'Vazgeç',
          type: 'danger',
          onConfirm: performFileRestore
        });
      }
    });
  };

  // Tarihi formatla
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR') + ' - ' + date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  // Yeni kullanıcı modalını aç
  const openNewUserModal = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', password: '', role: 'user', store_id: '', permissions: { canEditPrice: true }, default_sales_staff_id: '' });
    setShowUserModal(true);
  };

  // Düzenleme modalını aç
  const openEditUserModal = (user) => {
    setEditingUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      store_id: user.store_id || '',
      permissions: user.permissions || { canEditPrice: true },
      default_sales_staff_id: user.default_sales_staff_id || ''
    });
    setShowUserModal(true);
  };

  // Kullanıcı kaydet (yeni veya güncelle)
  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email) {
      toast.warning('Ad ve email gerekli!');
      return;
    }

    if (!editingUser && !userForm.password) {
      toast.warning('Yeni kullanıcı için şifre gerekli!');
      return;
    }

    // Kasiyer, Terminal ve Depo Sorumlusu için mağaza zorunlu
    if ((userForm.role === 'cashier' || userForm.role === 'terminal' || userForm.role === 'depo') && !userForm.store_id) {
      toast.warning('Bu rol için mağaza seçimi zorunludur!');
      return;
    }

    const roleNames = { admin: 'Yönetici', cashier: 'Kasiyer', terminal: 'El Terminali', atolye: 'Atölye', depo: 'Depo Sorumlusu', user: 'Kullanıcı' };
    const storeName = stores.find(s => s.id === parseInt(userForm.store_id))?.name || '';

    try {
      if (editingUser) {
        // Güncelleme
        const response = await fetch(`${API_URL}/users/${editingUser.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(userForm)
        });
        const data = await response.json();
        if (data.success) {
          toast.success('Kullanıcı güncellendi!');
          // Değişiklikleri tespit et
          const changes = [];
          if (editingUser.name !== userForm.name) changes.push(`Ad: "${editingUser.name}" → "${userForm.name}"`);
          if (editingUser.email !== userForm.email) changes.push(`Email: "${editingUser.email}" → "${userForm.email}"`);
          if (editingUser.role !== userForm.role) changes.push(`Rol: ${roleNames[editingUser.role]} → ${roleNames[userForm.role]}`);
          if (userForm.password) changes.push('Şifre değiştirildi');

          logService.log('settings', 'update',
            `Kullanıcı güncellendi: ${userForm.name} (${roleNames[userForm.role]})`, {
            entity_type: 'user',
            entity_id: editingUser.id,
            details: {
              kullanici: userForm.name,
              email: userForm.email,
              rol: roleNames[userForm.role],
              magaza: storeName || '-',
              degisiklikler: changes.length > 0 ? changes : ['Değişiklik yapılmadı']
            },
            status: 'success'
          });
          setShowUserModal(false);
          fetchUsers();
        } else {
          toast.error(data.message || 'Güncelleme başarısız!');
          logService.log('settings', 'update', `Kullanıcı güncellenemedi: ${userForm.name}`, {
            entity_type: 'user',
            details: { error: data.message },
            status: 'error'
          });
        }
      } else {
        // Yeni kullanıcı
        const response = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(userForm)
        });
        const data = await response.json();
        if (data.success) {
          toast.success('Kullanıcı oluşturuldu!');
          logService.log('settings', 'create',
            `Yeni kullanıcı oluşturuldu: ${userForm.name} (${roleNames[userForm.role]})`, {
            entity_type: 'user',
            entity_id: data.user?.id,
            details: {
              kullanici: userForm.name,
              email: userForm.email,
              rol: roleNames[userForm.role],
              magaza: storeName || '-'
            },
            status: 'success'
          });
          setShowUserModal(false);
          fetchUsers();
        } else {
          toast.error(data.message || 'Kullanıcı oluşturulamadı!');
          logService.log('settings', 'create', `Kullanıcı oluşturulamadı: ${userForm.name}`, {
            entity_type: 'user',
            details: { error: data.message },
            status: 'error'
          });
        }
      }
    } catch (error) {
      console.error('Kullanıcı kaydetme hatası:', error);
      toast.error('Bir hata oluştu!');
      logService.log('settings', editingUser ? 'update' : 'create',
        `Kullanıcı ${editingUser ? 'güncelleme' : 'oluşturma'} hatası: ${userForm.name}`, {
        entity_type: 'user',
        details: { error: error.message },
        status: 'error'
      });
    }
  };

  // Kullanıcı sil
  const handleDeleteUser = async (user) => {
    const roleNames = { admin: 'Yönetici', cashier: 'Kasiyer', terminal: 'El Terminali', atolye: 'Atölye', depo: 'Depo Sorumlusu', user: 'Kullanıcı' };

    if (user.role === 'admin') {
      toast.error('Admin kullanıcısı silinemez!');
      return;
    }

    showConfirmation({
      title: 'Kullanıcı Silme',
      message: `"${user.name}" kullanıcısını silmek istediğinize emin misiniz?`,
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/users/${user.id}`, {
            method: 'DELETE'
          });
          const data = await response.json();
          if (data.success) {
            toast.success('Kullanıcı silindi!');
            logService.log('settings', 'delete',
              `Kullanıcı silindi: ${user.name} (${roleNames[user.role]})`, {
              entity_type: 'user',
              entity_id: user.id,
              details: {
                kullanici: user.name,
                email: user.email,
                rol: roleNames[user.role]
              },
              status: 'success'
            });
            fetchUsers();
          } else {
            toast.error(data.message || 'Silme başarısız!');
            logService.log('settings', 'delete', `Kullanıcı silinemedi: ${user.name}`, {
              entity_type: 'user',
              entity_id: user.id,
              details: { error: data.message },
              status: 'error'
            });
          }
        } catch (error) {
          console.error('Kullanıcı silme hatası:', error);
          toast.error('Bir hata oluştu!');
          logService.log('settings', 'delete', `Kullanıcı silme hatası: ${user.name}`, {
            entity_type: 'user',
            entity_id: user.id,
            details: { error: error.message },
            status: 'error'
          });
        }
      }
    });
  };

  // QR Kodu Token Al
  const fetchQrToken = async (userId) => {
    setLoadingQrToken(true);
    try {
      const response = await fetch(`${API_URL}/users/${userId}/qr-token`);
      const data = await response.json();
      if (data.success) {
        if (data.hasToken) {
          setQrLoginCode(data.qrCode);
          setQrTokenActive(true);
        } else {
          setQrLoginCode('');
          setQrTokenActive(false);
        }
      }
    } catch (error) {
      console.error('QR token alma hatası:', error);
    } finally {
      setLoadingQrToken(false);
    }
  };

  // QR Kodu Token Oluştur
  const generateQrToken = async (userId) => {
    setLoadingQrToken(true);
    try {
      const response = await fetch(`${API_URL}/users/${userId}/qr-token`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        setQrLoginCode(data.qrCode);
        setQrTokenActive(true);
        toast.success('QR kodu oluşturuldu!');
      } else {
        toast.error(data.message || 'QR kodu oluşturulamadı!');
      }
    } catch (error) {
      console.error('QR token oluşturma hatası:', error);
      toast.error('Bir hata oluştu!');
    } finally {
      setLoadingQrToken(false);
    }
  };

  // QR Kodu Token İptal Et
  const revokeQrToken = async (userId) => {
    showConfirmation({
      title: 'QR Kodu İptali',
      message: 'QR kodunu iptal etmek istediğinize emin misiniz?\n\nKullanıcı bu QR kodu ile giriş yapamayacak.',
      confirmText: 'Evet, İptal Et',
      cancelText: 'Vazgeç',
      type: 'warning',
      onConfirm: async () => {
        setLoadingQrToken(true);
        try {
          const response = await fetch(`${API_URL}/users/${userId}/qr-token`, {
            method: 'DELETE'
          });
          const data = await response.json();
          if (data.success) {
            setQrLoginCode('');
            setQrTokenActive(false);
            toast.success('QR kodu iptal edildi!');
          } else {
            toast.error(data.message || 'QR kodu iptal edilemedi!');
          }
        } catch (error) {
          console.error('QR token iptal hatası:', error);
          toast.error('Bir hata oluştu!');
        } finally {
          setLoadingQrToken(false);
        }
      }
    });
  };

  // QR kodunu indir
  const downloadQrCode = (userName) => {
    if (!qrLoginCode) return;
    const link = document.createElement('a');
    link.href = qrLoginCode;
    link.download = `qr-login-${userName.replace(/\s+/g, '-').toLowerCase()}.png`;
    link.click();
  };

  // QR modalını aç
  const openQrModal = async (userId) => {
    setShowQrModal(true);
    await fetchQrToken(userId);
  };

  // Kullanıcı durumunu değiştir
  const toggleUserStatus = async (user) => {
    const newStatus = user.active ? 'Pasif' : 'Aktif';
    try {
      const response = await fetch(`${API_URL}/users/${user.id}/toggle`, {
        method: 'PATCH'
      });
      const data = await response.json();
      if (data.success) {
        logService.log('settings', 'update',
          `Kullanıcı durumu değiştirildi: ${user.name} → ${newStatus}`, {
          entity_type: 'user',
          entity_id: user.id,
          details: {
            kullanici: user.name,
            onceki_durum: user.active ? 'Aktif' : 'Pasif',
            yeni_durum: newStatus
          },
          status: 'success'
        });
        fetchUsers();
      }
    } catch (error) {
      console.error('Durum değiştirme hatası:', error);
    }
  };

  // Satış aktarımı - Şablon indir
  const downloadSalesImportTemplate = () => {
    const templateData = [
      {
        'Fiş No': 'F001',
        'Tarih': '2024-01-15',
        'Saat': '14:30',
        'Kasiyer ID': '20',
        'Müşteri Ad Soyad': 'Mehmet Yılmaz',
        'Barkodlar': '06010001,06010002',
        'Toplam Tutar': 1500,
        'İndirim': 100,
        'Alınacak Tutar': 1400,
        'Açıklama': 'Örnek satış'
      },
      {
        'Fiş No': 'F002',
        'Tarih': '2024-01-15',
        'Saat': '15:45',
        'Kasiyer ID': '15 & 20',
        'Müşteri Ad Soyad': '',
        'Barkodlar': '06010003',
        'Toplam Tutar': 800,
        'İndirim': 0,
        'Alınacak Tutar': 800,
        'Açıklama': ''
      }
    ];
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Satışlar');
    worksheet['!cols'] = [
      { wch: 12 }, { wch: 14 }, { wch: 8 }, { wch: 15 }, { wch: 22 },
      { wch: 25 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 20 }
    ];
    XLSX.writeFile(workbook, 'satis_aktarim_sablonu.xlsx');
  };

  // Satış aktarımı - Excel yükle ve ön izleme
  const handleSalesExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            toast.error('Excel dosyasında veri bulunamadı');
            return;
          }

          // Başlık satırını al ve normalize et
          const headerRow = jsonData[0] || [];
          const headers = headerRow.map(h => h?.toString().toLowerCase().trim() || '');

          const getColIndex = (possibleNames) => {
            // Önce tam eşleşme dene (fiyat vs indirimli fiyat karışmasın)
            for (const name of possibleNames) {
              const idx = headers.findIndex(h => h === name);
              if (idx !== -1) return idx;
            }
            // Sonra kısmi eşleşme dene
            for (const name of possibleNames) {
              const idx = headers.findIndex(h => h.includes(name));
              if (idx !== -1) return idx;
            }
            return -1;
          };

          const colMap = {
            receipt_number: getColIndex(['fiş no', 'fis no', 'fiş', 'fis', 'receipt']),
            date: getColIndex(['tarih', 'date']),
            time: getColIndex(['saat', 'time']),
            cashier: getColIndex(['kasiyer id', 'kasiyer', 'cashier id', 'cashier', 'personel', 'staff', 'plasiyer']),
            customer_name: getColIndex(['müşteri', 'musteri', 'müşteri ad', 'customer']),
            barcodes: getColIndex(['barkod', 'barcode', 'barkodlar']),
            total_amount: getColIndex(['toplam tutar', 'toplam', 'total', 'fiyat']),
            discount: getColIndex(['indirim', 'iskonto', 'discount']),
            net_amount: getColIndex(['indirimli fiyat', 'indirimli', 'alınacak', 'alinacak', 'net', 'alınacak tutar']),
            description: getColIndex(['açıklama', 'aciklama', 'not', 'notes', 'description'])
          };

          // Fiş no ve barkod sütunu zorunlu
          if (colMap.receipt_number < 0) {
            toast.error('Excel dosyasında "Fiş No" sütunu bulunamadı');
            return;
          }
          if (colMap.barcodes < 0) {
            toast.error('Excel dosyasında "Barkodlar" sütunu bulunamadı');
            return;
          }

          const getStr = (row, colName) => colMap[colName] >= 0 ? (row[colMap[colName]]?.toString().trim() || '') : '';
          const getNum = (row, colName) => {
            if (colMap[colName] < 0) return null;
            const val = row[colMap[colName]];
            if (val === undefined || val === '' || val === null) return null;
            const num = parseFloat(val);
            return isNaN(num) ? null : num;
          };

          // Excel tarih değerini yyyy-mm-dd string'e dönüştür
          const formatDate = (val) => {
            if (!val) return '';
            if (typeof val === 'number') {
              // Excel serial date number → JS Date ile dönüştür
              try {
                const date = XLSX.SSF.parse_date_code(val);
                if (date) {
                  return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
                }
              } catch (e) {}
              // Fallback: manuel dönüşüm (Excel epoch: 30 Aralık 1899)
              const jsDate = new Date(Date.UTC(1899, 11, 30) + Math.floor(val) * 86400000);
              return `${jsDate.getUTCFullYear()}-${String(jsDate.getUTCMonth() + 1).padStart(2, '0')}-${String(jsDate.getUTCDate()).padStart(2, '0')}`;
            }
            const str = val.toString().trim();
            // yyyy-mm-dd zaten doğru format
            if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.substring(0, 10);
            // dd.mm.yyyy veya dd/mm/yyyy (4 haneli yıl)
            const match4 = str.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
            if (match4) {
              return `${match4[3]}-${match4[2].padStart(2, '0')}-${match4[1].padStart(2, '0')}`;
            }
            // MM.DD.YY veya DD.MM.YY (2 haneli yıl) — BarkodSis formatı
            const match2 = str.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2})$/);
            if (match2) {
              const a = parseInt(match2[1]);
              const b = parseInt(match2[2]);
              const yearShort = parseInt(match2[3]);
              const year = yearShort >= 50 ? 1900 + yearShort : 2000 + yearShort;
              // a > 12 ise gün olmalı → DD.MM.YY
              if (a > 12) return `${year}-${String(b).padStart(2, '0')}-${String(a).padStart(2, '0')}`;
              // b > 12 ise gün olmalı → MM.DD.YY
              if (b > 12) return `${year}-${String(a).padStart(2, '0')}-${String(b).padStart(2, '0')}`;
              // İkisi de ≤12 → MM.DD.YY varsay (BarkodSis standardı)
              return `${year}-${String(a).padStart(2, '0')}-${String(b).padStart(2, '0')}`;
            }
            return str;
          };

          const formatTime = (val) => {
            if (!val) return '';
            if (typeof val === 'number') {
              // Eğer >= 1 ise tam datetime serial — sadece kesir kısmını (saat) al
              const timeFraction = val >= 1 ? (val - Math.floor(val)) : val;
              const totalSeconds = Math.round(timeFraction * 86400);
              const h = Math.floor(totalSeconds / 3600) % 24;
              const m = Math.floor((totalSeconds % 3600) / 60);
              const s = totalSeconds % 60;
              return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
            }
            return val.toString().trim();
          };

          // Veri satırlarını parse et
          const rows = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;

            const receipt_number = getStr(row, 'receipt_number');
            const barcodes = getStr(row, 'barcodes');
            if (!receipt_number && !barcodes) continue;

            rows.push({
              receipt_number,
              date: formatDate(colMap.date >= 0 ? row[colMap.date] : ''),
              time: formatTime(colMap.time >= 0 ? row[colMap.time] : ''),
              cashier: getStr(row, 'cashier'),
              customer_name: getStr(row, 'customer_name'),
              barcodes,
              total_amount: getNum(row, 'total_amount'),
              discount: getNum(row, 'discount'),
              net_amount: getNum(row, 'net_amount'),
              description: getStr(row, 'description')
            });
          }

          if (rows.length === 0) {
            toast.error('İçe aktarılacak satış bulunamadı');
            return;
          }

          setSalesImportData(rows);

          // Backend'e ön izleme için gönder
          setImportingSales(true);
          const response = await fetch(`${API_URL}/sales/import-preview`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ rows, store_id: salesImportStore || null })
          });
          const result = await response.json();

          if (result.success) {
            setSalesImportPreview(result);
            setShowSalesImportPreview(true);
          } else {
            toast.error(result.error || 'Ön izleme hatası');
          }
        } catch (err) {
          console.error('Excel parse error:', err);
          toast.error('Excel dosyası okunamadı: ' + err.message);
        } finally {
          setImportingSales(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      toast.error('Dosya okunamadı');
    }

    // Input'u sıfırla (aynı dosya tekrar seçilebilsin)
    if (salesImportFileRef.current) {
      salesImportFileRef.current.value = '';
    }
  };

  // Satış aktarımı - Onayla ve kaydet
  const handleConfirmSalesImport = async () => {
    if (!salesImportPreview?.validRows || salesImportPreview.validRows.length === 0) {
      toast.error('Aktarılacak geçerli satış bulunamadı');
      return;
    }

    setImportingSales(true);
    try {
      const response = await fetch(`${API_URL}/sales/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sales: salesImportPreview.validRows,
          store_id: salesImportStore || null,
          user_name: user?.name || user?.email || 'Sistem'
        })
      });
      const result = await response.json();

      if (result.success) {
        setSalesImportResult(result);
        setShowSalesImportPreview(false);
        setSalesImportPreview(null);
        setSalesImportData(null);
        toast.success(`${result.imported} satış başarıyla aktarıldı!`);
        logService.log('settings', 'import', `Excel ile ${result.imported} satış aktarıldı`, {
          details: { imported: result.imported, errors: result.errors?.length || 0 }
        });
      } else {
        toast.error(result.error || 'Aktarım hatası');
      }
    } catch (err) {
      toast.error('Satış aktarımı sırasında hata: ' + err.message);
    } finally {
      setImportingSales(false);
    }
  };

  // İade aktarımı - Şablon indir
  const downloadReturnsImportTemplate = () => {
    const templateData = [
      {
        'Fiş No': 'I001',
        'Tarih': '2024-01-15',
        'Saat': '14:30',
        'Kasiyer ID': '20',
        'Müşteri Ad Soyad': 'Mehmet Yılmaz',
        'Barkodlar': '06010001,06010002',
        'Toplam Tutar': 1500,
        'İndirim': 100,
        'İade Tutarı': 1400,
        'Açıklama': 'Örnek iade'
      },
      {
        'Fiş No': 'I002',
        'Tarih': '2024-01-15',
        'Saat': '15:45',
        'Kasiyer ID': '15 & 20',
        'Müşteri Ad Soyad': '',
        'Barkodlar': '06010003',
        'Toplam Tutar': 800,
        'İndirim': 0,
        'İade Tutarı': 800,
        'Açıklama': ''
      }
    ];
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'İadeler');
    worksheet['!cols'] = [
      { wch: 12 }, { wch: 14 }, { wch: 8 }, { wch: 15 }, { wch: 22 },
      { wch: 25 }, { wch: 14 }, { wch: 10 }, { wch: 14 }, { wch: 20 }
    ];
    XLSX.writeFile(workbook, 'iade_aktarim_sablonu.xlsx');
  };

  // İade aktarımı - Excel yükle ve ön izleme
  const handleReturnsExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            toast.error('Excel dosyasında veri bulunamadı');
            return;
          }

          const headerRow = jsonData[0] || [];
          const headers = headerRow.map(h => h?.toString().toLowerCase().trim() || '');

          const getColIndex = (possibleNames) => {
            // Önce tam eşleşme dene (fiyat vs indirimli fiyat karışmasın)
            for (const name of possibleNames) {
              const idx = headers.findIndex(h => h === name);
              if (idx !== -1) return idx;
            }
            // Sonra kısmi eşleşme dene
            for (const name of possibleNames) {
              const idx = headers.findIndex(h => h.includes(name));
              if (idx !== -1) return idx;
            }
            return -1;
          };

          const colMap = {
            receipt_number: getColIndex(['fiş no', 'fis no', 'fiş', 'fis', 'receipt']),
            date: getColIndex(['tarih', 'date']),
            time: getColIndex(['saat', 'time']),
            cashier: getColIndex(['kasiyer id', 'kasiyer', 'cashier id', 'cashier', 'personel', 'staff', 'plasiyer']),
            customer_name: getColIndex(['müşteri', 'musteri', 'müşteri ad', 'customer']),
            barcodes: getColIndex(['barkod', 'barcode', 'barkodlar']),
            total_amount: getColIndex(['toplam tutar', 'toplam', 'total', 'fiyat']),
            discount: getColIndex(['indirim', 'iskonto', 'discount']),
            net_amount: getColIndex(['indirimli fiyat', 'indirimli', 'iade tutarı', 'iade tutar', 'alınacak', 'alinacak', 'net', 'alınacak tutar']),
            description: getColIndex(['açıklama', 'aciklama', 'not', 'notes', 'description'])
          };

          if (colMap.receipt_number < 0) {
            toast.error('Excel dosyasında "Fiş No" sütunu bulunamadı');
            return;
          }
          if (colMap.barcodes < 0) {
            toast.error('Excel dosyasında "Barkodlar" sütunu bulunamadı');
            return;
          }

          const getStr = (row, colName) => colMap[colName] >= 0 ? (row[colMap[colName]]?.toString().trim() || '') : '';
          const getNum = (row, colName) => {
            if (colMap[colName] < 0) return null;
            const val = row[colMap[colName]];
            if (val === undefined || val === '' || val === null) return null;
            const num = parseFloat(val);
            return isNaN(num) ? null : num;
          };

          const formatDate = (val) => {
            if (!val) return '';
            if (typeof val === 'number') {
              try {
                const date = XLSX.SSF.parse_date_code(val);
                if (date) {
                  return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
                }
              } catch (e) {}
              const jsDate = new Date(Date.UTC(1899, 11, 30) + Math.floor(val) * 86400000);
              return `${jsDate.getUTCFullYear()}-${String(jsDate.getUTCMonth() + 1).padStart(2, '0')}-${String(jsDate.getUTCDate()).padStart(2, '0')}`;
            }
            const str = val.toString().trim();
            if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.substring(0, 10);
            const match4 = str.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
            if (match4) {
              return `${match4[3]}-${match4[2].padStart(2, '0')}-${match4[1].padStart(2, '0')}`;
            }
            const match2 = str.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2})$/);
            if (match2) {
              const a = parseInt(match2[1]);
              const b = parseInt(match2[2]);
              const yearShort = parseInt(match2[3]);
              const year = yearShort >= 50 ? 1900 + yearShort : 2000 + yearShort;
              if (a > 12) return `${year}-${String(b).padStart(2, '0')}-${String(a).padStart(2, '0')}`;
              if (b > 12) return `${year}-${String(a).padStart(2, '0')}-${String(b).padStart(2, '0')}`;
              return `${year}-${String(a).padStart(2, '0')}-${String(b).padStart(2, '0')}`;
            }
            return str;
          };

          const formatTime = (val) => {
            if (!val) return '';
            if (typeof val === 'number') {
              const timeFraction = val >= 1 ? (val - Math.floor(val)) : val;
              const totalSeconds = Math.round(timeFraction * 86400);
              const h = Math.floor(totalSeconds / 3600) % 24;
              const m = Math.floor((totalSeconds % 3600) / 60);
              const s = totalSeconds % 60;
              return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
            }
            return val.toString().trim();
          };

          const rows = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;

            const receipt_number = getStr(row, 'receipt_number');
            const barcodes = getStr(row, 'barcodes');
            if (!receipt_number && !barcodes) continue;

            rows.push({
              receipt_number,
              date: formatDate(colMap.date >= 0 ? row[colMap.date] : ''),
              time: formatTime(colMap.time >= 0 ? row[colMap.time] : ''),
              cashier: getStr(row, 'cashier'),
              customer_name: getStr(row, 'customer_name'),
              barcodes,
              total_amount: getNum(row, 'total_amount'),
              discount: getNum(row, 'discount'),
              net_amount: getNum(row, 'net_amount'),
              description: getStr(row, 'description')
            });
          }

          if (rows.length === 0) {
            toast.error('İçe aktarılacak iade bulunamadı');
            return;
          }

          setReturnsImportData(rows);

          setImportingReturns(true);
          const response = await fetch(`${API_URL}/returns/import-preview`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ rows, store_id: returnsImportStore || null })
          });
          const result = await response.json();

          if (result.success) {
            setReturnsImportPreview(result);
            setShowReturnsImportPreview(true);
          } else {
            toast.error(result.error || 'Ön izleme hatası');
          }
        } catch (err) {
          console.error('Excel parse error:', err);
          toast.error('Excel dosyası okunamadı: ' + err.message);
        } finally {
          setImportingReturns(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      toast.error('Dosya okunamadı');
    }

    if (returnsImportFileRef.current) {
      returnsImportFileRef.current.value = '';
    }
  };

  // İade aktarımı - Onayla ve kaydet
  const handleConfirmReturnsImport = async () => {
    if (!returnsImportPreview?.validRows || returnsImportPreview.validRows.length === 0) {
      toast.error('Aktarılacak geçerli iade bulunamadı');
      return;
    }

    setImportingReturns(true);
    try {
      const response = await fetch(`${API_URL}/returns/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          returns: returnsImportPreview.validRows,
          store_id: returnsImportStore || null,
          user_name: user?.name || user?.email || 'Sistem'
        })
      });
      const result = await response.json();

      if (result.success) {
        setReturnsImportResult(result);
        setShowReturnsImportPreview(false);
        setReturnsImportPreview(null);
        setReturnsImportData(null);
        toast.success(`${result.imported} iade başarıyla aktarıldı!`);
        logService.log('settings', 'import', `Excel ile ${result.imported} iade aktarıldı`, {
          details: { imported: result.imported, errors: result.errors?.length || 0 }
        });
      } else {
        toast.error(result.error || 'Aktarım hatası');
      }
    } catch (err) {
      toast.error('İade aktarımı sırasında hata: ' + err.message);
    } finally {
      setImportingReturns(false);
    }
  };

  return (
    <div className="settings-container">
      <header className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/')}>
            <ArrowLeft size={18} /> Ana Sayfa
          </button>
          <h1>Ayarlar</h1>
        </div>
        <div className="header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <div className="header-buttons">
          <button className="action-header-btn log-btn" onClick={() => setShowLogModal(true)}>
            <ScrollText size={18} /> İşlem Geçmişi
          </button>
        </div>
      </header>

      <div className="settings-content">
        {/* Yardım Butonu */}
        <button className="help-btn-corner" title="Yardım" onClick={() => setShowHelpModal(true)}>?</button>

        {/* Sidebar */}
        <div className="settings-sidebar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => (tab.path ? navigate(tab.path) : setActiveTab(tab.id))}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-name">{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="settings-main">
          {activeTab === 'general' && (
            <div className="settings-panel" key="general-panel">
              <h2>Genel Ayarlar</h2>
              <div className="settings-group">
                <div className="setting-item">
                  <div className="setting-info">
                    <label>Ses Efektleri</label>
                    <p>Sayım ve işlem seslerini aç/kapat</p>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={soundEnabled}
                      onChange={(e) => handleSoundToggle(e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <label>Eski Etiket Barkod Okutma</label>
                    <p>Perakende satış sayfasında eski etiket barkod okutma alanını göster</p>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={eskiEtiketScanEnabled}
                      onChange={(e) => handleEskiEtiketScanToggle(e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <label>Tamirat Hatırlatma</label>
                    <p>Son teslim tarihine yaklaşan tamiratlar için bildirim al</p>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={repairReminderEnabled}
                      onChange={(e) => {
                        setRepairReminderEnabled(e.target.checked);
                        localStorage.setItem('repairReminderEnabled', e.target.checked.toString());
                      }}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                {repairReminderEnabled && (
                  <>
                    <div className="setting-item">
                      <div className="setting-info">
                        <label>Hatırlatma Süresi (Saat)</label>
                        <p>Son teslim tarihine kaç saat kala hatırlatma başlasın</p>
                      </div>
                      <select
                        value={repairReminderHours}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          setRepairReminderHours(val);
                          localStorage.setItem('repairReminderHours', val.toString());
                          toast.success(`Hatırlatma süresi ${val} saat olarak ayarlandı`);
                        }}
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                      >
                        <option value={6}>6 Saat</option>
                        <option value={12}>12 Saat</option>
                        <option value={24}>24 Saat (1 Gün)</option>
                        <option value={48}>48 Saat (2 Gün)</option>
                        <option value={72}>72 Saat (3 Gün)</option>
                      </select>
                    </div>
                    <div className="setting-item">
                      <div className="setting-info">
                        <label>Bildirim Sıklığı (Dakika)</label>
                        <p>Kaç dakikada bir bildirim gösterilsin</p>
                      </div>
                      <select
                        value={repairReminderInterval}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          setRepairReminderInterval(val);
                          localStorage.setItem('repairReminderIntervalMin', val.toString());
                          toast.success(`Bildirim sıklığı ${val} dakika olarak ayarlandı`);
                        }}
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                      >
                        <option value={1}>1 Dakika</option>
                        <option value={5}>5 Dakika</option>
                        <option value={10}>10 Dakika</option>
                        <option value={15}>15 Dakika</option>
                        <option value={30}>30 Dakika</option>
                        <option value={60}>60 Dakika (1 Saat)</option>
                      </select>
                    </div>
                  </>
                )}
                <div className="setting-item">
                  <div className="setting-info">
                    <label>Kod Olmadan Tamirat Teslim</label>
                    <p>Fiş kodu kaybolduğunda yönetici şifresiyle teslim etmeyi aktifleştir</p>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={repairOverrideEnabled}
                      onChange={(e) => saveRepairOverrideEnabled(e.target.checked)}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                {repairOverrideEnabled && (
                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Teslim Şifresi</label>
                      <p>Fiş kodu yerine kullanılacak yönetici şifresini belirleyin (tüm cihazlarda ortak)</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="password"
                        value={repairOverridePassword}
                        onChange={(e) => setRepairOverridePassword(e.target.value)}
                        placeholder={repairOverrideHasPassword ? 'Yeni şifre belirle...' : 'Şifre girin...'}
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '160px' }}
                      />
                      <button
                        onClick={saveRepairOverridePassword}
                        style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', background: '#10b981', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}
                      >
                        {repairOverrideHasPassword ? 'Yenile' : 'Kaydet'}
                      </button>
                    </div>
                  </div>
                )}
                {isAdmin && (
                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Log Saklama Süresi</label>
                      <p>Sistem loglarının otomatik silinme sıklığını belirleyin</p>
                    </div>
                    <select
                      value={logRetentionDays}
                      onChange={(e) => saveLogRetention(e.target.value)}
                      disabled={savingLogRetention}
                      style={{ padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                    >
                      <option value={3}>3 Gün</option>
                      <option value={7}>Haftalık (7 Gün)</option>
                      <option value={14}>2 Haftalık (14 Gün)</option>
                      <option value={30}>Aylık (30 Gün)</option>
                      <option value={90}>3 Aylık (90 Gün)</option>
                      <option value={180}>6 Aylık (180 Gün)</option>
                      <option value={365}>Yıllık (365 Gün)</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Kasiyer İndirim Limitleri - Sadece Admin */}
              {isAdmin && (
                <div className="settings-group" style={{ marginTop: '24px' }}>
                  <div
                    onClick={() => setIsCashierLimitsOpen(!isCashierLimitsOpen)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      cursor: 'pointer', padding: '20px', userSelect: 'none',
                      background: '#ffffff', border: '1px solid #e0f2f1',
                      borderRadius: '12px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}
                  >
                    <div>
                      <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Percent size={20} /> Kasiyer İndirim Limitleri
                      </h3>
                      <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 0' }}>
                        Kasiyerlerin maksimum indirim yüzdelerini belirleyin
                      </p>
                    </div>
                    {isCashierLimitsOpen ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
                  </div>
                  {isCashierLimitsOpen && (
                    <>
                      <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px', marginTop: '16px' }}>
                        Her kasiyerin uygulayabileceği maksimum manuel indirim yüzdesini belirleyin. Boş bırakılan kasiyerlerde limit uygulanmaz. Admin kullanıcılar limitle sınırlı değildir.
                      </p>
                      {cashierDiscountLimits.length === 0 ? (
                        <p style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>Kayıtlı satış sorumlusu bulunamadı.</p>
                      ) : (
                        cashierDiscountLimits.map(cashier => (
                          <div className="setting-item" key={cashier.id}>
                            <div className="setting-info">
                              <label>{cashier.name}</label>
                              <p>Maks. indirim / hurda yüzdelik limiti</p>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  placeholder="Limit yok"
                                  value={cashier.max_discount_percent}
                                  onChange={(e) => setCashierDiscountLimits(prev => prev.map(c => c.id === cashier.id ? { ...c, max_discount_percent: e.target.value } : c))}
                                  style={{ width: '100px', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0', textAlign: 'center' }}
                                  title="Maks. indirim yüzdesi"
                                />
                                <span style={{ color: '#64748b', fontSize: '12px' }}>% indirim</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  placeholder="Limit yok"
                                  value={cashier.max_scrap_bonus_percent}
                                  onChange={(e) => setCashierDiscountLimits(prev => prev.map(c => c.id === cashier.id ? { ...c, max_scrap_bonus_percent: e.target.value } : c))}
                                  style={{ width: '100px', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0', textAlign: 'center' }}
                                  title="Maks. hurda yüzdelik limiti"
                                />
                                <span style={{ color: '#64748b', fontSize: '12px' }}>% hurda</span>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                      <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          onClick={saveMaxDiscountSettings}
                          disabled={savingMaxDiscount || cashierDiscountLimits.length === 0}
                          style={{
                            padding: '8px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#3b82f6',
                            color: '#fff',
                            fontWeight: 600,
                            cursor: (savingMaxDiscount || cashierDiscountLimits.length === 0) ? 'not-allowed' : 'pointer',
                            opacity: (savingMaxDiscount || cashierDiscountLimits.length === 0) ? 0.6 : 1
                          }}
                        >
                          {savingMaxDiscount ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'security' && (
            <div className="settings-panel" key="security-panel">
              <h2><Shield size={24} /> Güvenlik Ayarları</h2>

              {/* İki Faktörlü Doğrulama */}
              <div className="security-section">
                <h3><Key size={20} /> İki Faktörlü Doğrulama (2FA)</h3>
                <p className="security-description">
                  Hesabınızı daha güvenli hale getirmek için iki faktörlü doğrulamayı etkinleştirin.
                  Microsoft Authenticator, Google Authenticator veya benzeri uygulamalar kullanabilirsiniz.
                </p>

                {loading2FA ? (
                  <div className="loading-2fa">Yükleniyor...</div>
                ) : twoFactorEnabled ? (
                  <div className="two-factor-enabled">
                    <div className="twofa-status-badge enabled">
                      <CheckCircle size={20} />
                      <span>İki Faktörlü Doğrulama Aktif</span>
                    </div>

                    {!showDisable2FA ? (
                      <button
                        className="btn btn-danger"
                        onClick={() => setShowDisable2FA(true)}
                      >
                        <Lock size={16} /> 2FA'yı Devre Dışı Bırak
                      </button>
                    ) : (
                      <div className="disable-2fa-form">
                        <h4>2FA'yı Devre Dışı Bırak</h4>
                        <p>Güvenliğiniz için şifrenizi ve doğrulama kodunuzu girin.</p>
                        <div className="form-group">
                          <label>Şifreniz</label>
                          <input
                            type="password"
                            value={disablePassword}
                            onChange={(e) => setDisablePassword(e.target.value)}
                            placeholder="Şifrenizi girin"
                          />
                        </div>
                        <div className="form-group">
                          <label>Doğrulama Kodu</label>
                          <input
                            type="text"
                            maxLength={6}
                            value={disableCode}
                            onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                            placeholder="6 haneli kod"
                          />
                        </div>
                        <div className="form-actions">
                          <button
                            className="btn btn-danger"
                            onClick={disable2FA}
                            disabled={setting2FA}
                          >
                            {setting2FA ? 'İşleniyor...' : 'Devre Dışı Bırak'}
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setShowDisable2FA(false);
                              setDisablePassword('');
                              setDisableCode('');
                            }}
                          >
                            İptal
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : qrCode ? (
                  <div className="two-factor-setup">
                    <div className="setup-steps">
                      <div className="step">
                        <span className="step-number">1</span>
                        <span>Microsoft Authenticator veya Google Authenticator uygulamasını açın</span>
                      </div>
                      <div className="step">
                        <span className="step-number">2</span>
                        <span>QR kodunu tarayın veya anahtarı manuel girin</span>
                      </div>
                      <div className="step">
                        <span className="step-number">3</span>
                        <span>Uygulamadaki 6 haneli kodu aşağıya girin</span>
                      </div>
                    </div>

                    <div className="qr-code-container">
                      <img src={qrCode} alt="2FA QR Code" className="qr-code" />
                      <div className="manual-key">
                        <label>Manuel Anahtar:</label>
                        <code>{secret}</code>
                      </div>
                    </div>

                    <div className="verify-code-form">
                      <div className="form-group">
                        <label>Doğrulama Kodu</label>
                        <input
                          type="text"
                          maxLength={6}
                          value={verifyCode}
                          onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                          placeholder="6 haneli kod"
                          className="verify-input"
                        />
                      </div>
                      <div className="form-actions">
                        <button
                          className="btn btn-primary"
                          onClick={verify2FA}
                          disabled={setting2FA || verifyCode.length !== 6}
                        >
                          {setting2FA ? 'Doğrulanıyor...' : 'Doğrula ve Etkinleştir'}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={cancel2FASetup}
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="two-factor-disabled">
                    <div className="twofa-status-badge disabled">
                      <Unlock size={20} />
                      <span>İki Faktörlü Doğrulama Kapalı</span>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={setup2FA}
                      disabled={setting2FA}
                    >
                      {setting2FA ? 'Hazırlanıyor...' : <><Shield size={16} /> 2FA Kurulumunu Başlat</>}
                    </button>
                  </div>
                )}
              </div>

            </div>
          )}

          {activeTab === 'compliance' && isAdmin && (
            <div className="settings-panel compliance-panel" key="compliance-panel">
              <div className="panel-header">
                <h2><ClipboardCheck size={24} /> Güvenlik ve Uyumluluk</h2>
                <button
                  className="add-user-btn"
                  onClick={() => setShowAddComplianceModal(true)}
                >
                  <Plus size={16} /> Yeni Öğe Ekle
                </button>
              </div>

              <p className="compliance-intro">
                Şirketinizin yasal düzenlemelere, sektör standartlarına ve iç politikalara uyumluluğunu bu bölümden yönetin.
              </p>

              {loadingCompliance ? (
                <div className="loading-compliance">Yükleniyor...</div>
              ) : (
                <div className="compliance-categories">
                  {complianceCategories.map(category => {
                    const items = getItemsByCategory(category.id);
                    const completedCount = items.filter(i => i.status === 'completed').length;
                    const totalCount = items.length;

                    return (
                      <div
                        key={category.id}
                        className={`compliance-category-card ${selectedComplianceCategory === category.id ? 'expanded' : ''}`}
                        style={{ borderLeftColor: category.color }}
                      >
                        <div
                          className="category-header"
                          onClick={() => setSelectedComplianceCategory(
                            selectedComplianceCategory === category.id ? null : category.id
                          )}
                        >
                          <div className="category-icon" style={{ backgroundColor: category.color + '20', color: category.color }}>
                            {category.icon}
                          </div>
                          <div className="category-info">
                            <h3>{category.name}</h3>
                            <p>{category.description}</p>
                          </div>
                          <div className="category-stats">
                            {totalCount > 0 ? (
                              <span className="stat-badge" style={{ backgroundColor: completedCount === totalCount ? '#10b981' : '#f59e0b' }}>
                                {completedCount}/{totalCount}
                              </span>
                            ) : (
                              <span className="stat-badge empty">0</span>
                            )}
                            <ChevronRight
                              size={20}
                              className={`expand-icon ${selectedComplianceCategory === category.id ? 'rotated' : ''}`}
                            />
                          </div>
                        </div>

                        {selectedComplianceCategory === category.id && (
                          <div className="category-items">
                            {items.length === 0 ? (
                              <div className="no-items">
                                <AlertCircle size={16} />
                                <span>Bu kategoride henüz öğe yok</span>
                                <button
                                  className="add-item-btn"
                                  onClick={() => {
                                    setNewComplianceItem({ ...newComplianceItem, category: category.id });
                                    setShowAddComplianceModal(true);
                                  }}
                                >
                                  <Plus size={14} /> Ekle
                                </button>
                              </div>
                            ) : (
                              items.map(item => (
                                <div key={item.id} className={`compliance-item ${item.status}`}>
                                  <div className="item-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={item.status === 'completed'}
                                      onChange={(e) => updateComplianceStatus(item.id, e.target.checked ? 'completed' : 'pending')}
                                    />
                                  </div>
                                  <div className="item-content">
                                    <h4>{item.title}</h4>
                                    {item.description && <p>{item.description}</p>}
                                  </div>
                                  <div className="item-actions">
                                    <select
                                      value={item.status}
                                      onChange={(e) => updateComplianceStatus(item.id, e.target.value)}
                                      className={`status-select ${item.status}`}
                                    >
                                      <option value="pending">Beklemede</option>
                                      <option value="in_progress">Devam Ediyor</option>
                                      <option value="completed">Tamamlandı</option>
                                      <option value="not_applicable">Uygulanmaz</option>
                                    </select>
                                    <button
                                      className="delete-item-btn"
                                      onClick={() => deleteComplianceItem(item.id)}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Eksik bir şey var mı? kartı */}
                  <div
                    className="compliance-category-card suggest-card"
                    onClick={() => {
                      setNewComplianceItem({ title: '', description: '', category: 'other', status: 'pending' });
                      setShowAddComplianceModal(true);
                    }}
                  >
                    <div className="category-header">
                      <div className="category-icon" style={{ backgroundColor: '#64748b20', color: '#64748b' }}>
                        <HelpCircle size={20} />
                      </div>
                      <div className="category-info">
                        <h3>Eksik Bir Şey Mi Var?</h3>
                        <p>Listede olmayan bir uyumluluk gereksinimi eklemek için tıklayın</p>
                      </div>
                      <div className="category-stats">
                        <Plus size={20} style={{ color: '#64748b' }} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Yeni Uyumluluk Öğesi Modal */}
              {showAddComplianceModal && (
                <div className="modal-overlay">
                  <div className="modal compliance-modal" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>Yeni Uyumluluk Öğesi Ekle</h3>
                      <button className="close-btn" onClick={() => setShowAddComplianceModal(false)}>&times;</button>
                    </div>
                    <div className="modal-body">
                      <div className="form-group">
                        <label>Kategori *</label>
                        <select
                          value={newComplianceItem.category}
                          onChange={(e) => setNewComplianceItem({ ...newComplianceItem, category: e.target.value })}
                        >
                          <option value="">Kategori Seçin</option>
                          {complianceCategories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                          <option value="other">Diğer</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Başlık *</label>
                        <input
                          type="text"
                          value={newComplianceItem.title}
                          onChange={(e) => setNewComplianceItem({ ...newComplianceItem, title: e.target.value })}
                          placeholder="Uyumluluk gereksinimi başlığı"
                        />
                      </div>
                      <div className="form-group">
                        <label>Açıklama</label>
                        <textarea
                          value={newComplianceItem.description}
                          onChange={(e) => setNewComplianceItem({ ...newComplianceItem, description: e.target.value })}
                          placeholder="Detaylı açıklama (opsiyonel)"
                          rows={3}
                        />
                      </div>
                      <div className="form-group">
                        <label>Durum</label>
                        <select
                          value={newComplianceItem.status}
                          onChange={(e) => setNewComplianceItem({ ...newComplianceItem, status: e.target.value })}
                        >
                          <option value="pending">Beklemede</option>
                          <option value="in_progress">Devam Ediyor</option>
                          <option value="completed">Tamamlandı</option>
                          <option value="not_applicable">Uygulanmaz</option>
                        </select>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-secondary" onClick={() => setShowAddComplianceModal(false)}>
                        İptal
                      </button>
                      <button className="btn btn-primary" onClick={addComplianceItem}>
                        <Plus size={16} /> Ekle
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'printer' && (
            <div className="settings-panel" key="printer-panel">
              <h2>Yazıcı Ayarları</h2>

              {/* Önizlemesiz Yazdırma */}
              <div className="settings-group">
                <h3 style={{ marginBottom: '15px', color: '#666', fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                  <Printer size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Yazdırma Modu
                </h3>
                <div className="setting-item">
                  <div className="setting-info">
                    <label>Önizlemesiz Yazdırma</label>
                    <p>Açık olduğunda fişler önizleme ekranı olmadan doğrudan yazıcıya gönderilir</p>
                  </div>
                  <div
                    onClick={() => {
                      const newVal = !directPrintEnabled;
                      setDirectPrintEnabled(newVal);
                      localStorage.setItem('directPrintEnabled', newVal);
                      toast.success(newVal ? 'Önizlemesiz yazdırma açıldı' : 'Önizlemesiz yazdırma kapatıldı');
                    }}
                    style={{
                      width: '52px', height: '28px', borderRadius: '28px', cursor: 'pointer',
                      background: directPrintEnabled ? 'linear-gradient(135deg, #10b981, #059669)' : '#94a3b8',
                      position: 'relative', transition: 'background 0.3s', flexShrink: 0
                    }}
                  >
                    <div style={{
                      width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
                      position: 'absolute', top: '3px', left: directPrintEnabled ? '27px' : '3px',
                      transition: 'left 0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                  </div>
                </div>
              </div>

              {/* Yazıcı Seçimi */}
              <div className="settings-group" style={{ marginTop: '30px' }}>
                <h3 style={{ marginBottom: '15px', color: '#666', fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>
                    <Printer size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Yazıcı Seçimi
                  </span>
                  <button
                    onClick={fetchPrinters}
                    disabled={loadingPrinters}
                    style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f8fafc', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: '#475569' }}
                  >
                    <RefreshCw size={12} className={loadingPrinters ? 'spinning' : ''} /> Yenile
                  </button>
                </h3>

                {loadingPrinters ? (
                  <p style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>Yazıcılar yükleniyor...</p>
                ) : printerList.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', background: '#fefce8', borderRadius: '10px', border: '1px solid #fef08a' }}>
                    <p style={{ fontSize: '13px', color: '#854d0e', marginBottom: '8px', fontWeight: 500 }}>Sisteme bağlı yazıcı bulunamadı</p>
                    <p style={{ fontSize: '12px', color: '#a16207' }}>Bilgisayarınıza yazıcı eklemek için: Sistem Ayarları → Yazıcılar ve Tarayıcılar</p>
                  </div>
                ) : (
                  <>
                    <div className="setting-item">
                      <div className="setting-info">
                        <label>Fiş Yazıcısı</label>
                        <p>Satış fişleri, raporlar ve tahsilat fişleri için</p>
                      </div>
                      <select
                        value={receiptPrinter}
                        onChange={(e) => {
                          setReceiptPrinter(e.target.value);
                          localStorage.setItem('receiptPrinter', e.target.value);
                          if (e.target.value) toast.success(`Fiş yazıcısı: ${e.target.value}`);
                        }}
                        style={{ width: '240px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px' }}
                      >
                        <option value="">Seçiniz...</option>
                        {printerList.map((printer, idx) => (
                          <option key={idx} value={printer.name}>{printer.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="setting-item">
                      <div className="setting-info">
                        <label>Etiket Yazıcısı</label>
                        <p>Ürün etiketleri için</p>
                      </div>
                      <select
                        value={labelPrinter}
                        onChange={(e) => {
                          setLabelPrinter(e.target.value);
                          localStorage.setItem('labelPrinter', e.target.value);
                          if (e.target.value) toast.success(`Etiket yazıcısı: ${e.target.value}`);
                        }}
                        style={{ width: '240px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px' }}
                      >
                        <option value="">Seçiniz...</option>
                        {printerList.map((printer, idx) => (
                          <option key={idx} value={printer.name}>{printer.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="setting-item">
                      <div className="setting-info">
                        <label>Tamirat Yazıcısı</label>
                        <p>Tamirat fişleri için ikinci yazıcı (sadece "Yazıcı Sunucusu" cihazı yazdırır)</p>
                      </div>
                      <select
                        value={repairPrinter}
                        onChange={(e) => {
                          setRepairPrinter(e.target.value);
                          localStorage.setItem('repairPrinter', e.target.value);
                          if (e.target.value) toast.success(`Tamirat yazıcısı: ${e.target.value}`);
                        }}
                        style={{ width: '240px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px' }}
                      >
                        <option value="">Seçiniz...</option>
                        {printerList.map((printer, idx) => (
                          <option key={idx} value={printer.name}>{printer.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="setting-item">
                      <div className="setting-info">
                        <label>Bu Cihaz Tamirat Yazıcı Sunucusu</label>
                        <p>Açıkken: tamirat fişi başka cihazdan tetiklendiğinde bu cihaz (yazıcıya bağlı olan) yazdırır</p>
                      </div>
                      <div
                        onClick={() => {
                          const newVal = !isRepairPrintHost;
                          setIsRepairPrintHost(newVal);
                          localStorage.setItem('isRepairPrintHost', newVal.toString());
                          toast.success(newVal ? 'Bu cihaz tamirat yazıcı sunucusu olarak işaretlendi' : 'Tamirat yazıcı sunucusu kapatıldı');
                        }}
                        style={{
                          width: '52px', height: '28px', borderRadius: '28px', cursor: 'pointer',
                          background: isRepairPrintHost ? 'linear-gradient(135deg, #10b981, #059669)' : '#94a3b8',
                          position: 'relative', transition: 'background 0.3s', flexShrink: 0
                        }}
                      >
                        <div style={{
                          width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
                          position: 'absolute', top: '3px', left: isRepairPrintHost ? '27px' : '3px',
                          transition: 'left 0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                    </div>

                    {/* Bağlı yazıcılar listesi */}
                    <div style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                      <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', fontWeight: 600 }}>Bağlı Yazıcılar ({printerList.length})</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {printerList.map((printer, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', fontSize: '12px' }}>
                            <Printer size={12} color={printer.enabled ? '#10b981' : '#94a3b8'} />
                            <span style={{ color: '#334155', flex: 1 }}>{printer.name}</span>
                            {receiptPrinter === printer.name && <span style={{ fontSize: '10px', color: '#059669', background: '#ecfdf5', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>Fiş</span>}
                            {labelPrinter === printer.name && <span style={{ fontSize: '10px', color: '#7c3aed', background: '#f5f3ff', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>Etiket</span>}
                            {repairPrinter === printer.name && <span style={{ fontSize: '10px', color: '#0369a1', background: '#eff6ff', padding: '1px 6px', borderRadius: '4px', fontWeight: 600 }}>Tamirat</span>}
                            <span style={{ fontSize: '10px', color: printer.enabled ? '#10b981' : '#ef4444' }}>{printer.enabled ? 'Hazır' : 'Çevrimdışı'}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="setting-item" style={{ marginTop: '12px' }}>
                      <div className="setting-info">
                        <label>Test Yazdır</label>
                        <p>Seçili yazıcıyla test sayfası yazdır</p>
                      </div>
                      <button className="test-btn" onClick={() => {
                        const testHtml = `
                          <html><head><title>Yazıcı Test</title>
                          <style>body{font-family:Arial;text-align:center;padding:20px;}h2{margin-bottom:10px;}</style></head>
                          <body><h2>Yazıcı Test Sayfası</h2><p>Fiş Yazıcısı: ${receiptPrinter || 'Seçilmedi'}</p><p>Etiket Yazıcısı: ${labelPrinter || 'Seçilmedi'}</p><p>Önizlemesiz Mod: ${directPrintEnabled ? 'Açık' : 'Kapalı'}</p><hr/><p style="font-size:11px;">Bu bir test sayfasıdır.</p>
                          <script>window.onload=function(){var dp=localStorage.getItem('directPrintEnabled')==='true';window.print();if(dp){setTimeout(function(){window.close();},500);}}<\/script></body></html>
                        `;
                        handlePrint(testHtml, 'receipt');
                      }}><Printer size={16} /> Test Et</button>
                    </div>
                  </>
                )}
              </div>

              {/* Bluetooth Fiş Yazıcısı (Atölye / Tamirat) */}
              <div className="settings-group" style={{ marginTop: '30px' }}>
                <h3 style={{ marginBottom: '15px', color: '#666', fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>
                    <Printer size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Bluetooth Fiş Yazıcısı (Atölye / Tamirat)
                  </span>
                  {btPrinterInfo.name && (
                    <span style={{ fontSize: '11px', fontWeight: 600, color: btPrinterInfo.enabled ? '#059669' : '#94a3b8', background: btPrinterInfo.enabled ? '#ecfdf5' : '#f1f5f9', padding: '2px 8px', borderRadius: '6px' }}>
                      {btPrinterInfo.enabled ? 'Aktif' : 'Pasif'}
                    </span>
                  )}
                </h3>

                {!isBluetoothSupported() ? (
                  <div style={{ padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px' }}>
                    <p style={{ fontSize: '13px', color: '#991b1b', fontWeight: 500 }}>
                      Bu tarayıcı Web Bluetooth desteklemiyor. Mac üzerinde Chrome veya Edge kullanın (https:// veya localhost zorunlu).
                    </p>
                  </div>
                ) : (
                  <>
                    <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '14px', lineHeight: 1.5 }}>
                      HPRT HM-A300E gibi taşınabilir Bluetooth fiş yazıcısını eşleştirin. Bu yazıcı sadece <b>Tamirat sayfasındaki fiş yazdırma</b> için kullanılacak; sistem yazıcılarınız etkilenmez.
                    </p>

                    <div className="setting-item">
                      <div className="setting-info">
                        <label>Eşleştirilmiş Cihaz</label>
                        <p>{btPrinterInfo.name ? btPrinterInfo.name : 'Henüz eşleştirilmedi'}</p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={async () => {
                            setBtPairing(true);
                            try {
                              const info = await pairBluetoothPrinter();
                              setBtPrinterInfo(getBluetoothPrinterInfo());
                              toast.success(`Eşleştirildi: ${info.name}`);
                            } catch (e) {
                              if (e?.name !== 'NotFoundError') {
                                toast.error(e?.message || 'Eşleştirme başarısız');
                              }
                            } finally {
                              setBtPairing(false);
                            }
                          }}
                          disabled={btPairing}
                          style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: btPairing ? 'not-allowed' : 'pointer', opacity: btPairing ? 0.6 : 1 }}
                        >
                          {btPairing ? 'Eşleştiriliyor...' : (btPrinterInfo.name ? 'Yeniden Eşleştir' : 'Eşleştir')}
                        </button>
                        {btPrinterInfo.name && (
                          <button
                            onClick={() => {
                              clearBluetoothPrinter();
                              setBtPrinterInfo(getBluetoothPrinterInfo());
                              toast.info('Bluetooth yazıcı kaldırıldı');
                            }}
                            style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #ef4444', background: '#fff', color: '#ef4444', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                          >
                            Kaldır
                          </button>
                        )}
                      </div>
                    </div>

                    {btPrinterInfo.name && (
                      <>
                        <div className="setting-item">
                          <div className="setting-info">
                            <label>Bluetooth Yazıcıyı Aktif Et</label>
                            <p>Açık olduğunda Tamirat sayfasında "BT ile Bas" butonu görünür</p>
                          </div>
                          <div
                            onClick={() => {
                              const newVal = !btPrinterInfo.enabled;
                              setBluetoothPrinterEnabled(newVal);
                              setBtPrinterInfo(getBluetoothPrinterInfo());
                              toast.success(newVal ? 'Bluetooth yazıcı aktif' : 'Bluetooth yazıcı pasif');
                            }}
                            style={{
                              width: '52px', height: '28px', borderRadius: '28px', cursor: 'pointer',
                              background: btPrinterInfo.enabled ? 'linear-gradient(135deg, #10b981, #059669)' : '#94a3b8',
                              position: 'relative', transition: 'background 0.3s', flexShrink: 0
                            }}
                          >
                            <div style={{
                              width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
                              position: 'absolute', top: '3px', left: btPrinterInfo.enabled ? '27px' : '3px',
                              transition: 'left 0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }} />
                          </div>
                        </div>

                        <div className="setting-item">
                          <div className="setting-info">
                            <label>Test Yazdır</label>
                            <p>Bluetooth yazıcıdan kısa bir test fişi gönderir</p>
                          </div>
                          <button
                            className="test-btn"
                            disabled={btTesting}
                            onClick={async () => {
                              setBtTesting(true);
                              try {
                                await printRawToBluetooth(buildTestReceiptBytes());
                                toast.success('Test fişi gönderildi');
                              } catch (e) {
                                toast.error(e?.message || 'Bluetooth yazdırma hatası');
                              } finally {
                                setBtTesting(false);
                              }
                            }}
                          >
                            <Printer size={16} /> {btTesting ? 'Gönderiliyor...' : 'Test Et'}
                          </button>
                        </div>

                        <div style={{ marginTop: '8px', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px', color: '#64748b', lineHeight: 1.5 }}>
                          <b>İpucu:</b> Tarayıcı yenilendiğinde bağlantı kopabilir. "Bluetooth yazıcı bağlantısı kayboldu" hatası alırsanız bu sayfadan tekrar Eşleştir'e basın.
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Etiket Yazıcısı - Dizayn Sayfasına Yönlendirme */}
              <div className="settings-group" style={{ marginTop: '30px' }}>
                <h3 style={{ marginBottom: '15px', color: '#666', fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                  <Tag size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Kuyumcu Etiket Yazıcısı
                </h3>

                <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px', lineHeight: 1.6 }}>
                  Etiket tasarımınızı sürükle & bırak editörü ile oluşturun. Altın ve gümüş için ayrı dizaynlar tanımlayabilirsiniz.
                </p>

                <button
                  onClick={() => navigate('/label-design')}
                  style={{
                    width: '100%',
                    padding: '16px 24px',
                    background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
                    color: 'white',
                    border: '2px solid #3b82f6',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #16213e, #1a1a2e)';
                    e.currentTarget.style.borderColor = '#60a5fa';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(59,130,246,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #1a1a2e, #16213e)';
                    e.currentTarget.style.borderColor = '#3b82f6';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Tag size={20} /> Etiket Dizaynı
                  <ChevronRight size={18} />
                </button>

                {/* Mini preview of current design */}
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}>
                  <p style={{ fontSize: '12px', color: '#999', marginBottom: '10px' }}>Mevcut Etiket Önizleme</p>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '10px',
                    background: '#fff',
                    borderRadius: '6px',
                    border: '1px solid #eee',
                  }}>
                    <div style={{
                      width: '210px',
                      height: '27px',
                      background: 'white',
                      border: '1px solid #999',
                      display: 'flex',
                      flexDirection: 'row',
                      boxShadow: '1px 1px 3px rgba(0,0,0,0.1)',
                    }}>
                      <div style={{
                        width: '60px',
                        height: '100%',
                        borderRight: '1px dashed #ccc',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '1px',
                        fontSize: '5px',
                      }}>
                        <div style={{ fontWeight: 'bold', textAlign: 'center' }}>Akik Gold Jewellery</div>
                        <div style={{
                          height: '8px',
                          background: 'repeating-linear-gradient(90deg, #000, #000 1px, #fff 1px, #fff 2px)',
                          margin: '0 2px',
                        }}></div>
                        <div style={{ fontSize: '4px', textAlign: 'center', fontFamily: 'monospace' }}>01010163</div>
                      </div>
                      <div style={{ width: '9px', height: '100%', background: '#f5f0e0', borderRight: '1px dashed #ccc' }}></div>
                      <div style={{
                        width: '48px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '1px',
                        fontSize: '5px',
                        lineHeight: '1.3',
                        borderRight: '1px dashed #ccc',
                      }}>
                        <div style={{ fontWeight: 'bold' }}>4.01 - 14</div>
                        <div style={{ fontWeight: 'bold' }}>12.03 - HAS</div>
                        <div style={{ fontWeight: 'bold' }}>Küpe</div>
                      </div>
                      <div style={{
                        flex: 1,
                        background: '#f8f8f8',
                        borderRadius: '0 15px 15px 0',
                      }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && isAdmin && (
            <div className="settings-panel" key="users-panel">
              <div className="panel-header">
                <h2>Kullanıcı Yönetimi</h2>
                <button className="add-user-btn" onClick={openNewUserModal}>+ Yeni Kullanıcı</button>
              </div>

              {/* Kullanıcı Rol Filtreleri */}
              <div className="user-filters">
                <button
                  className={`user-filter-btn ${userRoleFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setUserRoleFilter('all')}
                >
                  Tümü
                  <span className="filter-count">{users.length}</span>
                </button>
                <button
                  className={`user-filter-btn admin ${userRoleFilter === 'admin' ? 'active' : ''}`}
                  onClick={() => setUserRoleFilter('admin')}
                >
                  Yönetici
                  <span className="filter-count">{users.filter(u => u.role === 'admin').length}</span>
                </button>
                <button
                  className={`user-filter-btn cashier ${userRoleFilter === 'cashier' ? 'active' : ''}`}
                  onClick={() => setUserRoleFilter('cashier')}
                >
                  Kasiyer
                  <span className="filter-count">{users.filter(u => u.role === 'cashier').length}</span>
                </button>
                <button
                  className={`user-filter-btn terminal ${userRoleFilter === 'terminal' ? 'active' : ''}`}
                  onClick={() => setUserRoleFilter('terminal')}
                >
                  El Terminali
                  <span className="filter-count">{users.filter(u => u.role === 'terminal').length}</span>
                </button>
                <button
                  className={`user-filter-btn atolye ${userRoleFilter === 'atolye' ? 'active' : ''}`}
                  onClick={() => setUserRoleFilter('atolye')}
                >
                  Atölye
                  <span className="filter-count">{users.filter(u => u.role === 'atolye').length}</span>
                </button>
                <button
                  className={`user-filter-btn user ${userRoleFilter === 'user' ? 'active' : ''}`}
                  onClick={() => setUserRoleFilter('user')}
                >
                  Kullanıcı
                  <span className="filter-count">{users.filter(u => u.role === 'user').length}</span>
                </button>
              </div>

              <div className="users-list">
                {loadingUsers ? (
                  <div className="loading-state">Yükleniyor...</div>
                ) : users.filter(u => userRoleFilter === 'all' || u.role === userRoleFilter).length === 0 ? (
                  <div className="empty-state">
                    {userRoleFilter === 'all' ? 'Henüz kullanıcı yok' : 'Bu rolde kullanıcı yok'}
                  </div>
                ) : (
                  users.filter(u => userRoleFilter === 'all' || u.role === userRoleFilter).map(user => (
                    <div key={user.id} className={`user-item ${!user.active ? 'inactive' : ''}`}>
                      <div className="user-avatar" style={{ background: user.role === 'admin' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : user.role === 'cashier' ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : user.role === 'terminal' ? 'linear-gradient(135deg, #10b981, #059669)' : user.role === 'atolye' ? 'linear-gradient(135deg, #ec4899, #db2777)' : 'linear-gradient(135deg, #00bfa6, #009688)' }}>
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div className="user-info">
                        <span className="user-name">{user.name}</span>
                        <span className="user-email">{user.email}</span>
                        <span className={`user-role ${user.role}`}>
                          {user.role === 'admin' ? 'Yönetici' : user.role === 'cashier' ? 'Kasiyer' : user.role === 'terminal' ? 'El Terminali' : user.role === 'atolye' ? 'Atölye' : user.role === 'depo' ? 'Depo Sorumlusu' : 'Kullanıcı'}
                        </span>
                        {(user.role === 'cashier' || user.role === 'terminal' || user.role === 'depo') && user.store_name && (
                          <span className="user-store">{user.store_name}</span>
                        )}
                        {user.role === 'terminal' && user.default_sales_staff_id && (
                          // İsim, terminalin kullandığı listeden (employees) çözülür; API'nin döndürdüğü ad eski sales_staff tablosundan geliyordu
                          <span className="user-staff"><User size={14} /> {salesStaffList.find(s => String(s.id) === String(user.default_sales_staff_id))?.name || `Listede yok (#${user.default_sales_staff_id})`}</span>
                        )}
                      </div>
                      <div className="user-actions">
                        <button className="user-btn edit" onClick={() => openEditUserModal(user)} title="Düzenle"><Pencil size={16} /></button>
                        <button className="user-btn toggle" onClick={() => toggleUserStatus(user)} title={user.active ? 'Pasif Yap' : 'Aktif Yap'}>
                          {user.active ? <Lock size={16} /> : <Unlock size={16} />}
                        </button>
                        {user.role !== 'admin' && (
                          <button className="user-btn delete" onClick={() => handleDeleteUser(user)} title="Sil"><Trash2 size={16} /></button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'efatura' && (
            <div className="settings-panel" key="efatura-panel">
              <div className="panel-header">
                <h2>E-Fatura / E-Arsiv Ayarlari</h2>
                <div className="header-actions">
                  <button
                    className="test-btn"
                    onClick={testEfaturaConnection}
                    disabled={testingConnection}
                  >
                    {testingConnection ? 'Test Ediliyor...' : 'Baglanti Testi'}
                  </button>
                  <button
                    className="save-btn"
                    onClick={saveEfaturaSettings}
                    disabled={savingEfatura}
                  >
                    {savingEfatura ? 'Kaydediliyor...' : 'Kaydet'}
                  </button>
                </div>
              </div>

              {loadingEfatura ? (
                <div className="loading-state">Yukleniyor...</div>
              ) : (
                <>
                  {/* İstatistikler */}
                  {efaturaStats && (
                    <div className="efatura-stats">
                      <div className="stat-card">
                        <span className="stat-icon"><FileText size={24} style={{ color: '#3b82f6' }} /></span>
                        <div className="stat-info">
                          <span className="stat-value">{efaturaStats.total_invoices || 0}</span>
                          <span className="stat-label">Toplam Fatura</span>
                        </div>
                      </div>
                      <div className="stat-card success">
                        <span className="stat-icon"><CheckCircle size={24} style={{ color: '#10b981' }} /></span>
                        <div className="stat-info">
                          <span className="stat-value">{efaturaStats.sent_count || 0}</span>
                          <span className="stat-label">Gonderilen</span>
                        </div>
                      </div>
                      <div className="stat-card warning">
                        <span className="stat-icon"><Clock size={24} style={{ color: '#f59e0b' }} /></span>
                        <div className="stat-info">
                          <span className="stat-value">{efaturaStats.pending_count || 0}</span>
                          <span className="stat-label">Bekleyen</span>
                        </div>
                      </div>
                      <div className="stat-card">
                        <span className="stat-icon"><Coins size={24} style={{ color: '#f59e0b' }} /></span>
                        <div className="stat-info">
                          <span className="stat-value">{(efaturaStats.total_amount || 0).toLocaleString('tr-TR')} TL</span>
                          <span className="stat-label">Toplam Tutar</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Aktivasyon Toggle'ları */}
                  <div className="efatura-toggles">
                    <div className="toggle-card">
                      <div className="toggle-info">
                        <h4>E-Fatura</h4>
                        <p>Kurumsal musterilere e-fatura kesimi</p>
                      </div>
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={efaturaSettings.efatura_enabled}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, efatura_enabled: e.target.checked})}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                    <div className="toggle-card">
                      <div className="toggle-info">
                        <h4>E-Arsiv</h4>
                        <p>Bireysel musterilere e-arsiv fatura</p>
                      </div>
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={efaturaSettings.earsiv_enabled}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, earsiv_enabled: e.target.checked})}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                    <div className="toggle-card">
                      <div className="toggle-info">
                        <h4>Otomatik Gonder</h4>
                        <p>Faturalari otomatik olarak GIB'e gonder</p>
                      </div>
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={efaturaSettings.auto_send}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, auto_send: e.target.checked})}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                    <div className="toggle-card">
                      <div className="toggle-info">
                        <h4>Test Modu</h4>
                        <p>GIB test ortamini kullan</p>
                      </div>
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={efaturaSettings.test_mode}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, test_mode: e.target.checked})}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>

                  {/* Firma Bilgileri */}
                  <div className="efatura-section">
                    <h3>Firma Bilgileri</h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>VKN / TCKN *</label>
                        <input
                          type="text"
                          value={efaturaSettings.company_vkn}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, company_vkn: e.target.value})}
                          placeholder="10 veya 11 haneli"
                          maxLength={11}
                        />
                      </div>
                      <div className="form-group">
                        <label>Firma Unvani *</label>
                        <input
                          type="text"
                          value={efaturaSettings.company_name}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, company_name: e.target.value})}
                          placeholder="Firma adi..."
                        />
                      </div>
                      <div className="form-group">
                        <label>Vergi Dairesi</label>
                        <input
                          type="text"
                          value={efaturaSettings.company_tax_office}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, company_tax_office: e.target.value})}
                          placeholder="Vergi dairesi..."
                        />
                      </div>
                      <div className="form-group">
                        <label>Il</label>
                        <input
                          type="text"
                          value={efaturaSettings.company_city}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, company_city: e.target.value})}
                          placeholder="Il..."
                        />
                      </div>
                      <div className="form-group">
                        <label>Ilce</label>
                        <input
                          type="text"
                          value={efaturaSettings.company_district}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, company_district: e.target.value})}
                          placeholder="Ilce..."
                        />
                      </div>
                      <div className="form-group">
                        <label>Posta Kodu</label>
                        <input
                          type="text"
                          value={efaturaSettings.company_postal_code}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, company_postal_code: e.target.value})}
                          placeholder="Posta kodu..."
                        />
                      </div>
                      <div className="form-group full-width">
                        <label>Adres</label>
                        <textarea
                          value={efaturaSettings.company_address}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, company_address: e.target.value})}
                          placeholder="Acik adres..."
                        />
                      </div>
                      <div className="form-group">
                        <label>Telefon</label>
                        <input
                          type="tel"
                          value={efaturaSettings.company_phone}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, company_phone: e.target.value})}
                          placeholder="Telefon..."
                        />
                      </div>
                      <div className="form-group">
                        <label>E-posta</label>
                        <input
                          type="email"
                          value={efaturaSettings.company_email}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, company_email: e.target.value})}
                          placeholder="E-posta..."
                        />
                      </div>
                      <div className="form-group">
                        <label>Web Sitesi</label>
                        <input
                          type="url"
                          value={efaturaSettings.company_website}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, company_website: e.target.value})}
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* GİB Bilgileri */}
                  <div className="efatura-section">
                    <h3>GIB Baglanti Bilgileri</h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>GIB Kullanici Adi</label>
                        <input
                          type="text"
                          value={efaturaSettings.gib_username}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, gib_username: e.target.value})}
                          placeholder="GIB kullanici adi..."
                        />
                      </div>
                      <div className="form-group">
                        <label>GIB Sifre</label>
                        <input
                          type="password"
                          value={efaturaSettings.gib_password}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, gib_password: e.target.value})}
                          placeholder="GIB sifre..."
                        />
                      </div>
                      <div className="form-group">
                        <label>GIB Posta Kutusu Adresi</label>
                        <input
                          type="text"
                          value={efaturaSettings.gib_postbox_alias}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, gib_postbox_alias: e.target.value})}
                          placeholder="urn:mail:defaultpk@..."
                        />
                      </div>
                      <div className="form-group">
                        <label>E-Fatura API URL</label>
                        <input
                          type="url"
                          value={efaturaSettings.efatura_api_url}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, efatura_api_url: e.target.value})}
                          placeholder="http://localhost:8080/api"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Dijital İmza */}
                  <div className="efatura-section">
                    <h3>Dijital Imza (Mali Muhur)</h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Imza Tipi</label>
                        <select
                          value={efaturaSettings.signature_type}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, signature_type: e.target.value})}
                        >
                          <option value="pkcs12">PKCS#12 (PFX Dosyasi)</option>
                          <option value="pkcs11">PKCS#11 (USB Token)</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Sertifika Dosya Yolu</label>
                        <input
                          type="text"
                          value={efaturaSettings.signature_path}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, signature_path: e.target.value})}
                          placeholder="/path/to/certificate.pfx"
                        />
                      </div>
                      <div className="form-group">
                        <label>Sertifika Sifresi</label>
                        <input
                          type="password"
                          value={efaturaSettings.signature_password}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, signature_password: e.target.value})}
                          placeholder="Sertifika sifresi..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Fatura Ayarları */}
                  <div className="efatura-section">
                    <h3>Fatura Ayarlari</h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Fatura On Eki</label>
                        <input
                          type="text"
                          value={efaturaSettings.invoice_prefix}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, invoice_prefix: e.target.value})}
                          placeholder="AKK"
                          maxLength={10}
                        />
                      </div>
                      <div className="form-group">
                        <label>KDV Orani (%)</label>
                        <select
                          value={efaturaSettings.default_tax_rate}
                          onChange={(e) => setEfaturaSettings({...efaturaSettings, default_tax_rate: parseFloat(e.target.value)})}
                        >
                          <option value="0">%0 (KDV Yok)</option>
                          <option value="1">%1</option>
                          <option value="8">%8</option>
                          <option value="10">%10</option>
                          <option value="18">%18</option>
                          <option value="20">%20</option>
                        </select>
                      </div>
                      <div className="form-group full-width">
                        <div className="checkbox-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={efaturaSettings.prices_include_tax}
                              onChange={(e) => setEfaturaSettings({...efaturaSettings, prices_include_tax: e.target.checked})}
                            />
                            <span>Magaza fiyatlarina KDV dahil</span>
                          </label>
                          <p className="help-text">Bu secili ise, satis fiyatlarindan KDV ayristirilarak fatura olusturulur.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Son Faturalar */}
                  <div className="efatura-section">
                    <h3>Son Faturalar</h3>

                    {/* Arama ve Filtreler */}
                    <div className="invoice-filters-advanced">
                      <div className="filter-row">
                        <div className="search-box">
                          <input
                            type="text"
                            placeholder="Fatura no, musteri adi ara..."
                            value={invoiceFilter.search}
                            onChange={(e) => setInvoiceFilter({...invoiceFilter, search: e.target.value})}
                            onKeyPress={(e) => e.key === 'Enter' && fetchInvoices()}
                          />
                          <button className="search-btn" onClick={fetchInvoices}>Ara</button>
                        </div>
                      </div>
                      <div className="filter-row">
                        <select
                          value={invoiceFilter.status}
                          onChange={(e) => {
                            setInvoiceFilter({...invoiceFilter, status: e.target.value});
                            setTimeout(fetchInvoices, 100);
                          }}
                        >
                          <option value="">Tum Durumlar</option>
                          <option value="DRAFT">Taslak</option>
                          <option value="VALIDATED">Onaylandi</option>
                          <option value="CREATED">Olusturuldu</option>
                          <option value="SIGNED">Imzalandi</option>
                          <option value="SENT">Gonderildi</option>
                          <option value="ACCEPTED">Kabul Edildi</option>
                          <option value="REJECTED">Reddedildi</option>
                        </select>
                        <select
                          value={invoiceFilter.scenario}
                          onChange={(e) => {
                            setInvoiceFilter({...invoiceFilter, scenario: e.target.value});
                            setTimeout(fetchInvoices, 100);
                          }}
                        >
                          <option value="">Tum Senaryolar</option>
                          <option value="EARSIV">E-Arsiv</option>
                          <option value="TEMEL">Temel Fatura</option>
                          <option value="TICARI">Ticari Fatura</option>
                        </select>
                        <input
                          type="date"
                          value={invoiceFilter.startDate}
                          onChange={(e) => {
                            setInvoiceFilter({...invoiceFilter, startDate: e.target.value});
                            setTimeout(fetchInvoices, 100);
                          }}
                          placeholder="Baslangic"
                        />
                        <input
                          type="date"
                          value={invoiceFilter.endDate}
                          onChange={(e) => {
                            setInvoiceFilter({...invoiceFilter, endDate: e.target.value});
                            setTimeout(fetchInvoices, 100);
                          }}
                          placeholder="Bitis"
                        />
                        <button
                          className="clear-filters-btn"
                          onClick={() => {
                            setInvoiceFilter({ status: '', scenario: '', search: '', startDate: '', endDate: '' });
                            setTimeout(fetchInvoices, 100);
                          }}
                        >
                          Temizle
                        </button>
                      </div>
                    </div>

                    <div className="invoices-count">
                      Toplam: <strong>{invoices.length}</strong> fatura
                    </div>

                    <div className="invoices-list">
                      {loadingInvoices ? (
                        <div className="loading-state">Yukleniyor...</div>
                      ) : invoices.length === 0 ? (
                        <div className="empty-state">Henuz fatura yok</div>
                      ) : (
                        invoices.map(invoice => (
                          <div key={invoice.id} className={`invoice-item status-${invoice.status?.toLowerCase()}`}>
                            <div className="invoice-main">
                              <span className="invoice-number">{invoice.invoice_number || `#${invoice.id}`}</span>
                              <span className="invoice-customer">{invoice.customer_name}</span>
                              <span className={`invoice-scenario ${invoice.scenario?.toLowerCase()}`}>
                                {invoice.scenario === 'EARSIV' ? 'E-Arsiv' : invoice.scenario === 'TEMEL' ? 'Temel' : 'Ticari'}
                              </span>
                            </div>
                            <div className="invoice-meta">
                              <span className="invoice-total">{parseFloat(invoice.total || 0).toLocaleString('tr-TR')} TL</span>
                              <span className={`invoice-status ${invoice.status?.toLowerCase()}`}>
                                {invoice.status === 'DRAFT' ? 'Taslak' :
                                 invoice.status === 'VALIDATED' ? 'Onaylandi' :
                                 invoice.status === 'CREATED' ? 'Olusturuldu' :
                                 invoice.status === 'SIGNED' ? 'Imzalandi' :
                                 invoice.status === 'SENT' ? 'Gonderildi' :
                                 invoice.status === 'ACCEPTED' ? 'Kabul' :
                                 invoice.status === 'REJECTED' ? 'Red' : invoice.status}
                              </span>
                              <span className="invoice-date">{new Date(invoice.created_at).toLocaleDateString('tr-TR')}</span>
                            </div>
                            <div className="invoice-actions">
                              <button
                                className="action-btn pdf-btn"
                                onClick={() => handleViewPdf(invoice)}
                                title="PDF Goruntule"
                              >
                                PDF
                              </button>
                              <button
                                className="action-btn xml-btn"
                                onClick={() => handleDownloadXml(invoice)}
                                title="XML Indir"
                              >
                                XML
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'backup' && (
            <div className="settings-panel" key="backup-panel">
              <h2>Yedekleme</h2>

              {/* Otomatik Yedekleme Ayarları */}
              <div className="auto-backup-settings">
                <h3>Otomatik Yedekleme</h3>
                <div className="auto-backup-row">
                  <div className="auto-backup-toggle">
                    <label className="toggle">
                      <input
                        type="checkbox"
                        checked={autoBackupEnabled}
                        onChange={(e) => setAutoBackupEnabled(e.target.checked)}
                      />
                      <span className="slider"></span>
                    </label>
                    <span className="toggle-label">
                      {autoBackupEnabled ? 'Aktif' : 'Kapalı'}
                    </span>
                  </div>
                  <div className="auto-backup-interval">
                    <label>Yedekleme Sıklığı:</label>
                    <select
                      value={autoBackupInterval}
                      onChange={(e) => setAutoBackupInterval(parseInt(e.target.value))}
                      disabled={!autoBackupEnabled}
                    >
                      <option value={1}>Her 1 saat</option>
                      <option value={6}>Her 6 saat</option>
                      <option value={12}>Her 12 saat</option>
                      <option value={24}>Her 24 saat</option>
                      <option value={48}>Her 2 gün</option>
                      <option value={168}>Her hafta</option>
                    </select>
                  </div>
                  <button
                    className="save-auto-backup-btn"
                    onClick={saveBackupSettings}
                    disabled={savingBackupSettings}
                  >
                    {savingBackupSettings ? 'Kaydediliyor...' : 'Kaydet'}
                  </button>
                </div>
                <p className="auto-backup-info">
                  {autoBackupEnabled
                    ? `Otomatik yedekleme her ${autoBackupInterval} saatte bir çalışacak. Son 10 otomatik yedek saklanır.`
                    : 'Otomatik yedekleme kapalı. Aktifleştirmek için toggle\'ı açın.'}
                </p>
              </div>

              <div className="backup-section">
                <div className="backup-card">
                  <span className="backup-icon"><Package size={32} style={{ color: '#3b82f6' }} /></span>
                  <h3>JSON Yedek Al</h3>
                  <p>Uygulama içinden geri yüklenebilir format</p>
                  <button
                    className="backup-btn"
                    onClick={() => createBackup('json')}
                    disabled={creatingBackup}
                  >
                    {creatingBackup ? 'Yedekleniyor...' : 'JSON Yedek Al'}
                  </button>
                </div>
                <div className="backup-card">
                  <span className="backup-icon"><Database size={32} style={{ color: '#8b5cf6' }} /></span>
                  <h3>SQL Yedek Al</h3>
                  <p>MySQL veritabanı tam yedek dosyası</p>
                  <button
                    className="backup-btn sql"
                    onClick={() => createBackup('sql')}
                    disabled={creatingBackup}
                  >
                    {creatingBackup ? 'Yedekleniyor...' : 'SQL Yedek Al'}
                  </button>
                </div>
                <div className="backup-card">
                  <span className="backup-icon"><Download size={32} style={{ color: '#10b981' }} /></span>
                  <h3>Dosyadan Geri Yükle</h3>
                  <p>JSON veya SQL yedek dosyası yükleyin</p>
                  <label className="backup-btn secondary">
                    {restoringBackup ? 'Yükleniyor...' : 'Dosya Seç'}
                    <input
                      type="file"
                      accept=".json,.sql"
                      onChange={handleFileRestore}
                      disabled={restoringBackup}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
              <div className="backup-history">
                <div className="backup-history-header">
                  <h3>Son Yedekler</h3>
                </div>

                {/* Filtre Bar */}
                <div className="backup-filter-bar">
                  <div className="backup-date-filters">
                    <button
                      className={`filter-chip ${backupDateFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setBackupDateFilter('all')}
                    >
                      Tümü
                    </button>
                    <button
                      className={`filter-chip ${backupDateFilter === '30min' ? 'active' : ''}`}
                      onClick={() => setBackupDateFilter('30min')}
                    >
                      Son 30 dk
                    </button>
                    <button
                      className={`filter-chip ${backupDateFilter === '1hour' ? 'active' : ''}`}
                      onClick={() => setBackupDateFilter('1hour')}
                    >
                      Son 1 Saat
                    </button>
                    <button
                      className={`filter-chip ${backupDateFilter === '6hour' ? 'active' : ''}`}
                      onClick={() => setBackupDateFilter('6hour')}
                    >
                      Son 6 Saat
                    </button>
                    <button
                      className={`filter-chip ${backupDateFilter === 'today' ? 'active' : ''}`}
                      onClick={() => setBackupDateFilter('today')}
                    >
                      Bugün
                    </button>
                    <button
                      className={`filter-chip ${backupDateFilter === 'week' ? 'active' : ''}`}
                      onClick={() => setBackupDateFilter('week')}
                    >
                      Son 7 Gün
                    </button>
                    <button
                      className={`filter-chip ${backupDateFilter === 'month' ? 'active' : ''}`}
                      onClick={() => setBackupDateFilter('month')}
                    >
                      Son 30 Gün
                    </button>
                    <span className="filter-separator">|</span>
                    <button
                      className={`filter-chip ${backupTypeFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setBackupTypeFilter('all')}
                    >
                      Tümü
                    </button>
                    <button
                      className={`filter-chip json ${backupTypeFilter === 'json' ? 'active' : ''}`}
                      onClick={() => setBackupTypeFilter('json')}
                    >
                      JSON
                    </button>
                    <button
                      className={`filter-chip sql ${backupTypeFilter === 'sql' ? 'active' : ''}`}
                      onClick={() => setBackupTypeFilter('sql')}
                    >
                      SQL
                    </button>
                  </div>
                  <div className="backup-search">
                    <input
                      type="text"
                      placeholder="Ara..."
                      value={backupFilter}
                      onChange={(e) => setBackupFilter(e.target.value)}
                      className="backup-filter-input"
                    />
                    {backupFilter && (
                      <button
                        className="backup-filter-clear"
                        onClick={() => setBackupFilter('')}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {loadingBackups ? (
                  <div className="loading-state">Yükleniyor...</div>
                ) : backups.length === 0 ? (
                  <div className="empty-state">Henüz yedek yok</div>
                ) : (
                  backups
                    .filter(backup => {
                      // Tarih filtresi
                      const backupDate = new Date(backup.created_at);
                      const now = new Date();
                      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                      const min30Ago = new Date(now.getTime() - 30 * 60 * 1000);
                      const hour1Ago = new Date(now.getTime() - 60 * 60 * 1000);
                      const hour6Ago = new Date(now.getTime() - 6 * 60 * 60 * 1000);
                      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

                      if (backupDateFilter === '30min' && backupDate < min30Ago) return false;
                      if (backupDateFilter === '1hour' && backupDate < hour1Ago) return false;
                      if (backupDateFilter === '6hour' && backupDate < hour6Ago) return false;
                      if (backupDateFilter === 'today' && backupDate < today) return false;
                      if (backupDateFilter === 'week' && backupDate < weekAgo) return false;
                      if (backupDateFilter === 'month' && backupDate < monthAgo) return false;

                      // Tür filtresi
                      if (backupTypeFilter !== 'all' && backup.type !== backupTypeFilter) return false;

                      // Arama filtresi
                      if (!backupFilter) return true;
                      const searchTerm = backupFilter.toLowerCase();
                      const dateStr = formatDate(backup.created_at).toLowerCase();
                      const filename = backup.filename?.toLowerCase() || '';
                      return dateStr.includes(searchTerm) || filename.includes(searchTerm);
                    })
                    .map((backup, index) => (
                    <div key={index} className={`backup-item ${backup.type}`}>
                      <span className={`backup-type-badge ${backup.type}`}>{backup.type?.toUpperCase()}</span>
                      <span className="backup-date">{formatDate(backup.created_at)}</span>
                      <span className="backup-size">{backup.size}</span>
                      <div className="backup-actions">
                        <button
                          className="download-btn"
                          onClick={() => downloadBackup(backup.filename)}
                          title="İndir"
                        >
                          <Download size={14} /> İndir
                        </button>
                        <button
                          className="restore-btn"
                          onClick={() => restoreFromBackup(backup.filename)}
                          disabled={restoringBackup}
                          title="Geri Yükle"
                        >
                          <RefreshCw size={14} /> Yükle
                        </button>
                        <button
                          className="delete-backup-btn"
                          onClick={() => deleteBackup(backup.filename)}
                          title="Sil"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Ürün + Resim Yedeği Bölümü */}
              <div className="product-image-backup-section" style={{ marginTop: '32px', borderTop: '2px solid #e5e7eb', paddingTop: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0 }}>Ürün + Resim Yedeği</h3>
                  <button
                    className="backup-btn"
                    onClick={createProductImageBackup}
                    disabled={creatingProductImageBackup}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    {creatingProductImageBackup ? (
                      <><RefreshCw size={14} className="spinning" /> Başlatılıyor...</>
                    ) : (
                      <><Server size={14} /> Manuel Yedek Başlat</>
                    )}
                  </button>
                </div>
                <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '16px' }}>
                  Ürün veritabanı ve tüm ürün resimlerinin tam yedeği. Her gece 03:00'te otomatik çalışır, email ile indirme linki gönderilir.
                </p>
                {loadingProductImageBackups ? (
                  <div className="loading-state">Yükleniyor...</div>
                ) : productImageBackups.length === 0 ? (
                  <div className="empty-state">Henüz ürün + resim yedeği yok</div>
                ) : (
                  productImageBackups.map((backup, index) => (
                    <div key={index} className="backup-item" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                      <span className="backup-type-badge" style={{ background: '#f59e0b', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>TAR.GZ</span>
                      <span className="backup-date" style={{ flex: 1 }}>{new Date(backup.date).toLocaleString('tr-TR')}</span>
                      <span className="backup-size" style={{ color: '#6b7280', fontSize: '13px' }}>{backup.size}</span>
                      <a
                        href={`${API_URL}/product-backup/download/${backup.filename}?token=${localStorage.getItem('token')}`}
                        className="download-btn"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 12px', background: '#3b82f6', color: '#fff', borderRadius: '6px', textDecoration: 'none', fontSize: '13px' }}
                      >
                        <Download size={14} /> İndir
                      </a>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Log Kayıtları Tab */}
          {activeTab === 'logs' && isAdmin && (
            <div className="settings-panel logs-panel" key="logs-panel">
              <div className="logs-header">
                <div className="logs-title-section">
                  <h2><History size={24} /> Log Kayıtları</h2>
                  <p className="logs-subtitle">Son 30 günlük sistem aktivitelerini görüntüleyin</p>
                </div>
                <button
                  className="cleanup-logs-btn"
                  onClick={handleCleanupLogs}
                  disabled={cleaningLogs}
                >
                  <Trash2 size={16} />
                  {cleaningLogs ? 'Temizleniyor...' : 'Eski Logları Temizle'}
                </button>
              </div>

              {/* Log Özet Kartları */}
              {logSummary && (
                <div className="log-summary-cards">
                  <div className="log-summary-card">
                    <div className="log-summary-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>
                      <Activity size={24} />
                    </div>
                    <div className="log-summary-info">
                      <span className="log-summary-value">{logSummary.total?.toLocaleString('tr-TR')}</span>
                      <span className="log-summary-label">Toplam Log</span>
                    </div>
                  </div>
                  <div className="log-summary-card">
                    <div className="log-summary-icon" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                      <Calendar size={24} />
                    </div>
                    <div className="log-summary-info">
                      <span className="log-summary-value">{logSummary.today?.toLocaleString('tr-TR')}</span>
                      <span className="log-summary-label">Bugün</span>
                    </div>
                  </div>
                  <div className="log-summary-card">
                    <div className="log-summary-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}>
                      <Clock size={24} />
                    </div>
                    <div className="log-summary-info">
                      <span className="log-summary-value">{logSummary.last30Days?.toLocaleString('tr-TR')}</span>
                      <span className="log-summary-label">Son 30 Gün</span>
                    </div>
                  </div>
                  <div className="log-summary-card">
                    <div className="log-summary-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                      <Users size={24} />
                    </div>
                    <div className="log-summary-info">
                      <span className="log-summary-value">{logSummary.activeUsers?.length || 0}</span>
                      <span className="log-summary-label">Aktif Kullanıcı</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Filtreler */}
              <div className="log-filters-section">
                <div className="log-filter-row">
                  <div className="log-filter-item">
                    <Filter size={16} />
                    <select
                      value={logFilters.module}
                      onChange={(e) => setLogFilters({ ...logFilters, module: e.target.value })}
                    >
                      <option value="">Tüm Modüller</option>
                      {Object.entries(moduleNames).map(([key, name]) => (
                        <option key={key} value={key}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="log-filter-item">
                    <select
                      value={logFilters.status}
                      onChange={(e) => setLogFilters({ ...logFilters, status: e.target.value })}
                    >
                      <option value="">Tüm Durumlar</option>
                      <option value="success">Başarılı</option>
                      <option value="error">Hata</option>
                      <option value="warning">Uyarı</option>
                      <option value="info">Bilgi</option>
                    </select>
                  </div>
                  <div className="log-filter-item search-filter">
                    <Search size={16} />
                    <input
                      type="text"
                      placeholder="Ara..."
                      value={logFilters.search}
                      onChange={(e) => setLogFilters({ ...logFilters, search: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && applyLogFilters()}
                    />
                  </div>
                  <button className="log-filter-btn apply" onClick={applyLogFilters}>
                    Filtrele
                  </button>
                  <button className="log-filter-btn clear" onClick={clearLogFilters}>
                    Temizle
                  </button>
                </div>
              </div>

              {/* Gün Bazlı Log Listesi */}
              <div className="logs-day-list">
                {loadingLogs ? (
                  <div className="logs-loading">
                    <div className="loading-spinner"></div>
                    <span>Loglar yükleniyor...</span>
                  </div>
                ) : Object.keys(logData.logs || {}).length === 0 ? (
                  <div className="logs-empty">
                    <History size={48} />
                    <span>Kayıtlı log bulunamadı</span>
                  </div>
                ) : (
                  Object.entries(logData.logs || {})
                    .sort(([a], [b]) => new Date(b) - new Date(a))
                    .map(([date, logs]) => {
                      const dayStat = logData.dayStats?.find(d => d.log_date === date);
                      const isExpanded = expandedDays[date];

                      return (
                        <div key={date} className="log-day-group">
                          <div
                            className={`log-day-header ${isExpanded ? 'expanded' : ''}`}
                            onClick={() => toggleDayExpand(date)}
                          >
                            <div className="log-day-info">
                              <Calendar size={18} />
                              <span className="log-day-date">{formatLogDate(date)}</span>
                              <span className="log-day-count">{logs.length} kayıt</span>
                            </div>
                            <div className="log-day-stats">
                              {dayStat?.success_count > 0 && (
                                <span className="log-stat success">
                                  <CheckCircle size={12} /> {dayStat.success_count}
                                </span>
                              )}
                              {dayStat?.error_count > 0 && (
                                <span className="log-stat error">
                                  <XCircle size={12} /> {dayStat.error_count}
                                </span>
                              )}
                              {dayStat?.warning_count > 0 && (
                                <span className="log-stat warning">
                                  <AlertCircle size={12} /> {dayStat.warning_count}
                                </span>
                              )}
                              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="log-day-content">
                              {logs.map((log) => (
                                <div
                                  key={log.id}
                                  className={`log-item status-${log.status}`}
                                  onClick={() => setSelectedLogDetail(log)}
                                >
                                  <div className="log-item-time">
                                    <Clock size={14} />
                                    {formatLogTime(log.created_at)}
                                  </div>
                                  <div className="log-item-status">
                                    <span
                                      className="status-badge"
                                      style={{ background: statusConfig[log.status]?.color }}
                                    >
                                      {statusConfig[log.status]?.icon}
                                    </span>
                                  </div>
                                  <div className="log-item-module">
                                    <span className="module-badge">{moduleNames[log.module] || log.module}</span>
                                  </div>
                                  <div className="log-item-action">
                                    {actionNames[log.action] || log.action}
                                  </div>
                                  <div className="log-item-description">
                                    {log.description}
                                  </div>
                                  <div className="log-item-user">
                                    <User size={14} />
                                    {log.user_name || 'Sistem'}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                )}
              </div>

              {/* Aktif Kullanıcılar */}
              {logSummary?.activeUsers && logSummary.activeUsers.length > 0 && (
                <div className="log-active-users">
                  <h3><Users size={18} /> En Aktif Kullanıcılar (Son 30 Gün)</h3>
                  <div className="active-users-list">
                    {logSummary.activeUsers.map((user, index) => (
                      <div key={user.user_id} className="active-user-item">
                        <span className="user-rank">#{index + 1}</span>
                        <span className="user-name">{user.user_name}</span>
                        <span className="user-actions">{user.action_count} işlem</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Modül Dağılımı */}
              {logSummary?.moduleDistribution && logSummary.moduleDistribution.length > 0 && (
                <div className="log-module-distribution">
                  <h3><Activity size={18} /> Modül Dağılımı (Son 30 Gün)</h3>
                  <div className="module-distribution-grid">
                    {logSummary.moduleDistribution.map((mod) => (
                      <div key={mod.module} className="module-dist-item">
                        <span className="module-name">{moduleNames[mod.module] || mod.module}</span>
                        <span className="module-count">{mod.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detaylı Loglar Bölümü */}
              <div className="detailed-logs-section">
                <div className="detailed-logs-header">
                  <h3><Eye size={20} /> Detaylı Kullanıcı Aktiviteleri</h3>
                  <p>Kullanıcıların sayfa ziyaretleri, tıklamaları ve tüm hareketlerini takip edin</p>
                  {!showDetailedLogs ? (
                    <button className="load-detailed-btn" onClick={loadDetailedLogs}>
                      <Activity size={16} /> Detaylı Logları Yükle
                    </button>
                  ) : (
                    <button className="load-detailed-btn refresh" onClick={loadDetailedLogs}>
                      <RefreshCw size={16} /> Yenile
                    </button>
                  )}
                </div>

                {showDetailedLogs && (
                  <>
                    {/* Aktivite İstatistik Kartları */}
                    {activityStats && (
                      <div className="activity-stats-cards">
                        <div className="activity-stat-card">
                          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
                            <Activity size={22} />
                          </div>
                          <div className="stat-info">
                            <span className="stat-value">{activityStats.total?.toLocaleString('tr-TR')}</span>
                            <span className="stat-label">Toplam Aktivite</span>
                          </div>
                        </div>
                        <div className="activity-stat-card">
                          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                            <Calendar size={22} />
                          </div>
                          <div className="stat-info">
                            <span className="stat-value">{activityStats.today?.toLocaleString('tr-TR')}</span>
                            <span className="stat-label">Bugün</span>
                          </div>
                        </div>
                        <div className="activity-stat-card">
                          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                            <Users size={22} />
                          </div>
                          <div className="stat-info">
                            <span className="stat-value">{activityStats.activeUsersToday || 0}</span>
                            <span className="stat-label">Bugün Aktif Kullanıcı</span>
                          </div>
                        </div>
                        <div className="activity-stat-card">
                          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #ec4899, #db2777)' }}>
                            <Eye size={22} />
                          </div>
                          <div className="stat-info">
                            <span className="stat-value">{activityStats.topPages?.[0]?.visits || 0}</span>
                            <span className="stat-label">En Çok Ziyaret</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Kullanıcı Aktivite Özeti */}
                    {userActivitySummary.length > 0 && (
                      <div className="user-activity-summary">
                        <h4><Users size={16} /> Kullanıcı Aktivite Özeti (Son 30 Gün)</h4>
                        <div className="user-activity-table">
                          <table>
                            <thead>
                              <tr>
                                <th>Kullanıcı</th>
                                <th>Rol</th>
                                <th>Toplam</th>
                                <th>Sayfa Görüntüleme</th>
                                <th>Tıklama</th>
                                <th>Oturum</th>
                                <th>Son Aktivite</th>
                                <th>İşlem</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userActivitySummary.map((userStat) => (
                                <tr key={userStat.user_id}>
                                  <td className="user-name-cell">{userStat.user_name}</td>
                                  <td><span className="role-badge">{userStat.user_role}</span></td>
                                  <td><strong>{userStat.total_activities}</strong></td>
                                  <td>{userStat.page_views}</td>
                                  <td>{userStat.clicks}</td>
                                  <td>{userStat.total_sessions}</td>
                                  <td className="date-cell">{new Date(userStat.last_activity).toLocaleString('tr-TR')}</td>
                                  <td>
                                    <button
                                      className="timeline-btn"
                                      onClick={() => {
                                        setSelectedUserTimeline(userStat);
                                        fetchUserTimeline(userStat.user_id);
                                      }}
                                    >
                                      <Clock size={14} /> Timeline
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* En Çok Ziyaret Edilen Sayfalar */}
                    {pageActivitySummary.length > 0 && (
                      <div className="page-activity-summary">
                        <h4><FileText size={16} /> En Çok Ziyaret Edilen Sayfalar (Son 30 Gün)</h4>
                        <div className="page-stats-grid">
                          {pageActivitySummary.slice(0, 10).map((pageStat, index) => (
                            <div key={pageStat.page_name} className="page-stat-item">
                              <span className="page-rank">#{index + 1}</span>
                              <div className="page-info">
                                <span className="page-name">{pageStat.page_name}</span>
                                <span className="page-path">{pageStat.page_path}</span>
                              </div>
                              <div className="page-metrics">
                                <span className="visits">{pageStat.total_visits} ziyaret</span>
                                <span className="users">{pageStat.unique_users} kullanıcı</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Aktivite Tipi Dağılımı */}
                    {activityStats?.typeDistribution && (
                      <div className="activity-type-distribution">
                        <h4><Filter size={16} /> Aktivite Tipi Dağılımı</h4>
                        <div className="type-dist-grid">
                          {activityStats.typeDistribution.map((type) => (
                            <div
                              key={type.activity_type}
                              className="type-dist-item"
                              style={{ borderLeftColor: activityTypeColors[type.activity_type] || '#94a3b8' }}
                            >
                              <span className="type-name">{activityTypeNames[type.activity_type] || type.activity_type}</span>
                              <span className="type-count">{type.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Aktivite Filtreleme ve Liste */}
                    <div className="activity-list-section">
                      <h4><History size={16} /> Tüm Aktiviteler</h4>

                      {/* Filtreler */}
                      <div className="activity-filters">
                        <select
                          value={activityFilters.activity_type}
                          onChange={(e) => setActivityFilters({ ...activityFilters, activity_type: e.target.value })}
                        >
                          <option value="">Tüm Aktiviteler</option>
                          {Object.entries(activityTypeNames).map(([key, name]) => (
                            <option key={key} value={key}>{name}</option>
                          ))}
                        </select>
                        <div className="search-input-wrapper">
                          <Search size={16} />
                          <input
                            type="text"
                            placeholder="Ara..."
                            value={activityFilters.search}
                            onChange={(e) => setActivityFilters({ ...activityFilters, search: e.target.value })}
                            onKeyDown={(e) => e.key === 'Enter' && applyActivityFilters()}
                          />
                        </div>
                        <button className="filter-btn" onClick={applyActivityFilters}>Filtrele</button>
                        <button className="filter-btn clear" onClick={clearActivityFilters}>Temizle</button>
                      </div>

                      {/* Aktivite Listesi */}
                      {loadingActivity ? (
                        <div className="activity-loading">
                          <div className="loading-spinner"></div>
                          <span>Yükleniyor...</span>
                        </div>
                      ) : (
                        <div className="activity-list">
                          {activityData.activities?.map((activity) => (
                            <div key={activity.id} className="activity-item">
                              <div className="activity-time">
                                {new Date(activity.created_at).toLocaleString('tr-TR')}
                              </div>
                              <div
                                className="activity-type-badge"
                                style={{ background: activityTypeColors[activity.activity_type] || '#94a3b8' }}
                              >
                                {activityTypeNames[activity.activity_type] || activity.activity_type}
                              </div>
                              <div className="activity-user">
                                <User size={14} />
                                {activity.user_name}
                              </div>
                              <div className="activity-page">
                                {activity.page_name || activity.page_path}
                              </div>
                              <div className="activity-detail">
                                {activity.element_text || '-'}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Sayfalama */}
                      {activityData.pagination?.totalPages > 1 && (
                        <div className="activity-pagination">
                          <button
                            disabled={activityPage === 1}
                            onClick={() => fetchActivityData(activityPage - 1)}
                          >
                            Önceki
                          </button>
                          <span>Sayfa {activityPage} / {activityData.pagination.totalPages}</span>
                          <button
                            disabled={activityPage >= activityData.pagination.totalPages}
                            onClick={() => fetchActivityData(activityPage + 1)}
                          >
                            Sonraki
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Kullanıcı Timeline Modal */}
          {selectedUserTimeline && (
            <div className="timeline-modal-overlay" onClick={() => setSelectedUserTimeline(null)}>
              <div className="timeline-modal" onClick={(e) => e.stopPropagation()}>
                <div className="timeline-modal-header">
                  <h3><Clock size={20} /> {selectedUserTimeline.user_name} - Aktivite Timeline</h3>
                  <button className="close-btn" onClick={() => setSelectedUserTimeline(null)}>✕</button>
                </div>
                <div className="timeline-modal-content">
                  {loadingTimeline ? (
                    <div className="timeline-loading">
                      <div className="loading-spinner"></div>
                      <span>Yükleniyor...</span>
                    </div>
                  ) : (
                    <div className="timeline-list">
                      {userTimeline.map((activity, index) => (
                        <div key={activity.id} className="timeline-item">
                          <div className="timeline-dot" style={{ background: activityTypeColors[activity.activity_type] || '#94a3b8' }}></div>
                          <div className="timeline-content">
                            <div className="timeline-time">
                              {new Date(activity.created_at).toLocaleString('tr-TR')}
                            </div>
                            <div className="timeline-action">
                              <span
                                className="type-badge"
                                style={{ background: activityTypeColors[activity.activity_type] || '#94a3b8' }}
                              >
                                {activityTypeNames[activity.activity_type] || activity.activity_type}
                              </span>
                              <span className="page-name">{activity.page_name || activity.page_path}</span>
                            </div>
                            {activity.element_text && (
                              <div className="timeline-detail">{activity.element_text}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Log Detay Modal */}
          {selectedLogDetail && (
            <div className="log-detail-modal-overlay" onClick={() => setSelectedLogDetail(null)}>
              <div className="log-detail-modal" onClick={(e) => e.stopPropagation()}>
                <div className="log-detail-header">
                  <h3>Log Detayı</h3>
                  <button className="close-btn" onClick={() => setSelectedLogDetail(null)}>✕</button>
                </div>
                <div className="log-detail-content">
                  <div className="log-detail-row">
                    <span className="label">Tarih/Saat:</span>
                    <span className="value">{new Date(selectedLogDetail.created_at).toLocaleString('tr-TR')}</span>
                  </div>
                  <div className="log-detail-row">
                    <span className="label">Kullanıcı:</span>
                    <span className="value">{selectedLogDetail.user_name || 'Sistem'} ({selectedLogDetail.user_role || '-'})</span>
                  </div>
                  <div className="log-detail-row">
                    <span className="label">Mağaza:</span>
                    <span className="value">{selectedLogDetail.store_name || '-'}</span>
                  </div>
                  <div className="log-detail-row">
                    <span className="label">Modül:</span>
                    <span className="value">{moduleNames[selectedLogDetail.module] || selectedLogDetail.module}</span>
                  </div>
                  <div className="log-detail-row">
                    <span className="label">İşlem:</span>
                    <span className="value">{actionNames[selectedLogDetail.action] || selectedLogDetail.action}</span>
                  </div>
                  <div className="log-detail-row">
                    <span className="label">Durum:</span>
                    <span className="value">
                      <span
                        className="status-badge-lg"
                        style={{ background: statusConfig[selectedLogDetail.status]?.color }}
                      >
                        {statusConfig[selectedLogDetail.status]?.icon}
                        {statusConfig[selectedLogDetail.status]?.name}
                      </span>
                    </span>
                  </div>
                  <div className="log-detail-row full">
                    <span className="label">Açıklama:</span>
                    <span className="value">{selectedLogDetail.description}</span>
                  </div>
                  {selectedLogDetail.entity_type && (
                    <div className="log-detail-row">
                      <span className="label">İlişkili Kayıt:</span>
                      <span className="value">{selectedLogDetail.entity_type} #{selectedLogDetail.entity_id}</span>
                    </div>
                  )}
                  {selectedLogDetail.ip_address && (
                    <div className="log-detail-row">
                      <span className="label">IP Adresi:</span>
                      <span className="value">{selectedLogDetail.ip_address}</span>
                    </div>
                  )}
                  {selectedLogDetail.details && (
                    <div className="log-detail-row full">
                      <span className="label">Detaylar:</span>
                      <pre className="log-details-json">
                        {JSON.stringify(
                          typeof selectedLogDetail.details === 'string'
                            ? JSON.parse(selectedLogDetail.details)
                            : selectedLogDetail.details,
                          null,
                          2
                        )}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Satış Aktarımı Tab */}
          {activeTab === 'sales-import' && isAdmin && (
            <div className="settings-panel" key="sales-import-panel">
              <h2><FileUp size={24} /> Satış Aktarımı (Excel)</h2>
              <p style={{ color: '#64748b', marginBottom: '20px' }}>
                BarkodSis gibi harici sistemlerden gelen geçmiş satış verilerini Excel dosyası ile içe aktarın.
                <strong style={{ color: '#f59e0b' }}> Bu işlem stok değişikliği yapmaz.</strong>
              </p>

              {/* Mağaza Seçimi */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                    Mağaza Seçimi
                  </label>
                  <select
                    value={salesImportStore}
                    onChange={(e) => setSalesImportStore(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', background: '#fff' }}
                  >
                    <option value="">Mağaza seçin...</option>
                    {stores.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={downloadSalesImportTemplate}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #3b82f6', background: 'transparent', color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}
                >
                  <Download size={16} /> Şablon İndir
                </button>

                <label
                  style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: salesImportStore ? '#3b82f6' : '#94a3b8', color: '#fff', cursor: salesImportStore ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap', opacity: importingSales ? 0.7 : 1 }}
                >
                  <FileUp size={16} /> {importingSales ? 'İşleniyor...' : 'Excel Yükle'}
                  <input
                    ref={salesImportFileRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleSalesExcelUpload}
                    disabled={!salesImportStore || importingSales}
                  />
                </label>
              </div>

              {/* Bilgilendirme kutusu */}
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Info size={18} /> Excel Dosyası Formatı
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '6px', fontSize: '13px', color: '#334155' }}>
                  <span><strong>1.</strong> Fiş No (zorunlu)</span>
                  <span><strong>2.</strong> Tarih (zorunlu)</span>
                  <span><strong>3.</strong> Saat</span>
                  <span><strong>4.</strong> Kasiyer</span>
                  <span><strong>5.</strong> Müşteri Ad Soyad</span>
                  <span><strong>6.</strong> Barkodlar (zorunlu, virgülle ayrık)</span>
                  <span><strong>7.</strong> Toplam Tutar</span>
                  <span><strong>8.</strong> İndirim</span>
                  <span><strong>9.</strong> Alınacak Tutar</span>
                  <span><strong>10.</strong> Açıklama</span>
                </div>
              </div>

              {/* Son aktarım sonucu */}
              {salesImportResult && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 8px', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={18} /> Son Aktarım Sonucu
                  </h4>
                  <p style={{ margin: 0, color: '#166534' }}>
                    <strong>{salesImportResult.imported}</strong> satış başarıyla aktarıldı.
                    {salesImportResult.errors?.length > 0 && (
                      <span style={{ color: '#dc2626' }}> {salesImportResult.errors.length} satışta hata oluştu.</span>
                    )}
                  </p>
                  {salesImportResult.errors?.length > 0 && (
                    <ul style={{ margin: '8px 0 0', paddingLeft: '20px', color: '#dc2626', fontSize: '13px' }}>
                      {salesImportResult.errors.map((err, i) => (
                        <li key={i}>Fiş {err.receipt_number}: {err.error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Satış Aktarımı Ön İzleme Modalı */}
          {showSalesImportPreview && salesImportPreview && (
            <div className="modal-overlay" onClick={() => { if (!importingSales) { setShowSalesImportPreview(false); } }}>
              <div className="modal" style={{ maxWidth: '900px', maxHeight: '85vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <FileSearch size={24} /> Satış Aktarımı Ön İzleme
                </h2>

                {/* Özet Badge'ler */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#eff6ff', color: '#1e40af', fontWeight: '600', fontSize: '13px' }}>
                    Toplam: {salesImportPreview.summary.total}
                  </span>
                  <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#f0fdf4', color: '#166534', fontWeight: '600', fontSize: '13px' }}>
                    Geçerli: {salesImportPreview.summary.valid}
                  </span>
                  {salesImportPreview.summary.errors > 0 && (
                    <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#fef2f2', color: '#dc2626', fontWeight: '600', fontSize: '13px' }}>
                      Hatalı: {salesImportPreview.summary.errors}
                    </span>
                  )}
                  {salesImportPreview.summary.duplicates > 0 && (
                    <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#fefce8', color: '#a16207', fontWeight: '600', fontSize: '13px' }}>
                      Mükerrer: {salesImportPreview.summary.duplicates}
                    </span>
                  )}
                  <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#f5f3ff', color: '#6d28d9', fontWeight: '600', fontSize: '13px' }}>
                    Eşleşen Ürün: {salesImportPreview.summary.matchedProducts}
                  </span>
                  {salesImportPreview.summary.unmatchedProducts > 0 && (
                    <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#fff7ed', color: '#c2410c', fontWeight: '600', fontSize: '13px' }}>
                      Eşleşmeyen Ürün: {salesImportPreview.summary.unmatchedProducts}
                    </span>
                  )}
                  <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#ecfdf5', color: '#059669', fontWeight: '600', fontSize: '13px' }}>
                    Eşleşen Müşteri: {salesImportPreview.summary.matchedCustomers}
                  </span>
                </div>

                {/* Hata Listesi */}
                {salesImportPreview.errorRows.length > 0 && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                    <h4 style={{ color: '#dc2626', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <XCircle size={16} /> Hatalı Satırlar ({salesImportPreview.errorRows.length})
                    </h4>
                    <div style={{ maxHeight: '150px', overflow: 'auto', fontSize: '13px' }}>
                      {salesImportPreview.errorRows.map((row, i) => (
                        <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #fecaca', color: '#991b1b' }}>
                          <strong>Satır {row.rowIndex}:</strong> {row.errors.join(', ')}
                          {row.receipt_number && <span style={{ marginLeft: '8px', color: '#6b7280' }}>(Fiş: {row.receipt_number})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Geçerli Satışlar Tablosu */}
                {salesImportPreview.validRows.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 10px', color: '#166534' }}>
                      Aktarılacak Satışlar ({salesImportPreview.validRows.length})
                    </h4>
                    <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Fiş No</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Tarih</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Müşteri</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Ürünler</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Tutar</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesImportPreview.validRows.map((row, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '8px 10px', fontWeight: '600' }}>{row.receipt_number}</td>
                              <td style={{ padding: '8px 10px', color: '#64748b' }}>{row.date} {row.time}</td>
                              <td style={{ padding: '8px 10px' }}>
                                {row.matchedCustomer ? (
                                  <span style={{ color: '#059669' }}>{row.matchedCustomer.name}</span>
                                ) : row.customer_name ? (
                                  <span style={{ color: '#f59e0b' }}>{row.customer_name} (eşleşmedi)</span>
                                ) : (
                                  <span style={{ color: '#94a3b8' }}>—</span>
                                )}
                              </td>
                              <td style={{ padding: '8px 10px' }}>
                                {row.matchedItems?.map((item, j) => (
                                  <span key={j} style={{ display: 'inline-block', background: '#ecfdf5', color: '#059669', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', marginRight: '4px', marginBottom: '2px' }}>
                                    {item.barcode}
                                  </span>
                                ))}
                                {row.unmatchedBarcodes?.map((bc, j) => (
                                  <span key={`u${j}`} style={{ display: 'inline-block', background: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', marginRight: '4px', marginBottom: '2px' }}>
                                    {bc} ?
                                  </span>
                                ))}
                              </td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600' }}>
                                {(row.net_amount || row.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Butonlar */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <button
                    onClick={() => { setShowSalesImportPreview(false); }}
                    disabled={importingSales}
                    style={{ padding: '10px 24px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: '600' }}
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleConfirmSalesImport}
                    disabled={importingSales || salesImportPreview.validRows.length === 0}
                    style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: salesImportPreview.validRows.length > 0 ? '#059669' : '#94a3b8', color: '#fff', cursor: salesImportPreview.validRows.length > 0 ? 'pointer' : 'not-allowed', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', opacity: importingSales ? 0.7 : 1 }}
                  >
                    <CheckCircle size={16} />
                    {importingSales ? 'Aktarılıyor...' : `${salesImportPreview.validRows.length} Satışı Aktar`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* İade Aktarımı Tab */}
          {activeTab === 'returns-import' && isAdmin && (
            <div className="settings-panel" key="returns-import-panel">
              <h2><RotateCcw size={24} /> İade Aktarımı (Excel)</h2>
              <p style={{ color: '#64748b', marginBottom: '20px' }}>
                BarkodSis gibi harici sistemlerden gelen geçmiş iade verilerini Excel dosyası ile içe aktarın.
                <strong style={{ color: '#f59e0b' }}> Bu işlem stok değişikliği yapmaz.</strong>
              </p>

              {/* Mağaza Seçimi */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#334155', fontSize: '14px' }}>
                    Mağaza Seçimi
                  </label>
                  <select
                    value={returnsImportStore}
                    onChange={(e) => setReturnsImportStore(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', background: '#fff' }}
                  >
                    <option value="">Mağaza seçin...</option>
                    {stores.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={downloadReturnsImportTemplate}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #3b82f6', background: 'transparent', color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}
                >
                  <Download size={16} /> Şablon İndir
                </button>

                <label
                  style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: returnsImportStore ? '#3b82f6' : '#94a3b8', color: '#fff', cursor: returnsImportStore ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap', opacity: importingReturns ? 0.7 : 1 }}
                >
                  <FileUp size={16} /> {importingReturns ? 'İşleniyor...' : 'Excel Yükle'}
                  <input
                    ref={returnsImportFileRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleReturnsExcelUpload}
                    disabled={!returnsImportStore || importingReturns}
                  />
                </label>
              </div>

              {/* Bilgilendirme kutusu */}
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Info size={18} /> Excel Dosyası Formatı
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '6px', fontSize: '13px', color: '#334155' }}>
                  <span><strong>1.</strong> Fiş No (zorunlu)</span>
                  <span><strong>2.</strong> Tarih (zorunlu)</span>
                  <span><strong>3.</strong> Saat</span>
                  <span><strong>4.</strong> Kasiyer</span>
                  <span><strong>5.</strong> Müşteri Ad Soyad</span>
                  <span><strong>6.</strong> Barkodlar (zorunlu, virgülle ayrık)</span>
                  <span><strong>7.</strong> Toplam Tutar</span>
                  <span><strong>8.</strong> İndirim</span>
                  <span><strong>9.</strong> İade Tutarı</span>
                  <span><strong>10.</strong> Açıklama</span>
                </div>
              </div>

              {/* Son aktarım sonucu */}
              {returnsImportResult && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 8px', color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={18} /> Son Aktarım Sonucu
                  </h4>
                  <p style={{ margin: 0, color: '#166534' }}>
                    <strong>{returnsImportResult.imported}</strong> iade başarıyla aktarıldı.
                    {returnsImportResult.errors?.length > 0 && (
                      <span style={{ color: '#dc2626' }}> {returnsImportResult.errors.length} iadede hata oluştu.</span>
                    )}
                  </p>
                  {returnsImportResult.errors?.length > 0 && (
                    <ul style={{ margin: '8px 0 0', paddingLeft: '20px', color: '#dc2626', fontSize: '13px' }}>
                      {returnsImportResult.errors.map((err, i) => (
                        <li key={i}>Fiş {err.receipt_number}: {err.error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {/* İade Aktarımı Ön İzleme Modalı */}
          {showReturnsImportPreview && returnsImportPreview && (
            <div className="modal-overlay" onClick={() => { if (!importingReturns) { setShowReturnsImportPreview(false); } }}>
              <div className="modal" style={{ maxWidth: '900px', maxHeight: '85vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <FileSearch size={24} /> İade Aktarımı Ön İzleme
                </h2>

                {/* Özet Badge'ler */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#eff6ff', color: '#1e40af', fontWeight: '600', fontSize: '13px' }}>
                    Toplam: {returnsImportPreview.summary.total}
                  </span>
                  <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#f0fdf4', color: '#166534', fontWeight: '600', fontSize: '13px' }}>
                    Geçerli: {returnsImportPreview.summary.valid}
                  </span>
                  {returnsImportPreview.summary.errors > 0 && (
                    <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#fef2f2', color: '#dc2626', fontWeight: '600', fontSize: '13px' }}>
                      Hatalı: {returnsImportPreview.summary.errors}
                    </span>
                  )}
                  {returnsImportPreview.summary.duplicates > 0 && (
                    <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#fefce8', color: '#a16207', fontWeight: '600', fontSize: '13px' }}>
                      Mükerrer: {returnsImportPreview.summary.duplicates}
                    </span>
                  )}
                  <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#f5f3ff', color: '#6d28d9', fontWeight: '600', fontSize: '13px' }}>
                    Eşleşen Ürün: {returnsImportPreview.summary.matchedProducts}
                  </span>
                  {returnsImportPreview.summary.unmatchedProducts > 0 && (
                    <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#fff7ed', color: '#c2410c', fontWeight: '600', fontSize: '13px' }}>
                      Eşleşmeyen Ürün: {returnsImportPreview.summary.unmatchedProducts}
                    </span>
                  )}
                  <span style={{ padding: '6px 14px', borderRadius: '20px', background: '#ecfdf5', color: '#059669', fontWeight: '600', fontSize: '13px' }}>
                    Eşleşen Müşteri: {returnsImportPreview.summary.matchedCustomers}
                  </span>
                </div>

                {/* Hata Listesi */}
                {returnsImportPreview.errorRows.length > 0 && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                    <h4 style={{ color: '#dc2626', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <XCircle size={16} /> Hatalı Satırlar ({returnsImportPreview.errorRows.length})
                    </h4>
                    <div style={{ maxHeight: '150px', overflow: 'auto', fontSize: '13px' }}>
                      {returnsImportPreview.errorRows.map((row, i) => (
                        <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #fecaca', color: '#991b1b' }}>
                          <strong>Satır {row.rowIndex}:</strong> {row.errors.join(', ')}
                          {row.receipt_number && <span style={{ marginLeft: '8px', color: '#6b7280' }}>(Fiş: {row.receipt_number})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Geçerli İadeler Tablosu */}
                {returnsImportPreview.validRows.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 10px', color: '#166534' }}>
                      Aktarılacak İadeler ({returnsImportPreview.validRows.length})
                    </h4>
                    <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Fiş No</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Tarih</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Müşteri</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>Ürünler</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>İade Tutarı</th>
                          </tr>
                        </thead>
                        <tbody>
                          {returnsImportPreview.validRows.map((row, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '8px 10px', fontWeight: '600' }}>{row.receipt_number}</td>
                              <td style={{ padding: '8px 10px', color: '#64748b' }}>{row.date} {row.time}</td>
                              <td style={{ padding: '8px 10px' }}>
                                {row.matchedCustomer ? (
                                  <span style={{ color: '#059669' }}>{row.matchedCustomer.name}</span>
                                ) : row.customer_name ? (
                                  <span style={{ color: '#f59e0b' }}>{row.customer_name} (eşleşmedi)</span>
                                ) : (
                                  <span style={{ color: '#94a3b8' }}>—</span>
                                )}
                              </td>
                              <td style={{ padding: '8px 10px' }}>
                                {row.matchedItems?.map((item, j) => (
                                  <span key={j} style={{ display: 'inline-block', background: '#ecfdf5', color: '#059669', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', marginRight: '4px', marginBottom: '2px' }}>
                                    {item.barcode}
                                  </span>
                                ))}
                                {row.unmatchedBarcodes?.map((bc, j) => (
                                  <span key={`u${j}`} style={{ display: 'inline-block', background: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', marginRight: '4px', marginBottom: '2px' }}>
                                    {bc} ?
                                  </span>
                                ))}
                              </td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600' }}>
                                {(row.net_amount || row.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Butonlar */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                  <button
                    onClick={() => { setShowReturnsImportPreview(false); }}
                    disabled={importingReturns}
                    style={{ padding: '10px 24px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: '600' }}
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleConfirmReturnsImport}
                    disabled={importingReturns || returnsImportPreview.validRows.length === 0}
                    style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: returnsImportPreview.validRows.length > 0 ? '#059669' : '#94a3b8', color: '#fff', cursor: returnsImportPreview.validRows.length > 0 ? 'pointer' : 'not-allowed', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', opacity: importingReturns ? 0.7 : 1 }}
                  >
                    <CheckCircle size={16} />
                    {importingReturns ? 'Aktarılıyor...' : `${returnsImportPreview.validRows.length} İadeyi Aktar`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Yasal Bilgiler Tab */}
          {activeTab === 'legal' && (
            <div className="settings-panel legal-panel" key="legal-panel">
              <h2><ScrollText size={24} /> Yasal Bilgiler ve Sorumluluk Beyanı</h2>
              <p className="legal-intro">
                Bu sayfa, Akik Gümüş ve Kuyumculuk ERP ve Perakende Yönetim Sistemi uygulamasının kullanım koşullarını, KVKK kapsamındaki sorumlulukları ve
                sorumluluk reddi beyanını içermektedir. Uygulamayı kullanarak aşağıdaki koşulları kabul etmiş sayılırsınız.
              </p>

              {/* Sorumluluk Reddi Beyanı */}
              <div className={`legal-section ${openLegalSections.disclaimer ? 'open' : ''}`}>
                <div className="legal-section-header" onClick={() => toggleLegalSection('disclaimer')}>
                  <AlertTriangle size={20} color="#f59e0b" />
                  <h3>Sorumluluk Reddi Beyanı (Disclaimer)</h3>
                  <ChevronDown size={20} className={`legal-chevron ${openLegalSections.disclaimer ? 'rotated' : ''}`} />
                </div>
                {openLegalSections.disclaimer && <div className="legal-content">
                  <div className="legal-article">
                    <h4>Madde 1 - Genel Sorumluluk Sınırlandırması</h4>
                    <p>
                      Akik Gümüş ve Kuyumculuk ERP ve Perakende Yönetim Sistemi ("Uygulama"), kuyumculuk sektörüne yönelik stok takibi, satış yönetimi ve
                      muhasebe işlemlerini kolaylaştırmak amacıyla geliştirilmiştir. Uygulama geliştiricileri,
                      yazılımın kullanımından doğabilecek doğrudan veya dolaylı zararlardan, veri kayıplarından,
                      kar mahrumiyetinden veya iş kesintilerinden sorumlu tutulamaz.
                    </p>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 2 - Test ve Kalite Güvencesi</h4>
                    <p>
                      Uygulama, piyasaya sunulmadan önce kapsamlı testlerden geçirilmiş ve kalite güvence
                      süreçleri uygulanmıştır. Ancak hiçbir yazılımın %100 hatasız olması garanti edilemez.
                      Geliştiriciler, yazılımın tüm sistemlerde ve koşullarda sorunsuz çalışacağını taahhüt
                      etmemektedir. Olası hata ve aksaklıklar tespit edildiğinde en kısa sürede giderilmeye
                      çalışılacaktır.
                    </p>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 3 - Kullanıcı Sorumluluğu</h4>
                    <p>
                      Uygulamayı kullanan her kullanıcı (yönetici, kasiyer, personel vb.), kendi girdiği
                      verilerden, yaptığı işlemlerden ve aldığı kararlardan bireysel olarak sorumludur.
                      Hatalı veri girişi, yanlış fiyatlandırma, eksik stok kaydı veya benzeri kullanıcı
                      kaynaklı hatalardan doğan zararlardan geliştiriciler sorumlu tutulamaz.
                    </p>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 4 - Yedekleme Sorumluluğu</h4>
                    <p>
                      Verilerin düzenli olarak yedeklenmesi kullanıcının sorumluluğundadır. Uygulama
                      otomatik yedekleme özelliği sunsa da, yedeklerin alınması, saklanması ve gerektiğinde
                      geri yüklenmesi konusunda nihai sorumluluk kullanıcıya aittir. Veri kaybından
                      dolayı oluşabilecek zararlardan geliştiriciler sorumlu tutulamaz.
                    </p>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 5 - Üçüncü Taraf Entegrasyonları</h4>
                    <p>
                      Uygulama, e-fatura servisleri, ödeme sistemleri, kur bilgisi sağlayıcıları ve diğer
                      üçüncü taraf servislerle entegre olabilir. Bu servislerin erişilebilirliği, doğruluğu
                      ve güvenilirliği ilgili servis sağlayıcılarının sorumluluğundadır. Üçüncü taraf
                      servislerinden kaynaklanan sorunlardan geliştiriciler sorumlu tutulamaz.
                    </p>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 6 - Mali ve Hukuki Danışmanlık</h4>
                    <p>
                      Uygulama, muhasebe ve mali işlemler için yardımcı bir araç olarak tasarlanmıştır.
                      Ancak profesyonel muhasebe, mali müşavirlik veya hukuki danışmanlık hizmeti yerine
                      geçmez. Mali ve hukuki konularda yetkili meslek mensuplarından danışmanlık alınması
                      tavsiye edilir. Uygulama üzerinden yapılan işlemlerin yasal uygunluğundan kullanıcı
                      sorumludur.
                    </p>
                  </div>
                </div>}
              </div>

              {/* KVKK Bilgilendirmesi */}
              <div className={`legal-section ${openLegalSections.kvkk ? 'open' : ''}`}>
                <div className="legal-section-header" onClick={() => toggleLegalSection('kvkk')}>
                  <Shield size={20} color="#3b82f6" />
                  <h3>Kişisel Verilerin Korunması (KVKK) Bilgilendirmesi</h3>
                  <ChevronDown size={20} className={`legal-chevron ${openLegalSections.kvkk ? 'rotated' : ''}`} />
                </div>
                {openLegalSections.kvkk && <div className="legal-content">
                  <div className="legal-article">
                    <h4>Madde 1 - Veri Sorumlusu</h4>
                    <p>
                      6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") kapsamında, bu uygulama
                      aracılığıyla toplanan ve işlenen kişisel veriler bakımından <strong>veri sorumlusu,
                      uygulamayı lisanslayan/kullanan işletme ve bu işletme adına hesap oluşturan yetkili
                      kişidir</strong>. Yazılım geliştiricileri, veri işleyen sıfatıyla yalnızca teknik
                      altyapı hizmeti sunmaktadır.
                    </p>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 2 - Toplanan Kişisel Veriler</h4>
                    <p>Uygulama aracılığıyla aşağıdaki kişisel veriler toplanabilmektedir:</p>
                    <ul className="legal-list">
                      <li><strong>Müşteri Bilgileri:</strong> Ad-soyad, telefon numarası, e-posta adresi, TC kimlik numarası, adres bilgileri</li>
                      <li><strong>Satış Verileri:</strong> Satın alınan ürünler, ödeme bilgileri, fatura adresleri</li>
                      <li><strong>Personel Bilgileri:</strong> Kullanıcı adı, e-posta, şifre (şifrelenmiş), rol ve yetkiler</li>
                      <li><strong>İşlem Logları:</strong> Sistem üzerinde yapılan tüm işlemlerin kayıtları</li>
                    </ul>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 3 - KVKK Aydınlatma Yükümlülüğü</h4>
                    <div className="kvkk-notice-box">
                      <AlertCircle size={20} />
                      <div>
                        <strong>ÖNEMLİ: İşletme/Kasiyer Sorumluluğu</strong>
                        <p>
                          KVKK'nın 10. maddesi gereğince, kişisel verilerin işlenmesinden önce ilgili
                          kişilerin (müşterilerin) aydınlatılması zorunludur. <strong>Bu aydınlatma
                          yükümlülüğü, müşteri kaydı oluşturan kasiyer/personel ve işletme yetkilisine
                          aittir.</strong>
                        </p>
                        <p style={{ marginTop: '8px' }}>
                          Müşteri bilgisi sisteme kaydedilmeden önce, müşterinin kişisel verilerinin
                          hangi amaçlarla işleneceği, kimlere aktarılabileceği ve hakları konusunda
                          yazılı veya sözlü olarak bilgilendirilmesi gerekmektedir. İşletmeler,
                          KVKK aydınlatma metnini müşterilerine sunmak ve gerekli açık rızaları
                          almakla yükümlüdür.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 4 - Verilerin İşlenme Amaçları</h4>
                    <p>Toplanan kişisel veriler aşağıdaki amaçlarla işlenmektedir:</p>
                    <ul className="legal-list">
                      <li>Satış ve faturalama işlemlerinin gerçekleştirilmesi</li>
                      <li>Müşteri ilişkilerinin yönetimi ve iletişim</li>
                      <li>Yasal yükümlülüklerin yerine getirilmesi (e-fatura, vergi beyanları vb.)</li>
                      <li>İşletme içi raporlama ve analiz</li>
                      <li>Garanti ve servis hizmetlerinin sunulması</li>
                    </ul>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 5 - Verilerin Saklanması ve Güvenliği</h4>
                    <p>
                      Kişisel veriler, işletmenin kendi sunucularında veya tercih ettiği bulut
                      hizmetinde saklanmaktadır. Uygulama, verilerin güvenliği için şifreleme,
                      güvenli bağlantı protokolleri ve erişim kontrolü gibi teknik önlemler
                      sunmaktadır. Ancak bu önlemlerin etkin bir şekilde uygulanması ve
                      sunucu/veritabanı güvenliğinin sağlanması işletmenin sorumluluğundadır.
                    </p>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 6 - İlgili Kişi Hakları</h4>
                    <p>KVKK'nın 11. maddesi kapsamında müşterilerinizin aşağıdaki hakları bulunmaktadır:</p>
                    <ul className="legal-list">
                      <li>Kişisel verilerinin işlenip işlenmediğini öğrenme</li>
                      <li>İşlenmişse buna ilişkin bilgi talep etme</li>
                      <li>İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme</li>
                      <li>Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme</li>
                      <li>Eksik veya yanlış işlenmişse düzeltilmesini isteme</li>
                      <li>Silinmesini veya yok edilmesini isteme</li>
                      <li>İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi
                          suretiyle aleyhine bir sonucun ortaya çıkmasına itiraz etme</li>
                      <li>Kanuna aykırı olarak işlenmesi sebebiyle zarara uğraması halinde zararın
                          giderilmesini talep etme</li>
                    </ul>
                    <p style={{ marginTop: '12px' }}>
                      <strong>Bu hakların kullanımına ilişkin başvuruları almak ve yanıtlamak
                      işletmenin sorumluluğundadır.</strong>
                    </p>
                  </div>
                </div>}
              </div>

              {/* Kullanım Koşulları */}
              <div className={`legal-section ${openLegalSections.terms ? 'open' : ''}`}>
                <div className="legal-section-header" onClick={() => toggleLegalSection('terms')}>
                  <FileText size={20} color="#10b981" />
                  <h3>Kullanım Koşulları ve Kabul Beyanı</h3>
                  <ChevronDown size={20} className={`legal-chevron ${openLegalSections.terms ? 'rotated' : ''}`} />
                </div>
                {openLegalSections.terms && <div className="legal-content">
                  <div className="legal-article">
                    <h4>Madde 1 - Kabul</h4>
                    <p>
                      Bu uygulamayı kullanarak, işbu sayfada belirtilen tüm koşulları, sorumluluk
                      reddi beyanını ve KVKK yükümlülüklerini okuduğunuzu, anladığınızı ve kabul
                      ettiğinizi beyan etmiş olursunuz. Bu koşulları kabul etmiyorsanız, uygulamayı
                      kullanmayı derhal sonlandırmalısınız.
                    </p>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 2 - Lisans ve Kullanım Hakkı</h4>
                    <p>
                      Uygulama, lisans sözleşmesinde belirtilen koşullar dahilinde kullanım hakkı
                      vermektedir. Bu hak, devredilemez ve alt lisanslanamaz. Uygulamanın kaynak
                      kodunun kopyalanması, dağıtılması, tersine mühendislik yapılması veya
                      değiştirilmesi kesinlikle yasaktır.
                    </p>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 3 - Güncellemeler ve Değişiklikler</h4>
                    <p>
                      Geliştiriciler, uygulamada ve bu yasal metinlerde önceden haber vermeksizin
                      değişiklik yapma hakkını saklı tutar. Kullanıcıların, bu sayfayı düzenli
                      olarak kontrol etmeleri ve güncel koşulları takip etmeleri önerilir.
                      Güncellemeler yayınlandıktan sonra uygulamayı kullanmaya devam etmeniz,
                      yeni koşulları kabul ettiğiniz anlamına gelir.
                    </p>
                  </div>

                  <div className="legal-article">
                    <h4>Madde 4 - Uyuşmazlık Çözümü</h4>
                    <p>
                      Bu koşullardan doğabilecek uyuşmazlıklarda Türkiye Cumhuriyeti kanunları
                      uygulanacak olup, İstanbul Mahkemeleri ve İcra Daireleri yetkilidir.
                    </p>
                  </div>
                </div>}
              </div>

              {/* Son Güncelleme ve İletişim */}
              <div className="legal-footer">
                <div className="legal-footer-item">
                  <Clock size={16} />
                  <span>Son Güncelleme: Ocak 2026</span>
                </div>
                <div className="legal-footer-item">
                  <Info size={16} />
                  <span>Bu metin bilgilendirme amaçlıdır ve hukuki danışmanlık yerine geçmez.</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Kullanıcı Ekleme/Düzenleme Modal */}
      {showUserModal && (
        <div className="modal-overlay">
          <div className="modal user-modal" onClick={e => e.stopPropagation()} style={{ width: '95%', maxWidth: '1400px', height: '85vh', position: 'relative' }}>
            <div className="modal-header">
              <h2>{editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}</h2>
              <button className="close-btn" onClick={() => setShowUserModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', paddingBottom: '55px', maxHeight: 'calc(85vh - 120px)' }}>
              <div className="user-form-grid">
              <div className="form-group">
                <label>Ad Soyad *</label>
                <input
                  type="text"
                  placeholder="Kullanıcı adı..."
                  value={userForm.name}
                  onChange={e => setUserForm({...userForm, name: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={userForm.email}
                  onChange={e => setUserForm({...userForm, email: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>{editingUser ? 'Yeni Şifre (boş bırakılabilir)' : 'Şifre *'}</label>
                <input
                  type="password"
                  placeholder={editingUser ? 'Değiştirmek istemiyorsanız boş bırakın' : 'Şifre girin...'}
                  value={userForm.password}
                  onChange={e => setUserForm({...userForm, password: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Rol</label>
                <select
                  value={userForm.role}
                  onChange={e => {
                    const newRole = e.target.value;
                    const needsStore = newRole === 'cashier' || newRole === 'terminal' || newRole === 'depo';
                    setUserForm({
                      ...userForm,
                      role: newRole,
                      store_id: needsStore ? userForm.store_id : '',
                      permissions: newRole === 'depo' ? (userForm.permissions || {}) : userForm.permissions
                    });
                  }}
                >
                  <option value="user">Kullanıcı</option>
                  <option value="cashier">Kasiyer</option>
                  <option value="terminal">El Terminali</option>
                  <option value="atolye">Atölye</option>
                  <option value="depo">Depo Sorumlusu</option>
                  <option value="admin">Yönetici</option>
                </select>
              </div>
              {(userForm.role === 'cashier' || userForm.role === 'terminal' || userForm.role === 'depo') && (
                <div className="form-group">
                  <label>Mağaza *</label>
                  <select
                    value={userForm.store_id}
                    onChange={e => setUserForm({...userForm, store_id: e.target.value})}
                  >
                    <option value="">Mağaza seçin...</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Varsayılan Satış Sorumlusu - Sadece Terminal için göster */}
              {userForm.role === 'terminal' && (
                <div className="form-group">
                  <label>Varsayılan Satış Sorumlusu</label>
                  <select
                    value={userForm.default_sales_staff_id}
                    onChange={e => setUserForm({...userForm, default_sales_staff_id: e.target.value})}
                  >
                    <option value="">Satış sorumlusu seçin (opsiyonel)...</option>
                    {/* Kayıtlı değer listede yoksa görünür kıl; yoksa tarayıcı "seçin"i gösterir ve onu seçmek değişiklik sayılmaz */}
                    {userForm.default_sales_staff_id && !formStoreStaffList.some(s => String(s.id) === String(userForm.default_sales_staff_id)) && (
                      <option value={userForm.default_sales_staff_id}>⚠ Bu mağazada olmayan sorumlu: {salesStaffList.find(s => String(s.id) === String(userForm.default_sales_staff_id))?.name || `#${userForm.default_sales_staff_id}`}</option>
                    )}
                    {formStoreStaffList.map(staff => (
                      <option key={staff.id} value={staff.id}>{staff.name}</option>
                    ))}
                  </select>
                  <span className="form-hint">Terminal sepeti kasaya gönderdiğinde bu satış sorumlusu otomatik atanır. Kasadan değiştirilebilir.</span>
                </div>
              )}

              {/* Yetki Ayarları - Sadece Kasiyer için göster */}
              {userForm.role === 'cashier' && (
                <div className="form-group permissions-group">
                  <label>Yetkiler</label>
                  <div className="permissions-list">
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canRefund !== false}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canRefund: e.target.checked }
                        })}
                      />
                      <span className="permission-label">İade Alabilme</span>
                      <span className="permission-desc">İade ve değişim işlemlerini yapabilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canEditPrice !== false}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canEditPrice: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Fiyat Değiştirme</span>
                      <span className="permission-desc">Satış sırasında ürün fiyatlarını değiştirebilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canEditCampaigns === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canEditCampaigns: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Kampanya Düzenleme</span>
                      <span className="permission-desc">Kampanyaları oluşturabilir, düzenleyebilir ve silebilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canAccessShopify === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canAccessShopify: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Shopify Erişimi</span>
                      <span className="permission-desc">Shopify entegrasyonuna ve e-ticaret ayarlarına erişebilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canViewReports === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canViewReports: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Rapor Görüntüleme</span>
                      <span className="permission-desc">Satış, stok ve diğer raporları görüntüleyebilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canEditAllStores === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canEditAllStores: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Tüm Mağazalarda Düzenleme</span>
                      <span className="permission-desc">Kendi mağazası dışındaki mağazalarda da işlem yapabilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canIntakeStock === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canIntakeStock: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Stok Alımı Yapabilme</span>
                      <span className="permission-desc">Stoğa yeni ürün girişi yapabilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canTransfer === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canTransfer: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Transfer Yapabilme</span>
                      <span className="permission-desc">Mağazalar arası transfer işlemleri yapabilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canSendToRepair === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canSendToRepair: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Tamirata Ürün Gönderme</span>
                      <span className="permission-desc">Ürün yönetimi sayfasından mağaza ürünlerini tamirata gönderebilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canInventoryCount === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canInventoryCount: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Sayım Yapabilme</span>
                      <span className="permission-desc">Stok sayımı başlatabilir ve sayım işlemleri yapabilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canDeductStock === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canDeductStock: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Stok Düşümü Yapabilme</span>
                      <span className="permission-desc">Mağazadan stok düşümü yapabilir. Bu yetki olmadan stok düşüm ekranına erişemez ve stok çıkışı gerçekleştiremez.</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canViewStockReport === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canViewStockReport: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Stokları İnceleme</span>
                      <span className="permission-desc">Stok raporu sayfasına erişebilir, tüm mağazaların stok durumlarını ve stok hareketlerini inceleyebilir.</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canCreateProduct === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canCreateProduct: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Ürün Oluşturma</span>
                      <span className="permission-desc">Yeni ürün ekleyebilir, fotoğraf yükleyebilir ve ürün oluşturma ekranına erişebilir.</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canEditProduct === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canEditProduct: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Ürün Düzenleme</span>
                      <span className="permission-desc">Mevcut ürün bilgilerini düzenleyebilir.</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canEditLabor === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canEditLabor: e.target.checked }
                        })}
                      />
                      <span className="permission-label">İşçilik Bilgisi Girebilme</span>
                      <span className="permission-desc">Ürün ekleme ve düzenleme ekranlarında işçilik alanına değer girebilir.</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canAccessExcel === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canAccessExcel: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Excel Erişimi</span>
                      <span className="permission-desc">Excel'e aktarma, Excel'den içe aktarma ve Excel ile ilgili tüm işlemlere erişebilir.</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Yetki Ayarları - Sadece Terminal için göster */}
              {userForm.role === 'terminal' && (
                <div className="form-group permissions-group">
                  <label>Yetkiler</label>
                  <div className="permissions-list">
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canIntakeStock === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canIntakeStock: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Stok Alımı Yapabilme</span>
                      <span className="permission-desc">Stoğa yeni ürün girişi yapabilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canTransfer === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canTransfer: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Transfer Yapabilme</span>
                      <span className="permission-desc">Mağazalar arası transfer işlemleri yapabilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canSendToRepair === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canSendToRepair: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Tamirata Ürün Gönderme</span>
                      <span className="permission-desc">Ürün yönetimi sayfasından mağaza ürünlerini tamirata gönderebilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canAccessShopify === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canAccessShopify: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Shopify Erişimi</span>
                      <span className="permission-desc">Shopify entegrasyonuna ve e-ticaret ayarlarına erişebilir</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Yetkiler - Sadece Depo Sorumlusu için göster */}
              {userForm.role === 'depo' && (
                <div className="form-group permissions-group">
                  <label>Yetkiler</label>
                  <div className="permissions-list">
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canCreateProduct === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canCreateProduct: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Ürün Oluşturma</span>
                      <span className="permission-desc">Yeni ürün ekleyebilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canEditProduct === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canEditProduct: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Ürün Düzenleme</span>
                      <span className="permission-desc">Mevcut ürün bilgilerini düzenleyebilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canEditPrice === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canEditPrice: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Fiyat Düzenleme</span>
                      <span className="permission-desc">Ürün fiyatlarını değiştirebilir</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.canAccessShopify === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, canAccessShopify: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Shopify Erişimi</span>
                      <span className="permission-desc">Shopify entegrasyonuna ve e-ticaret ayarlarına erişebilir</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Sayfa Erişim Yetkileri - Sadece Depo Sorumlusu için göster */}
              {userForm.role === 'depo' && (
                <div className="form-group permissions-group">
                  <label>Erişebileceği Sayfalar</label>
                  <div className="permissions-list">
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_retail_sales === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_retail_sales: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Perakende Satış</span>
                      <span className="permission-desc">POS sistemi ile satış işlemleri</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_repairs === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_repairs: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Tamirat Ürünleri</span>
                      <span className="permission-desc">Tamirat kayıtları yönetimi</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_customers === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_customers: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Müşteri Yönetimi</span>
                      <span className="permission-desc">Müşteri bilgileri ve CRM</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_products === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_products: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Ürün Yönetimi</span>
                      <span className="permission-desc">Ürün kataloğu ve fiyatlandırma</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_scrap_purchase === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_scrap_purchase: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Hurda Alımı</span>
                      <span className="permission-desc">Altın ve gümüş hurda alım işlemleri</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_reports === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_reports: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Raporlar</span>
                      <span className="permission-desc">Satış ve stok raporları</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_settings === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_settings: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Ayarlar</span>
                      <span className="permission-desc">Sistem ayarları</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_campaigns === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_campaigns: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Kampanyalar</span>
                      <span className="permission-desc">Kampanya ve indirim yönetimi</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_personnel === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_personnel: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Personel Yönetimi</span>
                      <span className="permission-desc">Personel, yoklama, izin ve maaş takibi</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_payment_calendar === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_payment_calendar: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Ödemeler & Takvim</span>
                      <span className="permission-desc">Ödeme planları ve hatırlatıcılar</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_wholesaler_payments === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_wholesaler_payments: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Toptancı Ödemeleri</span>
                      <span className="permission-desc">Toptancı ve tedarikçi ödeme takibi</span>
                    </label>
                    <label className="permission-item">
                      <input
                        type="checkbox"
                        checked={userForm.permissions?.page_shopify === true}
                        onChange={e => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, page_shopify: e.target.checked }
                        })}
                      />
                      <span className="permission-label">Shopify Entegrasyonu</span>
                      <span className="permission-desc">E-ticaret ürün ve sipariş yönetimi</span>
                    </label>
                  </div>
                </div>
              )}

              {/* QR Kodu ile Giriş - Sadece Terminal için ve düzenleme modunda */}
              {userForm.role === 'terminal' && editingUser && (
                <div className="form-group qr-login-section">
                  <label>QR Kodu ile Giriş</label>
                  <p className="field-description">El terminali kullanıcısı, QR kodu taratarak şifresiz giriş yapabilir.</p>
                  <button
                    type="button"
                    className="btn-qr-manage"
                    onClick={() => openQrModal(editingUser.id)}
                  >
                    <Key size={18} />
                    QR Kodu Yönet
                  </button>
                </div>
              )}
              </div>{/* user-form-grid kapanış */}
            </div>
            <div className="modal-footer" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, borderRadius: '0 0 16px 16px', background: 'white', borderTop: '1px solid #e5e7eb' }}>
              <button className="btn-cancel" onClick={() => setShowUserModal(false)}>İptal</button>
              <button className="btn-save" onClick={handleSaveUser}>
                {editingUser ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Kodu Modal */}
      {showQrModal && editingUser && (
        <div className="modal-overlay">
          <div className="modal qr-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Key size={20} style={{ marginRight: '8px' }} /> QR Kodu ile Giriş</h2>
              <button className="close-btn" onClick={() => setShowQrModal(false)}>✕</button>
            </div>
            <div className="modal-body qr-modal-body">
              <p className="qr-info-text">
                Bu QR kodunu el terminali cihazına vererek, kullanıcının şifresiz giriş yapmasını sağlayabilirsiniz.
              </p>

              {loadingQrToken ? (
                <div className="qr-loading">
                  <RefreshCw size={32} className="spin" />
                  <span>Yükleniyor...</span>
                </div>
              ) : qrTokenActive && qrLoginCode ? (
                <div className="qr-display">
                  <img src={qrLoginCode} alt="QR Kodu" className="qr-image" />
                  <div className="qr-status active">
                    <CheckCircle size={16} />
                    QR kodu aktif
                  </div>
                  <div className="qr-actions">
                    <button
                      className="btn-qr-download"
                      onClick={() => downloadQrCode(editingUser.name)}
                    >
                      <Download size={18} />
                      İndir
                    </button>
                    <button
                      className="btn-qr-regenerate"
                      onClick={() => generateQrToken(editingUser.id)}
                    >
                      <RefreshCw size={18} />
                      Yenile
                    </button>
                    <button
                      className="btn-qr-revoke"
                      onClick={() => revokeQrToken(editingUser.id)}
                    >
                      <Trash2 size={18} />
                      İptal Et
                    </button>
                  </div>
                </div>
              ) : (
                <div className="qr-empty">
                  <Key size={48} className="qr-empty-icon" />
                  <p>Henüz QR kodu oluşturulmamış</p>
                  <button
                    className="btn-qr-generate"
                    onClick={() => generateQrToken(editingUser.id)}
                  >
                    <Key size={18} />
                    QR Kodu Oluştur
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Yardım Modal */}
      {showHelpModal && (
        <div className="modal-overlay">
          <div className="modal help-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header help-header">
              <h2><BookOpen size={20} style={{ marginRight: '8px' }} /> Ayarlar Yardım</h2>
              <button className="close-btn" onClick={() => setShowHelpModal(false)}>✕</button>
            </div>
            <div className="help-modal-content">
              {/* Genel Bilgi */}
              <div className="help-section">
                <h3><SettingsIcon size={18} style={{ marginRight: '6px' }} /> Genel Bilgi</h3>
                <p>Bu sayfa üzerinden uygulama ayarlarını yönetebilir, kullanıcı ekleyebilir, yedekleme yapabilir ve e-fatura ayarlarını yapılandırabilirsiniz.</p>
              </div>

              {/* Sekmeler */}
              <div className="help-section">
                <h3><FileText size={18} style={{ marginRight: '6px' }} /> Sekmeler</h3>
                <div className="help-buttons-grid">
                  <div className="help-button-item">
                    <span className="btn-icon"><SettingsIcon size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Genel</strong>
                      <p>Dil, tema ve ses ayarları</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Printer size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Yazıcı</strong>
                      <p>Fiş yazıcısı ve kağıt ayarları</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Users size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Kullanıcılar</strong>
                      <p>Kullanıcı ekleme, düzenleme ve yetkilendirme (sadece yönetici)</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><FileText size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>E-Fatura / E-Arşiv</strong>
                      <p>E-fatura entegrasyon ayarları ve firma bilgileri</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Database size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Yedekleme</strong>
                      <p>Veritabanı yedekleme ve geri yükleme</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Shield size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Güvenlik</strong>
                      <p>İki faktörlü doğrulama (2FA) ayarları</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Kullanıcı Rolleri */}
              <div className="help-section">
                <h3><User size={18} style={{ marginRight: '6px' }} /> Kullanıcı Rolleri</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num"><Crown size={16} style={{ color: '#f59e0b' }} /></span>
                    <span><strong>Yönetici:</strong> Tüm yetkilere sahip, ayarları değiştirebilir</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num"><Banknote size={16} style={{ color: '#8b5cf6' }} /></span>
                    <span><strong>Kasiyer:</strong> Satış işlemleri yapabilir</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num"><Smartphone size={16} style={{ color: '#10b981' }} /></span>
                    <span><strong>El Terminali:</strong> Mobil cihazdan stok ve fiyat sorgulama</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num"><Wrench size={16} style={{ color: '#ec4899' }} /></span>
                    <span><strong>Atölye:</strong> Tamirat işlemlerini takip eder</span>
                  </div>
                </div>
              </div>

              {/* Ses Ayarı */}
              <div className="help-section">
                <h3><Volume2 size={18} style={{ marginRight: '6px' }} /> Ses Efektleri</h3>
                <p>Genel sekmesindeki "Ses Efektleri" ayarı, sayım sırasındaki onay ve hata seslerini kontrol eder. Bu ayar cihaza özeldir - her cihazda ayrı ayrı kapatılabilir.</p>
              </div>

              {/* İki Faktörlü Doğrulama */}
              <div className="help-section">
                <h3><Shield size={18} style={{ marginRight: '6px' }} /> İki Faktörlü Doğrulama (2FA)</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span><strong>Güvenlik</strong> sekmesine gidin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span><strong>2FA'yı Etkinleştir</strong> butonuna tıklayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>QR kodu Google Authenticator veya Microsoft Authenticator ile tarayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span>Uygulamadan gelen 6 haneli kodu girerek doğrulayın</span>
                  </div>
                </div>
                <div className="help-tip"><HelpCircle size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> 2FA aktif olduğunda her giriş için telefon uygulamasından kod gerekir.</div>
              </div>

              {/* Yedekleme */}
              <div className="help-section">
                <h3><Database size={18} style={{ marginRight: '6px' }} /> Yedekleme</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span>Yedekleme sekmesine gidin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span><strong>Yeni Yedek Al</strong> butonuna tıklayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>Otomatik yedekleme için aralık ayarlayın</span>
                  </div>
                </div>
                <div className="help-tip"><HelpCircle size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Düzenli yedekleme veri kaybını önler!</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Log Modal */}
      <LogModal
        isOpen={showLogModal}
        onClose={() => setShowLogModal(false)}
        module="settings"
        title="Ayarlar"
        showEntityCode={false}
      />

      {/* Genel Onay Modal (window.confirm yerine) */}
      {showConfirmModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-header" style={{
              background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                         confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                         'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={28} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{confirmModalConfig.title}</h2>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <p style={{
                margin: 0,
                fontSize: '15px',
                color: '#374151',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}>
                {confirmModalConfig.message}
              </p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px',
              background: '#f8fafc',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e2e8f0'
            }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.cancelText}
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  if (confirmModalConfig.onConfirm) {
                    confirmModalConfig.onConfirm();
                  }
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmModalConfig.type === 'danger' ? '#ef4444' :
                             confirmModalConfig.type === 'info' ? '#3b82f6' : '#f59e0b',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
